/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { RgbLedMode_Enum, rgbLedMode_EnumFromJSON, rgbLedMode_EnumToJSON } from "../../../gen/flight_deck/rgb_led_mode";
import { AgcSettings } from "../../../gen/flir_boson_driver/agc_settings";
import { BosonSettings } from "../../../gen/flir_boson_driver/boson_settings";
import { Colorlut_Enum, colorlut_EnumFromJSON, colorlut_EnumToJSON } from "../../../gen/flir_boson_driver/colorlut";
import { IsothermSettings } from "../../../gen/flir_boson_driver/isotherm_settings";
import { RoiSettings } from "../../../gen/flir_boson_driver/roi_settings";
import { ThermalParams } from "../../../gen/flir_boson_driver/thermal_params";
import { JoysticksSettings } from "../../../gen/joysticks/joysticks_settings";
import {
  DisplayUnits_Enum,
  displayUnits_EnumFromJSON,
  displayUnits_EnumToJSON,
} from "../../../gen/phone/display_units";
import {
  SubjectDetectionMode_Enum,
  subjectDetectionMode_EnumFromJSON,
  subjectDetectionMode_EnumToJSON,
} from "../../../gen/subject2/subject_detection_mode";
import {
  VisualSource_Enum,
  visualSource_EnumFromJSON,
  visualSource_EnumToJSON,
} from "../../../gen/subject2/visual_source";
import {
  AntiBandingMode_Enum,
  antiBandingMode_EnumFromJSON,
  antiBandingMode_EnumToJSON,
} from "../../../gen/user_camera/anti_banding_mode";
import { AutoRegionRequest } from "../../../gen/user_camera/auto_region_request";
import {
  AutofocusMode_Enum,
  autofocusMode_EnumFromJSON,
  autofocusMode_EnumToJSON,
} from "../../../gen/user_camera/autofocus_mode";
import { AwbMode_Enum, awbMode_EnumFromJSON, awbMode_EnumToJSON } from "../../../gen/user_camera/awb_mode";
import { ColorMode_Enum, colorMode_EnumFromJSON, colorMode_EnumToJSON } from "../../../gen/user_camera/color_mode";
import {
  CompensationMode_Enum,
  compensationMode_EnumFromJSON,
  compensationMode_EnumToJSON,
} from "../../../gen/user_camera/compensation_mode";
import {
  EncoderMode_Enum,
  encoderMode_EnumFromJSON,
  encoderMode_EnumToJSON,
} from "../../../gen/user_camera/encoder_mode";
import {
  ExposureMode_Enum,
  exposureMode_EnumFromJSON,
  exposureMode_EnumToJSON,
} from "../../../gen/user_camera/exposure_mode";
import { IsoMode_Enum, isoMode_EnumFromJSON, isoMode_EnumToJSON } from "../../../gen/user_camera/iso_mode";
import {
  PhotoFormat_Enum,
  photoFormat_EnumFromJSON,
  photoFormat_EnumToJSON,
} from "../../../gen/user_camera/photo_format";
import {
  PhotoIntervalMode_Enum,
  photoIntervalMode_EnumFromJSON,
  photoIntervalMode_EnumToJSON,
} from "../../../gen/user_camera/photo_interval_mode";
import {
  RecordingBitratePreset_Enum,
  recordingBitratePreset_EnumFromJSON,
  recordingBitratePreset_EnumToJSON,
} from "../../../gen/user_camera/recording_bitrate_preset";
import {
  RecordingFileFormat_Enum,
  recordingFileFormat_EnumFromJSON,
  recordingFileFormat_EnumToJSON,
} from "../../../gen/user_camera/recording_file_format";
import {
  RecordingMode_Enum,
  recordingMode_EnumFromJSON,
  recordingMode_EnumToJSON,
} from "../../../gen/user_camera/recording_mode";
import { SkillsPhotoMetadata } from "../../../gen/user_camera/skills_photo_metadata";
import { SkysticksPairingInfo } from "../../../infrastructure/wifi/wifi";
import { Geofence } from "../../../util/geofence/geofence";
import { AdsbRegulatoryManagerSettings } from "../../adsb_regulatory_manager/adsb_regulatory_manager";
import { BatteryCellVoltageImbalanceThresholds } from "../../health_monitor/fault_detectors";
import { FrameType_Enum, frameType_EnumFromJSON, frameType_EnumToJSON } from "../../skills/waypoints";
import { NavigationMode_Mode, navigationMode_ModeFromJSON, navigationMode_ModeToJSON } from "./navigation_mode";
import { ReturnSettings } from "./return_settings";

export const protobufPackage = "flight_deck";

export interface AllSettings {
  beaconSettings: BeaconSettings | undefined;
  joysticksSettings: JoysticksSettings | undefined;
  captureSettings: CaptureSettings | undefined;
  vehicleSettings: VehicleSettings | undefined;
  irCaptureSettings:
    | IrCaptureSettings
    | undefined;
  /** RemoteID registration and auth key info */
  remoteidAuthSettings: RemoteIDAuthSettings | undefined;
}

export interface BeaconSettings {
  utime: number;
  /** The set of skills to be available for selection on the beacon */
  selectedSkills: string[];
  /** Whether the beacon is in a locked or unlocked state. */
  locked: boolean;
  /** The type of units the phone and beacon should display (metric or imperial) */
  displayUnits: DisplayUnits_Enum;
  /** when true, the consumer app is connected */
  isConsumerAppConnected: boolean;
}

/**
 * A minimized message that contains various image processing parameters, intended for
 * exposing the value that AUTO modes settle on to clients
 *
 * NOTE(jfeng): When in AUTO modes, the value the ISP settles on may not cleanly lie at an enum
 * value. In other words, the enum values used by the client capture settings API correlate to some
 * real value used to drive the sensor, but they do not cover the whole space of valid input values.
 * As a result, we provide the "actual" values as integers, rather than as the enum for additional
 * clarity for clients.
 */
export interface ImageParameters {
  utime: number;
  iso: number;
  exposure: number;
  /** Approximate white balance value */
  correlatedColorTemp: number;
  compensationMode: number;
}

export interface CaptureSettings {
  utime: number;
  /**
   * Random number created by the camera_service on launch.
   * This number gets changed when we apply new settings.
   * Any client that publishes new settings on UPDATE_CAPTURE_SETTINGS_PB must have a matching id
   * for settings to be accepted. Enforcing this protects against rogue clients that publish
   * settings without copying from the existing settings.
   */
  validationId: number;
  source: CaptureSettings_CaptureSettingsSource;
  /** Video or photo recording mode of the vehicle. */
  recordingMode: RecordingMode_Enum;
  /** If true the video should auto-record, otherwise the user must start/stop recording manually. */
  videoAutoRecord: boolean;
  /**
   * The photo file format that should be used when requesting photos.
   * NOTE(jeff): this setting is not known to the ISP - it is meant to be used and applied by the
   * publisher of the user_camera.photo_capture_request_t LCM message.
   */
  photoFormat: PhotoFormat_Enum;
  /**
   * The interval at which the camera should be automatically taking photos.
   * NOTE(jeff): this setting is not known to the ISP - the camera_service implements this feature.
   */
  photoInterval: PhotoIntervalMode_Enum;
  iso: IsoMode_Enum;
  exposure: ExposureMode_Enum;
  compensation: CompensationMode_Enum;
  whiteBalance: AwbMode_Enum;
  /** Refers to the encoder mode of the file saved to disk */
  encoderMode: EncoderMode_Enum;
  /** Refers to the encoder mode of the stream sent to the phone */
  streamEncoderMode: EncoderMode_Enum;
  colorMode: ColorMode_Enum;
  userBitratePreset: RecordingBitratePreset_Enum;
  /**
   * NOTE(jfeng):
   * In CURRENT_CAPTURE_SETTINGS_PB, these values reflect the last commanded values, but not
   * necessarily the true realtime values experienced by the ISP. The rationale for this design is
   * to allow clients to easily determine if something else has changed the regions. For example,
   * tap to focus accidentally changing region when skills may want something else.
   * For actual realtime values:
   * - remote clients should listen to the USER_CAMERA_AUTOREGION_STATUS channel, which is always
   *   expressed in NORMALIZED_PREVIEW space.
   * - local clients that need to know regions at a pixel-perfect resolution should listen to the
   *   USER_CAMERA_SETTINGS_STATUS channel.
   * As a side note, regions in NORMALIZED_PREVIEW, while ultimately constant in sensor space, will
   * vary frame-by-frame in size and position as it needs to compensate for zoom changes, eis
   * application, etc.
   *
   * Clients updating these values in UPDATE_CAPTURE_SETTINGS_PB are granted a bit more granular
   * control over the regions and autofocus mode compared to the tap to focus path.
   * This is intended to give our skills more control over the camera. In practice, clients should
   * mostly be expressing regions in NORMALIZED_PREVIEW unless they know *exactly* what they are
   * doing. However, we have a few planned features (such as focus locking on subjects being
   * tracked) that will require clients to express regions in sensor space, and will likely want
   * very precise control of the focus and exposure.
   */
  autofocusRegion: AutoRegionRequest | undefined;
  autoexposureRegion:
    | AutoRegionRequest
    | undefined;
  /**
   * NOTE(jfeng): If autofocus mode is OFF, it will clear both the autofocus and autoexposure
   * regions as well. This is a unfortunate consequence of not having a higher level API to describe
   * focus actions, as well as the current implementation where the autofocus and autoexposure
   * regions are tied together and set from the same tap interaction. Eventually the goal is to be
   * able to support separate exposure and focus regions, at which point we can stop clearing the
   * autoexposure region when autofocus mode is OFF.
   */
  autofocusMode: AutofocusMode_Enum;
  /** Only honored if autofocus mode is OFF */
  manualFocusPosition: number;
  /** user override for bitrate in bits/second; 0 uses above preset */
  userBitrateOverride: number;
  antiBandingMode: AntiBandingMode_Enum;
  /** If true, the output of a photo should be close to 15MP, defaults to false */
  photo15mpEnabled: boolean;
  publishThumbnail: boolean;
  thumbnailWidth: number;
  /**
   * NOTE(harrison): we save a copy of this struct to persist camera settings
   * the problem is RecordingMode covers both video and photo modes, so we don't persist
   * photo settings if we end a flight in video mode
   * these two fields are a workaround to this
   */
  videoRecordingMode: RecordingMode_Enum;
  photoRecordingMode: RecordingMode_Enum;
  /** custom folder for saving media */
  customFolder: string;
  /** if true, use custom folder for saving media */
  useCustomFolder: boolean;
  /** Recording file type (MP4 or MPEGTS) */
  recordingFileFormat: RecordingFileFormat_Enum;
  /** EIS Tuning */
  eisHorizonLock: boolean;
  eisSmoothness: number;
  eisMargin: number;
  /**
   * Default skills photo metadata for all photo capture requests.
   * If metadata is present in the individual photo capture request, it takes priority.
   */
  skillsPhotoMetadata?: SkillsPhotoMetadata | undefined;
}

/** Which client is sending the settings update */
export enum CaptureSettings_CaptureSettingsSource {
  UNSPECIFIED = 0,
  SKILLS = 1,
  MOBILE_APP = 2,
  TELEOP_UI = 3,
  LANDING_PAD_FRONTEND = 4,
  MAVLINK = 5,
  CAMERA_SETTINGS_EXECUTABLE = 6,
  CLOUD_CONFIG_RESET = 7,
  UNRECOGNIZED = -1,
}

export function captureSettings_CaptureSettingsSourceFromJSON(object: any): CaptureSettings_CaptureSettingsSource {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return CaptureSettings_CaptureSettingsSource.UNSPECIFIED;
    case 1:
    case "SKILLS":
      return CaptureSettings_CaptureSettingsSource.SKILLS;
    case 2:
    case "MOBILE_APP":
      return CaptureSettings_CaptureSettingsSource.MOBILE_APP;
    case 3:
    case "TELEOP_UI":
      return CaptureSettings_CaptureSettingsSource.TELEOP_UI;
    case 4:
    case "LANDING_PAD_FRONTEND":
      return CaptureSettings_CaptureSettingsSource.LANDING_PAD_FRONTEND;
    case 5:
    case "MAVLINK":
      return CaptureSettings_CaptureSettingsSource.MAVLINK;
    case 6:
    case "CAMERA_SETTINGS_EXECUTABLE":
      return CaptureSettings_CaptureSettingsSource.CAMERA_SETTINGS_EXECUTABLE;
    case 7:
    case "CLOUD_CONFIG_RESET":
      return CaptureSettings_CaptureSettingsSource.CLOUD_CONFIG_RESET;
    case -1:
    case "UNRECOGNIZED":
    default:
      return CaptureSettings_CaptureSettingsSource.UNRECOGNIZED;
  }
}

export function captureSettings_CaptureSettingsSourceToJSON(object: CaptureSettings_CaptureSettingsSource): string {
  switch (object) {
    case CaptureSettings_CaptureSettingsSource.UNSPECIFIED:
      return "UNSPECIFIED";
    case CaptureSettings_CaptureSettingsSource.SKILLS:
      return "SKILLS";
    case CaptureSettings_CaptureSettingsSource.MOBILE_APP:
      return "MOBILE_APP";
    case CaptureSettings_CaptureSettingsSource.TELEOP_UI:
      return "TELEOP_UI";
    case CaptureSettings_CaptureSettingsSource.LANDING_PAD_FRONTEND:
      return "LANDING_PAD_FRONTEND";
    case CaptureSettings_CaptureSettingsSource.MAVLINK:
      return "MAVLINK";
    case CaptureSettings_CaptureSettingsSource.CAMERA_SETTINGS_EXECUTABLE:
      return "CAMERA_SETTINGS_EXECUTABLE";
    case CaptureSettings_CaptureSettingsSource.CLOUD_CONFIG_RESET:
      return "CLOUD_CONFIG_RESET";
    case CaptureSettings_CaptureSettingsSource.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface IrCaptureSettings {
  utime: number;
  colorlut: Colorlut_Enum;
  /** If true, reject photo requests. */
  photoDisabled: boolean;
  photoFormat: PhotoFormat_Enum;
  /**
   * These settings also can be directly modified through skybus on FLIR_CLIENT_X channels. The
   * reasoning is that this message has the potential to bloat as we gain new features, and we want
   * to avoid sending a large message on "small" updates that should be responsive (so that we can
   * see how it changes the stream realtime). However, it is still useful to track them here for:
   * 1) ease of persisting them through sessions with protobuf storage, and
   * 2) to make it easy for clients to know what the state of the IR camera is without having to
   *    subscribe to many different channels to aggregate state.
   */
  agc: AgcSettings | undefined;
  exposureRoi: RoiSettings | undefined;
  thermalParams: ThermalParams | undefined;
  isotherm: IsothermSettings | undefined;
  boson: BosonSettings | undefined;
}

/** Used to persist settings state are not controlled by the typical IR Capture Settings path above */
export interface IrAuxillaryPersistedSettings {
  spotMeterEnabled: boolean;
  userStatsRegionEnabled: boolean;
}

export interface RemoteIDAuthSettings {
  /** Registration code derived after registering vehicle with authority. */
  registrationCode: string;
  /**
   * defined in table-8 of
   * https://docs.google.com/document/d/1iK6xnVBoM_fX3VlGBtGK4nZ4zdS30hLa/edit
   */
  datablock: string;
  /** Whether the beacon is in a locked or unlocked state. */
  writeStatus: boolean;
  /** Modified date */
  modifiedDate: string;
  /** Broadcast Remote ID */
  broadcastMethod: boolean;
}

export interface MavlinkClientSettings {
  geofenceRadius: number;
  geofenceHeight: number;
  geofenceAction: MavlinkClientSettings_MavlinkGeofenceAction;
}

export enum MavlinkClientSettings_MavlinkGeofenceAction {
  MAVLINK_GEOFENCE_ACTION_NONE = 0,
  MAVLINK_GEOFENCE_ACTION_WARN = 1,
  MAVLINK_GEOFENCE_ACTION_HOLD = 2,
  MAVLINK_GEOFENCE_ACTION_RETURN = 3,
  MAVLINK_GEOFENCE_ACTION_TERMINATE = 4,
  MAVLINK_GEOFENCE_ACTION_LAND = 5,
  UNRECOGNIZED = -1,
}

export function mavlinkClientSettings_MavlinkGeofenceActionFromJSON(
  object: any,
): MavlinkClientSettings_MavlinkGeofenceAction {
  switch (object) {
    case 0:
    case "MAVLINK_GEOFENCE_ACTION_NONE":
      return MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_NONE;
    case 1:
    case "MAVLINK_GEOFENCE_ACTION_WARN":
      return MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_WARN;
    case 2:
    case "MAVLINK_GEOFENCE_ACTION_HOLD":
      return MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_HOLD;
    case 3:
    case "MAVLINK_GEOFENCE_ACTION_RETURN":
      return MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_RETURN;
    case 4:
    case "MAVLINK_GEOFENCE_ACTION_TERMINATE":
      return MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_TERMINATE;
    case 5:
    case "MAVLINK_GEOFENCE_ACTION_LAND":
      return MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_LAND;
    case -1:
    case "UNRECOGNIZED":
    default:
      return MavlinkClientSettings_MavlinkGeofenceAction.UNRECOGNIZED;
  }
}

export function mavlinkClientSettings_MavlinkGeofenceActionToJSON(
  object: MavlinkClientSettings_MavlinkGeofenceAction,
): string {
  switch (object) {
    case MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_NONE:
      return "MAVLINK_GEOFENCE_ACTION_NONE";
    case MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_WARN:
      return "MAVLINK_GEOFENCE_ACTION_WARN";
    case MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_HOLD:
      return "MAVLINK_GEOFENCE_ACTION_HOLD";
    case MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_RETURN:
      return "MAVLINK_GEOFENCE_ACTION_RETURN";
    case MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_TERMINATE:
      return "MAVLINK_GEOFENCE_ACTION_TERMINATE";
    case MavlinkClientSettings_MavlinkGeofenceAction.MAVLINK_GEOFENCE_ACTION_LAND:
      return "MAVLINK_GEOFENCE_ACTION_LAND";
    case MavlinkClientSettings_MavlinkGeofenceAction.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Settings for subject tracking and following, used by subject_follower and subject2 */
export interface SubjectSettings {
  /** Source of the settings */
  source: SubjectSettings_SubjectSettingsSource;
  /** Automatically follow after subject locked */
  autoFollow: boolean;
  /** Maintains a fixed radius around the subject */
  fixedElevation: boolean;
  /** Select between Absolute azimuth and Pursuit */
  absoluteAzimuth: boolean;
  /** Enables detection */
  detectionOn: boolean;
  /** Camera stream for detection */
  detectionMode: SubjectDetectionMode_Enum;
  /** Desired visual source */
  visualSource: VisualSource_Enum;
}

/** Source of the settings */
export enum SubjectSettings_SubjectSettingsSource {
  UNSPECIFIED = 0,
  /** RFD - Settings from RFD */
  RFD = 1,
  /** MOBILE - Settings from Controller */
  MOBILE = 2,
  /** SKILLS - Settings from skills */
  SKILLS = 3,
  UNRECOGNIZED = -1,
}

export function subjectSettings_SubjectSettingsSourceFromJSON(object: any): SubjectSettings_SubjectSettingsSource {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return SubjectSettings_SubjectSettingsSource.UNSPECIFIED;
    case 1:
    case "RFD":
      return SubjectSettings_SubjectSettingsSource.RFD;
    case 2:
    case "MOBILE":
      return SubjectSettings_SubjectSettingsSource.MOBILE;
    case 3:
    case "SKILLS":
      return SubjectSettings_SubjectSettingsSource.SKILLS;
    case -1:
    case "UNRECOGNIZED":
    default:
      return SubjectSettings_SubjectSettingsSource.UNRECOGNIZED;
  }
}

export function subjectSettings_SubjectSettingsSourceToJSON(object: SubjectSettings_SubjectSettingsSource): string {
  switch (object) {
    case SubjectSettings_SubjectSettingsSource.UNSPECIFIED:
      return "UNSPECIFIED";
    case SubjectSettings_SubjectSettingsSource.RFD:
      return "RFD";
    case SubjectSettings_SubjectSettingsSource.MOBILE:
      return "MOBILE";
    case SubjectSettings_SubjectSettingsSource.SKILLS:
      return "SKILLS";
    case SubjectSettings_SubjectSettingsSource.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * Sent from phone->vehicle to define and persist various vehicle settings. These are based on
 * settings enabled in the mobile app before and during flight
 * NOTE(roshan): If adding a setting to this message that should only be allowed for a subset
 * of users, a corresponding field should be added to VehicleSettingsOverrides and both the
 * vehicle_settings_overrides.py handle_overrides() method and
 * cloud_api/utils/switches.py apply_switches() method should be updated
 */
export interface VehicleSettings {
  utime: number;
  /**
   * Random number created by the VehicleSettingsFront on launch.
   * Any client that publishes new settings must have a matching id for settings to be accepted.
   * Enforcing this protects against rogue clients that publish settings without copying from the
   * existing settings.
   */
  validationId: number;
  /** The uclock timestamp of when the cloud-config message that set overrides was created */
  cloudConfigUclock: number;
  /**
   * If True, the user can fly to an arbitrary altitude. Otherwise, the value from
   * height_ceiling_value is respected.
   */
  heightCeilingOff: boolean;
  heightCeilingValue: number;
  ignoreGpsHeightCeil: boolean;
  /**
   * If False, the geofence field must exist, and the vehicle won't be permitted to go in/out of the
   * designated keep-out/keep-in zones, respectively. If True, the geofence will be ignored.
   */
  geofenceOff: boolean;
  /** Optional */
  geofence: Geofence | undefined;
  illuminationMode: VehicleSettings_IlluminationMode;
  obstacleMarginMode: VehicleSettings_ObstacleMarginMode;
  voxelMode: VehicleSettings_VoxelMode;
  /**
   * True if case landing disabled, otherwise False if enabled
   * NOTE(himel): This definition allows old phone versions to have case landing enabled by default
   */
  caseLandingDisabled: boolean;
  /**
   * If false, drone flies to home or launch point based on GPS (for good GPS environments)
   * If true, drone will fly home or to launch using the nav frame (for bad GPS environments,
   * where VIO is expected to perform well)
   */
  returnWithVisionOnly: boolean;
  /** True if RGB LEDs should be disabled; otherwise False to leave them on */
  rgbLedsDisabled: boolean;
  /**
   * If false, drone will not allow user to enable equirectangular 360 view.
   * If true, drone will allow user to enable equirectangular 360 view.
   */
  superzoom: boolean;
  /**
   * If false, drone will not allow user to zoom into the stream.
   * If true, drone will allow user to zoom into the stream.
   */
  digitalZoom: boolean;
  returnSettings:
    | ReturnSettings
    | undefined;
  /**
   * Deprecated. Doesn't do anything anymore.
   *
   * @deprecated
   */
  precisionFlightMode: boolean;
  /** If true, user last used enterprise app */
  enterpriseMode: boolean;
  navigationMode: NavigationMode_Mode;
  wifiBand: VehicleSettings_WifiBand;
  narrowBandEnabled: boolean;
  /** If true, turn off media card encryption when a key is present. */
  disableMediaEncryption: boolean;
  /**
   * If true, SkydioLink will be configured to use AES256 (ipsec)
   * Takes effect starting from the next pairing operation, onwards.
   */
  skydiolinkAes256: boolean;
  skysticksPairingInfo: SkysticksPairingInfo | undefined;
  ignoreLowBatteryBehavior: boolean;
  offline: boolean;
  rgbLedMode: RgbLedMode_Enum;
  /** Also known as "media clearing" */
  overwriteOldMedia: boolean;
  /** Enable writing the flight_history.csv file */
  flightHistory: boolean;
  /** Select a fixed Wifi channel. For auto, set to 0 */
  manualWifiChannel: number;
  wifiChannelList: number[];
  /**
   * if true, video stream encoding is done with multiple resolution(720/360 etc.) rather than fixed
   * rate(720)
   */
  multiResPreviewEnable: boolean;
  /**
   * if true enable centrralize forward error correction
   * This will adjust streaming video rate based on wifi link quality
   */
  centralizedFrbRateEnable: boolean;
  /** Enable writing a csv with aligned telemetry to the video on the media card */
  alignedTelemetryCsv: boolean;
  /**
   * Allow hand-wave to initialize heading.
   * This will disable the use of Magnetometer if it exists on hardware.
   */
  handWaveEnabled: boolean;
  /**
   * Display hand-wave menu in the mobile app based on cloud group
   * hand-wave-heading-calibration-enabled
   * NOTE(jesse, tung): This feature was removed in rc19.0, but the override is still
   * needed to support vehicles on older software.
   */
  displayHandWaveCalibration: boolean;
  /** Drone will reduce obstacle avoidance bubble in presence of obstacles. */
  variableObstacleAvoidance: boolean;
  /** Deprecated. Should always be set to 0 (default) to disable reboot.. */
  rebootBeforeScheduledMissionsSeconds: number;
  /** Enable dock jiggling */
  dockJigglingEnabled: boolean;
  /**
   * Disable the DockVehicleMismatchFault on the vehicle, which normally blocks takeoff when the
   * vehicle is in the wrong dock
   */
  allowTakeoffFromMismatchedDock: boolean;
  /** Enable tapping the battery button 4 times to launch */
  batteryButtonLaunch: boolean;
  /** Enable RF high power */
  rfHighPower: boolean;
  flightMode: VehicleSettings_FlightMode;
  /** Enable Remote ID */
  ridFeatureEnabled: boolean;
  /** Enable on-vehicle photogrammetry */
  allowOnVehiclePhotogrammetry: boolean;
  /** Disabled Remote ID */
  ridFeatureDisabled: boolean;
  /**
   * Enables allow_surface_scan_alpha_features based on cloud group
   * allow_surface_scan_alpha_features
   */
  allowSurfaceScanAlphaFeatures: boolean;
  /**
   * NOTE(Quentin): This field is removed on master, but required to support older software versions
   * Max uptime (in seconds) before an automatic reboot is triggered
   * NOTE: 0 disables the reboot which is set as the default
   */
  rebootMaxUptimeSeconds: number;
  battImbalanceThresholds: BatteryCellVoltageImbalanceThresholds | undefined;
  wifiCountryCode: string;
  /**
   * Enables "stop at structure" mode, which prevents the vehicle from slipping around obstacles
   * and limits the max speed of the drone when close to obstacles
   */
  stopAtStructure: boolean;
  /** Maximum speed of the drone when close to obstacles and "stop at structure" is enabled */
  speedNearObstacles: number;
  /** Enables surface scan feature */
  surfaceScan: boolean;
  landingLedMode: VehicleSettings_LandingLedMode;
  /** Enables rapid descent (E1/R47 only) when pressing the boost button */
  enableRapidDescent: boolean;
  /** Disable OA during dock landing */
  disableObstacleAvoidanceDockLanding: boolean;
  /**
   * Controls ofdma mode on both vehicle (r47) and controller (c36/c18)
   * NOTE(jon.sowers): Setting this to `true` disables ofdma mode
   */
  longRangeDisabled: boolean;
  /** Allows payloads to be plugged into the drone */
  allowPoweredPayloads: boolean;
  /** Disable country code scan */
  disableCountryCodeScan: boolean;
  /** Surface Scan modes */
  allow3dCaptureScanMode: boolean;
  allowSurfaceCaptureScanMode: boolean;
  allowMapCaptureScanMode: boolean;
  allowTowerCaptureScanMode: boolean;
  allowIndoorCaptureScanMode: boolean;
  allowIndoorCaptureScanModeV2: boolean;
  allowOrbitCaptureScanMode: boolean;
  allowCylinderCaptureScanMode: boolean;
  allowSemanticTransmissionTowerScanMode: boolean;
  /**
   * If true, vehicle does a 360 spin on takeoff to improve accelerometer bias observability. See
   * CS-8606 for an example of why one might want to do this.
   */
  fullSpinOnTakeoff: boolean;
  /** Enables the option to detect and track subjects in IR */
  allowIrSubjectTracking: boolean;
  /** Enable ISP level flip of image content */
  enableIspPixelContentFlip: boolean;
  enableVehicleRtspKlv: boolean;
  /** If true, allows gimbal to pitch fully 90 degrees up for inspections */
  gimbalInspectionMode: boolean;
  /**
   * If true and in night autonomy mode, ignore velocity limit normally set in night autonomy (new
   * limit at 20 m/s), turn off vehicle night autonomy lights, and turn off obstacle avoidance
   * NOTE(chet): should be read-only except for nightsense_manager_front.
   */
  nightsenseDisabled: boolean;
  /** If true, boost disables nightsense. */
  boostDisablesNightsense: boolean;
  /** If true, prevent showing the warning about transitioning to NightSense disabled. */
  nightsenseOffAllowedWithPoorGps: boolean;
  /** If true, prevents NightSense from turning on when switching into Night Autonomy mode. */
  nightsenseDisabledNightAutonomySwitch: boolean;
  /**
   * Used for R47 automated testing.
   * The combination of a usb flash drive and this setting cooperates this way.
   * setting | flash drive present | result
   *  false  |        false        | normal flight, applicable for most vehicles
   *  false  |         true        | USBC_IN_USE fault, the expected behavior
   *   true  |        false        | new fault blocking flight
   *   true  |         true        | flight is allowed. USBC_IN_USE is suppressed.
   */
  flashDriveRequiredForFlight: boolean;
  /**
   * adding band selection for wireless UI
   * based on band (2.4G vs UNII-1/UNII-3 vs DFS) wifi_channel_list is updated
   */
  manualBandSelection: number;
  bandSelectionList: number[];
  manualRadiowidthSelection: number;
  radiowidthSelectionList: number[];
  lteModemEnabled: boolean;
  allowCommandedEland: boolean;
  allowInternalMissionActions: boolean;
  autoLandInAttiMode: boolean;
  mhVehicleDcsCapable: boolean;
  /**
   * Unlocks increased max speed for map capture
   * TODO(Ayush): deprecate this.
   */
  increasedMaxSpeedMapCapture: boolean;
  /** Select a fixed Wifi channel for dock on rest. For auto, set to 0 */
  manualWifiChannelOnRest: number;
  allowMavlinkPerch: boolean;
  policeMode: boolean;
  lteDataSaver: boolean;
  /** "Blackout Mode" toggle, different from `rgb_leds_disabled` */
  blackoutModeEnabled: boolean;
  /** Disable GPS on vehicle */
  disableGps: boolean;
  manualFrameSelection: number;
  frameSelectionList: number[];
  enableHighAltitudeVio: boolean;
  enableAttitudeRtx: boolean;
  enableManualWff: boolean;
  oaOffGroundAvoidanceDisabled: boolean;
  enableJpgHighCompress3ds: boolean;
  enablePerch: boolean;
  manualModeSelection: number;
  modeSelectionList: number[];
  enableCommandedAttitude: boolean;
  publicSafetyPoliceMode: boolean;
  publicSafetyEmergencyMode: boolean;
  europeanUnionLighting: boolean;
  /** Force the drone into attitude mode */
  commandedAttitudeModeOn: boolean;
  enableGetMicrohardRfConfig: boolean;
  enableCommandedLostComms: boolean;
  mavlinkClientSettings: MavlinkClientSettings | undefined;
  enableGpsSpoofingDetector: boolean;
  enableRscToggle3ds: boolean;
  enableLowTakeoff: boolean;
  enableRtkSupport: boolean;
  enableFlightFileManifestTask: boolean;
  isEuropeanUnion: boolean;
  /** Turns on the slow max ground speed for the European Union */
  enableEuSlowSpeed: boolean;
  enableForceVioDegraded: boolean;
  enableThirdPartyAttachmentPrototyping: boolean;
  mhManualChannelCapacityAllocation: number;
  mhChannelCapacityAllocationList: number[];
  enableForceLowTakeoffToggle: boolean;
  forceLowTakeoff: boolean;
  alwaysLogSubjectStreamForDockFlights: boolean;
  enableInternalAttachmentPrototyping: boolean;
  enableRtkNavigation: boolean;
  enableMpegtsFileWriting: boolean;
  mapCaptureTerrainFollowAlpha: boolean;
  maxBoost: boolean;
  enable20MpsMaxSpeed: boolean;
  enableThirdPartyRadioAttachment: boolean;
  ridOperatorId: string;
  persistDisableGps: boolean;
  /**
   * Note: this is confusing as hell. The actual toggle for slow mode toggles `enable_eu_slow_speed`
   * above to turn the limit on or off. This is just the feature flag derived setting that enables
   * the toggle and feature to work at all. Also note that the slow mode feature can be enabled by
   * either this setting OR `is_european_union`.
   */
  enableSlowMode: boolean;
  enableParachuteExpectedButMissing: boolean;
  allowSubjectFollow: boolean;
  allowGpsFlightWithoutHandwave: boolean;
  pathfinderDisablesNightsense: boolean;
  enableMissionsCloudToController: boolean;
  enableVisibleNightsenseStrobing: boolean;
  enableIrNightsenseStrobing: boolean;
  /** Settings to toggle on/off Geofence layers */
  ignoreBaseGeofences: boolean;
  ignoreAutonomousGeofences: boolean;
  ignoreWarningGeofences: boolean;
  disableFrameLock: boolean;
  enableEnhancedSlBitrate: boolean;
  /**
   * Force fallback to handwave for GPS heading initialization when
   * ALLOW_GPS_FLIGHT_WITHOUT_HANDWAVE (default mag based heading estimation) is enabled.
   * This is to allow a fallback mechanism for GPS only flights when the default mag based heading
   * estimation fails.
   */
  forceFallbackToHandwave: boolean;
  enableThirdPartyAttachmentConfigs: boolean;
  enable20MpsMaxSpeedWaypointsOnly: boolean;
  enableDcsModeSelection: boolean;
  /** Skip country code validation for WiFi settings (defaults to false = validation enabled) */
  skipValidateCountrySettings: boolean;
  enableOnDemandMissionTier: boolean;
  /** Whether to enable prop icing warnings */
  enableIcingDetection: boolean;
  /** Settings for subject tracking and following */
  subjectSettings:
    | SubjectSettings
    | undefined;
  /** Whether to enable FEP power limit notification */
  enableFepPowerLimitNotification: boolean;
  enableBatteryLightDiscipline: boolean;
  enableSubjectFollowWithoutOa: boolean;
  /**
   * Reference frame for height_ceiling_value.
   * Use GPS_ABOVE_GROUND_LEVEL for AGL, WORLD_TAKEOFF for HAL (Height Above Launch).
   */
  heightCeilingReferenceFrame: FrameType_Enum;
  /** Whether to enable LSOP (Learnt SOP) */
  enableLsop: boolean;
  /** Internal testing feature flag for 30 mps ground speed */
  enable30MpsMaxGroundSpeed: boolean;
  /** FF for controller based ABI. */
  enableMobileInspectionAssets: boolean;
  /** ADS-B regulatory manager settings */
  adsbRegulatoryManagerSettings:
    | AdsbRegulatoryManagerSettings
    | undefined;
  /**
   * Whether to use geo-location-based selective DTED ingestion (true) or legacy bulk ingestion
   * (false)
   */
  enableGeoBasedDtedIngest: boolean;
}

/** The vehicle's physical illumination mode. */
export enum VehicleSettings_IlluminationMode {
  DEFAULT_ILLUMINATION = 0,
  /** VISIBLE_STROBE - Steady flash of a visible spectrum light */
  VISIBLE_STROBE = 1,
  /** INFRARED_STROBE - Steady flash of an infrared spectrum light */
  INFRARED_STROBE = 2,
  /** INFRARED_ON - Turn on infrared illumination, no strobing */
  INFRARED_ON = 3,
  UNRECOGNIZED = -1,
}

export function vehicleSettings_IlluminationModeFromJSON(object: any): VehicleSettings_IlluminationMode {
  switch (object) {
    case 0:
    case "DEFAULT_ILLUMINATION":
      return VehicleSettings_IlluminationMode.DEFAULT_ILLUMINATION;
    case 1:
    case "VISIBLE_STROBE":
      return VehicleSettings_IlluminationMode.VISIBLE_STROBE;
    case 2:
    case "INFRARED_STROBE":
      return VehicleSettings_IlluminationMode.INFRARED_STROBE;
    case 3:
    case "INFRARED_ON":
      return VehicleSettings_IlluminationMode.INFRARED_ON;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettings_IlluminationMode.UNRECOGNIZED;
  }
}

export function vehicleSettings_IlluminationModeToJSON(object: VehicleSettings_IlluminationMode): string {
  switch (object) {
    case VehicleSettings_IlluminationMode.DEFAULT_ILLUMINATION:
      return "DEFAULT_ILLUMINATION";
    case VehicleSettings_IlluminationMode.VISIBLE_STROBE:
      return "VISIBLE_STROBE";
    case VehicleSettings_IlluminationMode.INFRARED_STROBE:
      return "INFRARED_STROBE";
    case VehicleSettings_IlluminationMode.INFRARED_ON:
      return "INFRARED_ON";
    case VehicleSettings_IlluminationMode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * TODO(kristen): Add feature to be accessible to mobile, including mobile knowing if the vehicle
 * has the feature.
 */
export enum VehicleSettings_ObstacleMarginMode {
  DEFAULT_OBSTACLES = 0,
  REDUCED_OBSTACLES = 1,
  NO_OBSTACLES = 2,
  MINIMAL_OBSTACLES = 3,
  UNRECOGNIZED = -1,
}

export function vehicleSettings_ObstacleMarginModeFromJSON(object: any): VehicleSettings_ObstacleMarginMode {
  switch (object) {
    case 0:
    case "DEFAULT_OBSTACLES":
      return VehicleSettings_ObstacleMarginMode.DEFAULT_OBSTACLES;
    case 1:
    case "REDUCED_OBSTACLES":
      return VehicleSettings_ObstacleMarginMode.REDUCED_OBSTACLES;
    case 2:
    case "NO_OBSTACLES":
      return VehicleSettings_ObstacleMarginMode.NO_OBSTACLES;
    case 3:
    case "MINIMAL_OBSTACLES":
      return VehicleSettings_ObstacleMarginMode.MINIMAL_OBSTACLES;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettings_ObstacleMarginMode.UNRECOGNIZED;
  }
}

export function vehicleSettings_ObstacleMarginModeToJSON(object: VehicleSettings_ObstacleMarginMode): string {
  switch (object) {
    case VehicleSettings_ObstacleMarginMode.DEFAULT_OBSTACLES:
      return "DEFAULT_OBSTACLES";
    case VehicleSettings_ObstacleMarginMode.REDUCED_OBSTACLES:
      return "REDUCED_OBSTACLES";
    case VehicleSettings_ObstacleMarginMode.NO_OBSTACLES:
      return "NO_OBSTACLES";
    case VehicleSettings_ObstacleMarginMode.MINIMAL_OBSTACLES:
      return "MINIMAL_OBSTACLES";
    case VehicleSettings_ObstacleMarginMode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export enum VehicleSettings_VoxelMode {
  /** DEFAULT - use the default settings for the current vehicle type */
  DEFAULT = 0,
  /** UNKNOWN_UNOCCUPIED - all unknown is unoccupied */
  UNKNOWN_UNOCCUPIED = 1,
  /** UNKNOWN_OCCUPIED - all unknown is occupied */
  UNKNOWN_OCCUPIED = 2,
  UNKNOWN_OCCUPIED_1S_DECAY = 3,
  UNKNOWN_OCCUPIED_10S_DECAY = 4,
  UNKNOWN_OCCUPIED_30S_DECAY = 5,
  UNRECOGNIZED = -1,
}

export function vehicleSettings_VoxelModeFromJSON(object: any): VehicleSettings_VoxelMode {
  switch (object) {
    case 0:
    case "DEFAULT":
      return VehicleSettings_VoxelMode.DEFAULT;
    case 1:
    case "UNKNOWN_UNOCCUPIED":
      return VehicleSettings_VoxelMode.UNKNOWN_UNOCCUPIED;
    case 2:
    case "UNKNOWN_OCCUPIED":
      return VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED;
    case 3:
    case "UNKNOWN_OCCUPIED_1S_DECAY":
      return VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED_1S_DECAY;
    case 4:
    case "UNKNOWN_OCCUPIED_10S_DECAY":
      return VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED_10S_DECAY;
    case 5:
    case "UNKNOWN_OCCUPIED_30S_DECAY":
      return VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED_30S_DECAY;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettings_VoxelMode.UNRECOGNIZED;
  }
}

export function vehicleSettings_VoxelModeToJSON(object: VehicleSettings_VoxelMode): string {
  switch (object) {
    case VehicleSettings_VoxelMode.DEFAULT:
      return "DEFAULT";
    case VehicleSettings_VoxelMode.UNKNOWN_UNOCCUPIED:
      return "UNKNOWN_UNOCCUPIED";
    case VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED:
      return "UNKNOWN_OCCUPIED";
    case VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED_1S_DECAY:
      return "UNKNOWN_OCCUPIED_1S_DECAY";
    case VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED_10S_DECAY:
      return "UNKNOWN_OCCUPIED_10S_DECAY";
    case VehicleSettings_VoxelMode.UNKNOWN_OCCUPIED_30S_DECAY:
      return "UNKNOWN_OCCUPIED_30S_DECAY";
    case VehicleSettings_VoxelMode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * Mutually exclusive flight modes that the user can choose from.
 * Displayed in the flight mode menu alongside runmodes such as skills + 3D Scan.
 * TODO(bojanin): port certain runmodes to flightmodes once we dont require rebooting (i.e. 3d
 * scan).
 */
export enum VehicleSettings_FlightMode {
  /** NOT_SET - User has not chosen a flight mode. */
  NOT_SET = 0,
  /**
   * DYNAMIC - Launch from a moving craft. Flight is based on GPS position with the vision system on for
   * obstacle avoidance.
   */
  DYNAMIC = 1,
  /** MISSION - Plan a repeatable multipoint mission. */
  MISSION = 2,
  /** ATTITUDE - Flight does not hold position with GPS or the Skydio vision system. */
  ATTITUDE = 3,
  /** WIDE_PHOTOS - Capture full size of sensor with a 16x9 ratio. */
  WIDE_PHOTOS = 4,
  UNRECOGNIZED = -1,
}

export function vehicleSettings_FlightModeFromJSON(object: any): VehicleSettings_FlightMode {
  switch (object) {
    case 0:
    case "NOT_SET":
      return VehicleSettings_FlightMode.NOT_SET;
    case 1:
    case "DYNAMIC":
      return VehicleSettings_FlightMode.DYNAMIC;
    case 2:
    case "MISSION":
      return VehicleSettings_FlightMode.MISSION;
    case 3:
    case "ATTITUDE":
      return VehicleSettings_FlightMode.ATTITUDE;
    case 4:
    case "WIDE_PHOTOS":
      return VehicleSettings_FlightMode.WIDE_PHOTOS;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettings_FlightMode.UNRECOGNIZED;
  }
}

export function vehicleSettings_FlightModeToJSON(object: VehicleSettings_FlightMode): string {
  switch (object) {
    case VehicleSettings_FlightMode.NOT_SET:
      return "NOT_SET";
    case VehicleSettings_FlightMode.DYNAMIC:
      return "DYNAMIC";
    case VehicleSettings_FlightMode.MISSION:
      return "MISSION";
    case VehicleSettings_FlightMode.ATTITUDE:
      return "ATTITUDE";
    case VehicleSettings_FlightMode.WIDE_PHOTOS:
      return "WIDE_PHOTOS";
    case VehicleSettings_FlightMode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Select a WifiBand, default to 20 MHZ */
export enum VehicleSettings_WifiBand {
  DEFAULT_20_MHZ = 0,
  NARROW_10_MHZ = 1,
  NARROW_5_MHZ = 2,
  UNRECOGNIZED = -1,
}

export function vehicleSettings_WifiBandFromJSON(object: any): VehicleSettings_WifiBand {
  switch (object) {
    case 0:
    case "DEFAULT_20_MHZ":
      return VehicleSettings_WifiBand.DEFAULT_20_MHZ;
    case 1:
    case "NARROW_10_MHZ":
      return VehicleSettings_WifiBand.NARROW_10_MHZ;
    case 2:
    case "NARROW_5_MHZ":
      return VehicleSettings_WifiBand.NARROW_5_MHZ;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettings_WifiBand.UNRECOGNIZED;
  }
}

export function vehicleSettings_WifiBandToJSON(object: VehicleSettings_WifiBand): string {
  switch (object) {
    case VehicleSettings_WifiBand.DEFAULT_20_MHZ:
      return "DEFAULT_20_MHZ";
    case VehicleSettings_WifiBand.NARROW_10_MHZ:
      return "NARROW_10_MHZ";
    case VehicleSettings_WifiBand.NARROW_5_MHZ:
      return "NARROW_5_MHZ";
    case VehicleSettings_WifiBand.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** R47 Landing LEDs. */
export enum VehicleSettings_LandingLedMode {
  LANDING_LED_OFF = 0,
  /** LANDING_LED_ON - NEXT ID: 2 */
  LANDING_LED_ON = 1,
  UNRECOGNIZED = -1,
}

export function vehicleSettings_LandingLedModeFromJSON(object: any): VehicleSettings_LandingLedMode {
  switch (object) {
    case 0:
    case "LANDING_LED_OFF":
      return VehicleSettings_LandingLedMode.LANDING_LED_OFF;
    case 1:
    case "LANDING_LED_ON":
      return VehicleSettings_LandingLedMode.LANDING_LED_ON;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettings_LandingLedMode.UNRECOGNIZED;
  }
}

export function vehicleSettings_LandingLedModeToJSON(object: VehicleSettings_LandingLedMode): string {
  switch (object) {
    case VehicleSettings_LandingLedMode.LANDING_LED_OFF:
      return "LANDING_LED_OFF";
    case VehicleSettings_LandingLedMode.LANDING_LED_ON:
      return "LANDING_LED_ON";
    case VehicleSettings_LandingLedMode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * Sent from the cloud->vehicle via cloud config to securely allow or override values set
 * in the VehicleSettings message
 */
export interface VehicleSettingsOverrides {
  /**
   * We use DEFAULT=FORCE_FALSE here since this is a premium feature that should only be
   * permitted if explicitly allowed by the cloud
   */
  heightCeilingOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * Only allow these enum values and override to the first allowed if the setting is not allowed
   * If empty, equivalent to allowing all values since this is not a premium feature
   */
  allowedIlluminationModes: VehicleSettings_IlluminationMode[];
  /**
   * Only allow these enum values and override to the first allowed if the setting is not allowed
   * If empty, the default DEFAULT_OBSTACLES mode is forced since this is a premium feature
   */
  allowedObstacleMarginModes: VehicleSettings_ObstacleMarginMode[];
  /**
   * DEFAULT should be FORCE_FALSE here since this is an enterprise-only feature & must be allowed
   * by the cloud.
   */
  returnWithVisionOnlyOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * DEFAULT should be FORCE_FALSE here since this is an enterprise-only feature & must be allowed
   * by the cloud.
   */
  superzoomOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * DEFAULT should be FORCE_FALSE here since this is an enterprise-only feature & must be allowed
   * by the cloud.
   */
  digitalZoomOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * DEFAULT should be FORCE_FALSE here since this is an enterprise-only feature & must be allowed
   * by the cloud.
   */
  precisionFlightModeOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * DEFAULT should be FORCE_FALSE here since ipsec/AES256 incur additional CPU penalties, and this
   * feature does not benefit those who do not contractually require it.
   */
  skydiolinkAes256Override: VehicleSettingsOverrides_BooleanOverride;
  /**
   * Only allow these enum values and override to the first allowed if the setting is not allowed
   * If empty, the default DEFAULT_NAV mode is forced since AUTONOMY_OFF is a premium feature.
   */
  allowedNavigationModes: NavigationMode_Mode[];
  /**
   * DEFAULT should be FORCE_FALSE here since this is a feature specifically for SRR, and is
   * not safe for novice / untrained users. It must be allowed by the cloud.
   */
  ignoreLowBatteryBehaviorOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * DEFAULT should be FORCE_TRUE so that encryption is disabled unless it is explicitly allowed by
   * cloud switches.
   */
  disableMediaEncryptionOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * Media encryption for C18 online is somewhat busted on Asimov rc39, so we cannot enable that
   * switch for C18 online users on that software. In order to enable only for Battin (rc40)
   * onwards, we are making a new feature switch that will only be interpreted by newer rc40+
   * software.
   * https://www.notion.so/skydio/RFC-Enable-media-encryption-on-Battin-only-241dc5ce82938002b665e510d75e9e3e
   */
  disableMediaEncryptionOverrideV2: VehicleSettingsOverrides_BooleanOverride;
  /** DEFAULT should be FORCE_FALSE so that default vehicles are not considered as offline */
  offlineOverride: VehicleSettingsOverrides_BooleanOverride;
  /** DEFAULT should be FORCE_FALSE so that default vehicles have RGB LEDs enabled */
  rgbLedDisableOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * DEFAULT should be FORCE_FALSE here since this is an enterprise-only feature & must be allowed
   * by the cloud.
   */
  sdcardOverwriteOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * We use DEFAULT=FORCE_FALSE here since this is a premium feature that should only be
   * permitted if explicitly allowed by the cloud
   */
  flightHistoryOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * We use DEFAULT=FORCE_FALSE here since this is a premium feature that should only be
   * permitted if explicitly allowed by the cloud
   */
  multiResPreviewEnableOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * We use DEFAULT=FORCE_FALSE here since this is a premium feature that should only be
   * permitted if explicitly allowed by the cloud
   */
  centralizedFrbRateEnableOverride: VehicleSettingsOverrides_BooleanOverride;
  /** DEFAULT should be FORCE_FALSE here since this is a feature that must be enabled explicitly */
  alignedTelemetryCsvOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * Override to display hand wave calibration menu based on group
   * hand-wave-heading-calibration-enabled
   * NOTE(jesse, tung): This feature was removed in rc19.0, but the override is still
   * needed to support vehicles on older software.
   */
  displayHandWaveCalibrationOverride: VehicleSettingsOverrides_BooleanOverride;
  variableObstacleAvoidanceOverride: VehicleSettingsOverrides_BooleanOverride;
  dockJigglingEnabledOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow the vehicle to take off even if it is in the wrong dock */
  allowTakeoffFromMismatchedDockOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to enable on-vehicle photogrammetry. */
  allowOnVehiclePhotogrammetryOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow the vehicle to backtrack in rtx */
  useBacktrackingRtxOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to enable Remote-id broadcast over wifi */
  ridFeatureEnabledOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to disable Remote-id broadcast over wifi */
  ridFeatureDisabledOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * Override to enable allow_surface_scan_alpha_features based on group
   * allow_surface_scan_alpha_features
   */
  allowSurfaceScanAlphaFeaturesOverride: VehicleSettingsOverrides_BooleanOverride;
  /**
   * NOTE(Quentin): This field is removed on master, but required to support older software versions
   * Override to set the maximum uptime (in seconds) before an automatic reboot
   */
  rebootMaxUptimeSecondsOverride: number;
  /** Default vehicle settings geofence */
  defaultGeofence:
    | Geofence
    | undefined;
  /** Override the battery cell voltage imbalance thresholds */
  battImbalanceThresholdsOverride:
    | BatteryCellVoltageImbalanceThresholds
    | undefined;
  /** Override to enable surface_scan based on group surface_scan */
  surfaceScanOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to disable OA during dock landing */
  disableObstacleAvoidanceDockLandingOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow powered payloads */
  allowPoweredPayloadsOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to disble country_code scan for FED/ATO */
  disableCountryCodeScanOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Surface Scan Modes */
  allow3dCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowSurfaceCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowMapCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowTowerCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowIndoorCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowIndoorCaptureScanModeV2Override: VehicleSettingsOverrides_BooleanOverride;
  allowOrbitCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowCylinderCaptureScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  allowSemanticTransmissionTowerScanModeOverride: VehicleSettingsOverrides_BooleanOverride;
  fullSpinOnTakeoffOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow IR subject tracking */
  allowIrSubjectTrackingOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to enable ISP pixel content flips */
  enableIspPixelContentFlipOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Use RTSP server on vehicle instead of RTP and KLV */
  enableVehicleRtspKlvOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow gimbal to pitch fully 90 degrees up for inspections */
  gimbalInspectionModeOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Used for R47 automated testing. */
  flashDriveRequiredForFlightOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow Commanded Eland */
  allowCommandedElandOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow internal mission actions to be run */
  allowInternalMissionActionsOverride: VehicleSettingsOverrides_BooleanOverride;
  increasedMaxSpeedMapCaptureOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Override to allow mavlink perch */
  allowMavlinkPerchOverride: VehicleSettingsOverrides_BooleanOverride;
  /** DEFAULT should be USER_SETTING so that default vehicles have RGB LEDs enabled */
  forceRgbLedDisableOverride: VehicleSettingsOverrides_BooleanOverride;
  enableHighAltitudeVioOverride: VehicleSettingsOverrides_BooleanOverride;
  enableAttitudeRtxOverride: VehicleSettingsOverrides_BooleanOverride;
  enableManualWffOverride: VehicleSettingsOverrides_BooleanOverride;
  enableJpgHighCompress3dsOverride: VehicleSettingsOverrides_BooleanOverride;
  enableGetMicrohardRfConfigOverride: VehicleSettingsOverrides_BooleanOverride;
  enableCommandedLostCommsOverride: VehicleSettingsOverrides_BooleanOverride;
  enableGpsSpoofingDetectorOverride: VehicleSettingsOverrides_BooleanOverride;
  enableRscToggle3dsOverride: VehicleSettingsOverrides_BooleanOverride;
  enableLowTakeoffOverride: VehicleSettingsOverrides_BooleanOverride;
  enableRtkSupportOverride: VehicleSettingsOverrides_BooleanOverride;
  enableFlightFileManifestTaskOverride: VehicleSettingsOverrides_BooleanOverride;
  isEuropeanUnionOverride: VehicleSettingsOverrides_BooleanOverride;
  enablePerchOverride: VehicleSettingsOverrides_BooleanOverride;
  enableThirdPartyAttachmentPrototypingOverride: VehicleSettingsOverrides_BooleanOverride;
  alwaysLogSubjectStreamForDockFlightsOverride: VehicleSettingsOverrides_BooleanOverride;
  enableForceLowTakeoffToggleOverride: VehicleSettingsOverrides_BooleanOverride;
  enableInternalAttachmentPrototypingOverride: VehicleSettingsOverrides_BooleanOverride;
  enableRtkNavigation: VehicleSettingsOverrides_BooleanOverride;
  enableMpegtsFileWritingOverride: VehicleSettingsOverrides_BooleanOverride;
  mapCaptureTerrainFollowAlphaOverride: VehicleSettingsOverrides_BooleanOverride;
  enable20MpsMaxSpeedOverride: VehicleSettingsOverrides_BooleanOverride;
  enableThirdPartyRadioAttachmentOverride: VehicleSettingsOverrides_BooleanOverride;
  enableSlowModeOverride: VehicleSettingsOverrides_BooleanOverride;
  enableParachuteExpectedButMissingOverride: VehicleSettingsOverrides_BooleanOverride;
  allowSubjectFollowOverride: VehicleSettingsOverrides_BooleanOverride;
  allowGpsFlightWithoutHandwaveOverride: VehicleSettingsOverrides_BooleanOverride;
  /** Overrides whether range limits are on/off by default */
  geofenceOffOverride: VehicleSettingsOverrides_BooleanOverride;
  enableMissionsCloudToControllerOverride: VehicleSettingsOverrides_BooleanOverride;
  enableVisibleNightsenseStrobingOverride: VehicleSettingsOverrides_BooleanOverride;
  enableIrNightsenseStrobingOverride: VehicleSettingsOverrides_BooleanOverride;
  enableEnhancedSlBitrateOverride: VehicleSettingsOverrides_BooleanOverride;
  enableThirdPartyAttachmentConfigsOverride: VehicleSettingsOverrides_BooleanOverride;
  enable20MpsMaxSpeedWaypointsOnlyOverride: VehicleSettingsOverrides_BooleanOverride;
  enableDcsModeSelectionOverride: VehicleSettingsOverrides_BooleanOverride;
  skipValidateCountrySettingsOverride: VehicleSettingsOverrides_BooleanOverride;
  enableOnDemandMissionTierOverride: VehicleSettingsOverrides_BooleanOverride;
  enableIcingDetectionOverride: VehicleSettingsOverrides_BooleanOverride;
  enableFepPowerLimitNotificationOverride: VehicleSettingsOverrides_BooleanOverride;
  enableBatteryLightDisciplineOverride: VehicleSettingsOverrides_BooleanOverride;
  enableSubjectFollowWithoutOaOverride: VehicleSettingsOverrides_BooleanOverride;
  enableLsopOverride: VehicleSettingsOverrides_BooleanOverride;
  enable30MpsMaxGroundSpeedOverride: VehicleSettingsOverrides_BooleanOverride;
  enableMobileInspectionAssetsOverride: VehicleSettingsOverrides_BooleanOverride;
  enableGeoBasedDtedIngestOverride: VehicleSettingsOverrides_BooleanOverride;
}

export enum VehicleSettingsOverrides_BooleanOverride {
  /**
   * DEFAULT - NOTE(roshan): Depending on each override type, we can treat the DEFAULT case differently.
   * For premium or safety-critical features, we treat DEFAULT the same as FORCE_FALSE.
   * For normal user/consumer features, we treat DEFAULT the same as USER_SETTING.
   * See the 'default_false' flag in vehicle_settings_overrides.py 'apply_boolean_override'
   */
  DEFAULT = 0,
  /** FORCE_TRUE - force this setting to True */
  FORCE_TRUE = 1,
  /** FORCE_FALSE - force this setting to False */
  FORCE_FALSE = 2,
  /** USER_SETTING - allow the user setting to apply */
  USER_SETTING = 3,
  /** INIT_TRUE - same as USER_SETTING but initialize the override to True (on boot) */
  INIT_TRUE = 4,
  UNRECOGNIZED = -1,
}

export function vehicleSettingsOverrides_BooleanOverrideFromJSON(
  object: any,
): VehicleSettingsOverrides_BooleanOverride {
  switch (object) {
    case 0:
    case "DEFAULT":
      return VehicleSettingsOverrides_BooleanOverride.DEFAULT;
    case 1:
    case "FORCE_TRUE":
      return VehicleSettingsOverrides_BooleanOverride.FORCE_TRUE;
    case 2:
    case "FORCE_FALSE":
      return VehicleSettingsOverrides_BooleanOverride.FORCE_FALSE;
    case 3:
    case "USER_SETTING":
      return VehicleSettingsOverrides_BooleanOverride.USER_SETTING;
    case 4:
    case "INIT_TRUE":
      return VehicleSettingsOverrides_BooleanOverride.INIT_TRUE;
    case -1:
    case "UNRECOGNIZED":
    default:
      return VehicleSettingsOverrides_BooleanOverride.UNRECOGNIZED;
  }
}

export function vehicleSettingsOverrides_BooleanOverrideToJSON(
  object: VehicleSettingsOverrides_BooleanOverride,
): string {
  switch (object) {
    case VehicleSettingsOverrides_BooleanOverride.DEFAULT:
      return "DEFAULT";
    case VehicleSettingsOverrides_BooleanOverride.FORCE_TRUE:
      return "FORCE_TRUE";
    case VehicleSettingsOverrides_BooleanOverride.FORCE_FALSE:
      return "FORCE_FALSE";
    case VehicleSettingsOverrides_BooleanOverride.USER_SETTING:
      return "USER_SETTING";
    case VehicleSettingsOverrides_BooleanOverride.INIT_TRUE:
      return "INIT_TRUE";
    case VehicleSettingsOverrides_BooleanOverride.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface SettingsUpdateResponse {
  success: boolean;
}

/** This message indicates the source stream: subject_camera (False) or IR_Stream (True) */
export interface SettingsPreviewSource {
  utime: number;
  previewSource: boolean;
}

function createBaseAllSettings(): AllSettings {
  return {
    beaconSettings: undefined,
    joysticksSettings: undefined,
    captureSettings: undefined,
    vehicleSettings: undefined,
    irCaptureSettings: undefined,
    remoteidAuthSettings: undefined,
  };
}

export const AllSettings = {
  encode(message: AllSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.beaconSettings !== undefined) {
      BeaconSettings.encode(message.beaconSettings, writer.uint32(10).fork()).ldelim();
    }
    if (message.joysticksSettings !== undefined) {
      JoysticksSettings.encode(message.joysticksSettings, writer.uint32(18).fork()).ldelim();
    }
    if (message.captureSettings !== undefined) {
      CaptureSettings.encode(message.captureSettings, writer.uint32(26).fork()).ldelim();
    }
    if (message.vehicleSettings !== undefined) {
      VehicleSettings.encode(message.vehicleSettings, writer.uint32(34).fork()).ldelim();
    }
    if (message.irCaptureSettings !== undefined) {
      IrCaptureSettings.encode(message.irCaptureSettings, writer.uint32(42).fork()).ldelim();
    }
    if (message.remoteidAuthSettings !== undefined) {
      RemoteIDAuthSettings.encode(message.remoteidAuthSettings, writer.uint32(50).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): AllSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAllSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.beaconSettings = BeaconSettings.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.joysticksSettings = JoysticksSettings.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.captureSettings = CaptureSettings.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.vehicleSettings = VehicleSettings.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.irCaptureSettings = IrCaptureSettings.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.remoteidAuthSettings = RemoteIDAuthSettings.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): AllSettings {
    return {
      beaconSettings: isSet(object.beaconSettings) ? BeaconSettings.fromJSON(object.beaconSettings) : undefined,
      joysticksSettings: isSet(object.joysticksSettings)
        ? JoysticksSettings.fromJSON(object.joysticksSettings)
        : undefined,
      captureSettings: isSet(object.captureSettings) ? CaptureSettings.fromJSON(object.captureSettings) : undefined,
      vehicleSettings: isSet(object.vehicleSettings) ? VehicleSettings.fromJSON(object.vehicleSettings) : undefined,
      irCaptureSettings: isSet(object.irCaptureSettings)
        ? IrCaptureSettings.fromJSON(object.irCaptureSettings)
        : undefined,
      remoteidAuthSettings: isSet(object.remoteidAuthSettings)
        ? RemoteIDAuthSettings.fromJSON(object.remoteidAuthSettings)
        : undefined,
    };
  },

  toJSON(message: AllSettings): unknown {
    const obj: any = {};
    if (message.beaconSettings !== undefined) {
      obj.beaconSettings = BeaconSettings.toJSON(message.beaconSettings);
    }
    if (message.joysticksSettings !== undefined) {
      obj.joysticksSettings = JoysticksSettings.toJSON(message.joysticksSettings);
    }
    if (message.captureSettings !== undefined) {
      obj.captureSettings = CaptureSettings.toJSON(message.captureSettings);
    }
    if (message.vehicleSettings !== undefined) {
      obj.vehicleSettings = VehicleSettings.toJSON(message.vehicleSettings);
    }
    if (message.irCaptureSettings !== undefined) {
      obj.irCaptureSettings = IrCaptureSettings.toJSON(message.irCaptureSettings);
    }
    if (message.remoteidAuthSettings !== undefined) {
      obj.remoteidAuthSettings = RemoteIDAuthSettings.toJSON(message.remoteidAuthSettings);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<AllSettings>, I>>(base?: I): AllSettings {
    return AllSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<AllSettings>, I>>(object: I): AllSettings {
    const message = createBaseAllSettings();
    message.beaconSettings = (object.beaconSettings !== undefined && object.beaconSettings !== null)
      ? BeaconSettings.fromPartial(object.beaconSettings)
      : undefined;
    message.joysticksSettings = (object.joysticksSettings !== undefined && object.joysticksSettings !== null)
      ? JoysticksSettings.fromPartial(object.joysticksSettings)
      : undefined;
    message.captureSettings = (object.captureSettings !== undefined && object.captureSettings !== null)
      ? CaptureSettings.fromPartial(object.captureSettings)
      : undefined;
    message.vehicleSettings = (object.vehicleSettings !== undefined && object.vehicleSettings !== null)
      ? VehicleSettings.fromPartial(object.vehicleSettings)
      : undefined;
    message.irCaptureSettings = (object.irCaptureSettings !== undefined && object.irCaptureSettings !== null)
      ? IrCaptureSettings.fromPartial(object.irCaptureSettings)
      : undefined;
    message.remoteidAuthSettings = (object.remoteidAuthSettings !== undefined && object.remoteidAuthSettings !== null)
      ? RemoteIDAuthSettings.fromPartial(object.remoteidAuthSettings)
      : undefined;
    return message;
  },
};

function createBaseBeaconSettings(): BeaconSettings {
  return { utime: 0, selectedSkills: [], locked: false, displayUnits: 0, isConsumerAppConnected: false };
}

export const BeaconSettings = {
  encode(message: BeaconSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    for (const v of message.selectedSkills) {
      writer.uint32(18).string(v!);
    }
    if (message.locked === true) {
      writer.uint32(24).bool(message.locked);
    }
    if (message.displayUnits !== 0) {
      writer.uint32(32).int32(message.displayUnits);
    }
    if (message.isConsumerAppConnected === true) {
      writer.uint32(40).bool(message.isConsumerAppConnected);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): BeaconSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseBeaconSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.selectedSkills.push(reader.string());
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.locked = reader.bool();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.displayUnits = reader.int32() as any;
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.isConsumerAppConnected = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): BeaconSettings {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      selectedSkills: globalThis.Array.isArray(object?.selectedSkills)
        ? object.selectedSkills.map((e: any) => globalThis.String(e))
        : [],
      locked: isSet(object.locked) ? globalThis.Boolean(object.locked) : false,
      displayUnits: isSet(object.displayUnits) ? displayUnits_EnumFromJSON(object.displayUnits) : 0,
      isConsumerAppConnected: isSet(object.isConsumerAppConnected)
        ? globalThis.Boolean(object.isConsumerAppConnected)
        : false,
    };
  },

  toJSON(message: BeaconSettings): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.selectedSkills?.length) {
      obj.selectedSkills = message.selectedSkills;
    }
    if (message.locked === true) {
      obj.locked = message.locked;
    }
    if (message.displayUnits !== 0) {
      obj.displayUnits = displayUnits_EnumToJSON(message.displayUnits);
    }
    if (message.isConsumerAppConnected === true) {
      obj.isConsumerAppConnected = message.isConsumerAppConnected;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<BeaconSettings>, I>>(base?: I): BeaconSettings {
    return BeaconSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<BeaconSettings>, I>>(object: I): BeaconSettings {
    const message = createBaseBeaconSettings();
    message.utime = object.utime ?? 0;
    message.selectedSkills = object.selectedSkills?.map((e) => e) || [];
    message.locked = object.locked ?? false;
    message.displayUnits = object.displayUnits ?? 0;
    message.isConsumerAppConnected = object.isConsumerAppConnected ?? false;
    return message;
  },
};

function createBaseImageParameters(): ImageParameters {
  return { utime: 0, iso: 0, exposure: 0, correlatedColorTemp: 0, compensationMode: 0 };
}

export const ImageParameters = {
  encode(message: ImageParameters, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.iso !== 0) {
      writer.uint32(16).int32(message.iso);
    }
    if (message.exposure !== 0) {
      writer.uint32(24).int64(message.exposure);
    }
    if (message.correlatedColorTemp !== 0) {
      writer.uint32(32).int32(message.correlatedColorTemp);
    }
    if (message.compensationMode !== 0) {
      writer.uint32(40).int32(message.compensationMode);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ImageParameters {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseImageParameters();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.iso = reader.int32();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.exposure = longToNumber(reader.int64() as Long);
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.correlatedColorTemp = reader.int32();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.compensationMode = reader.int32();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ImageParameters {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      iso: isSet(object.iso) ? globalThis.Number(object.iso) : 0,
      exposure: isSet(object.exposure) ? globalThis.Number(object.exposure) : 0,
      correlatedColorTemp: isSet(object.correlatedColorTemp) ? globalThis.Number(object.correlatedColorTemp) : 0,
      compensationMode: isSet(object.compensationMode) ? globalThis.Number(object.compensationMode) : 0,
    };
  },

  toJSON(message: ImageParameters): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.iso !== 0) {
      obj.iso = Math.round(message.iso);
    }
    if (message.exposure !== 0) {
      obj.exposure = Math.round(message.exposure);
    }
    if (message.correlatedColorTemp !== 0) {
      obj.correlatedColorTemp = Math.round(message.correlatedColorTemp);
    }
    if (message.compensationMode !== 0) {
      obj.compensationMode = Math.round(message.compensationMode);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ImageParameters>, I>>(base?: I): ImageParameters {
    return ImageParameters.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ImageParameters>, I>>(object: I): ImageParameters {
    const message = createBaseImageParameters();
    message.utime = object.utime ?? 0;
    message.iso = object.iso ?? 0;
    message.exposure = object.exposure ?? 0;
    message.correlatedColorTemp = object.correlatedColorTemp ?? 0;
    message.compensationMode = object.compensationMode ?? 0;
    return message;
  },
};

function createBaseCaptureSettings(): CaptureSettings {
  return {
    utime: 0,
    validationId: 0,
    source: 0,
    recordingMode: 0,
    videoAutoRecord: false,
    photoFormat: 0,
    photoInterval: 0,
    iso: 0,
    exposure: 0,
    compensation: 0,
    whiteBalance: 0,
    encoderMode: 0,
    streamEncoderMode: 0,
    colorMode: 0,
    userBitratePreset: 0,
    autofocusRegion: undefined,
    autoexposureRegion: undefined,
    autofocusMode: 0,
    manualFocusPosition: 0,
    userBitrateOverride: 0,
    antiBandingMode: 0,
    photo15mpEnabled: false,
    publishThumbnail: false,
    thumbnailWidth: 0,
    videoRecordingMode: 0,
    photoRecordingMode: 0,
    customFolder: "",
    useCustomFolder: false,
    recordingFileFormat: 0,
    eisHorizonLock: false,
    eisSmoothness: 0,
    eisMargin: 0,
    skillsPhotoMetadata: undefined,
  };
}

export const CaptureSettings = {
  encode(message: CaptureSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.validationId !== 0) {
      writer.uint32(144).int32(message.validationId);
    }
    if (message.source !== 0) {
      writer.uint32(184).int32(message.source);
    }
    if (message.recordingMode !== 0) {
      writer.uint32(16).int32(message.recordingMode);
    }
    if (message.videoAutoRecord === true) {
      writer.uint32(24).bool(message.videoAutoRecord);
    }
    if (message.photoFormat !== 0) {
      writer.uint32(64).int32(message.photoFormat);
    }
    if (message.photoInterval !== 0) {
      writer.uint32(72).int32(message.photoInterval);
    }
    if (message.iso !== 0) {
      writer.uint32(32).int32(message.iso);
    }
    if (message.exposure !== 0) {
      writer.uint32(40).int32(message.exposure);
    }
    if (message.compensation !== 0) {
      writer.uint32(56).int32(message.compensation);
    }
    if (message.whiteBalance !== 0) {
      writer.uint32(48).int32(message.whiteBalance);
    }
    if (message.encoderMode !== 0) {
      writer.uint32(80).int32(message.encoderMode);
    }
    if (message.streamEncoderMode !== 0) {
      writer.uint32(176).int32(message.streamEncoderMode);
    }
    if (message.colorMode !== 0) {
      writer.uint32(88).int32(message.colorMode);
    }
    if (message.userBitratePreset !== 0) {
      writer.uint32(104).int32(message.userBitratePreset);
    }
    if (message.autofocusRegion !== undefined) {
      AutoRegionRequest.encode(message.autofocusRegion, writer.uint32(154).fork()).ldelim();
    }
    if (message.autoexposureRegion !== undefined) {
      AutoRegionRequest.encode(message.autoexposureRegion, writer.uint32(170).fork()).ldelim();
    }
    if (message.autofocusMode !== 0) {
      writer.uint32(160).int32(message.autofocusMode);
    }
    if (message.manualFocusPosition !== 0) {
      writer.uint32(197).float(message.manualFocusPosition);
    }
    if (message.userBitrateOverride !== 0) {
      writer.uint32(120).int32(message.userBitrateOverride);
    }
    if (message.antiBandingMode !== 0) {
      writer.uint32(112).int32(message.antiBandingMode);
    }
    if (message.photo15mpEnabled === true) {
      writer.uint32(96).bool(message.photo15mpEnabled);
    }
    if (message.publishThumbnail === true) {
      writer.uint32(128).bool(message.publishThumbnail);
    }
    if (message.thumbnailWidth !== 0) {
      writer.uint32(136).int32(message.thumbnailWidth);
    }
    if (message.videoRecordingMode !== 0) {
      writer.uint32(200).int32(message.videoRecordingMode);
    }
    if (message.photoRecordingMode !== 0) {
      writer.uint32(208).int32(message.photoRecordingMode);
    }
    if (message.customFolder !== "") {
      writer.uint32(218).string(message.customFolder);
    }
    if (message.useCustomFolder === true) {
      writer.uint32(224).bool(message.useCustomFolder);
    }
    if (message.recordingFileFormat !== 0) {
      writer.uint32(232).int32(message.recordingFileFormat);
    }
    if (message.eisHorizonLock === true) {
      writer.uint32(240).bool(message.eisHorizonLock);
    }
    if (message.eisSmoothness !== 0) {
      writer.uint32(248).int32(message.eisSmoothness);
    }
    if (message.eisMargin !== 0) {
      writer.uint32(269).float(message.eisMargin);
    }
    if (message.skillsPhotoMetadata !== undefined) {
      SkillsPhotoMetadata.encode(message.skillsPhotoMetadata, writer.uint32(258).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): CaptureSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCaptureSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 18:
          if (tag !== 144) {
            break;
          }

          message.validationId = reader.int32();
          continue;
        case 23:
          if (tag !== 184) {
            break;
          }

          message.source = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.recordingMode = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.videoAutoRecord = reader.bool();
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.photoFormat = reader.int32() as any;
          continue;
        case 9:
          if (tag !== 72) {
            break;
          }

          message.photoInterval = reader.int32() as any;
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.iso = reader.int32() as any;
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.exposure = reader.int32() as any;
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.compensation = reader.int32() as any;
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.whiteBalance = reader.int32() as any;
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.encoderMode = reader.int32() as any;
          continue;
        case 22:
          if (tag !== 176) {
            break;
          }

          message.streamEncoderMode = reader.int32() as any;
          continue;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.colorMode = reader.int32() as any;
          continue;
        case 13:
          if (tag !== 104) {
            break;
          }

          message.userBitratePreset = reader.int32() as any;
          continue;
        case 19:
          if (tag !== 154) {
            break;
          }

          message.autofocusRegion = AutoRegionRequest.decode(reader, reader.uint32());
          continue;
        case 21:
          if (tag !== 170) {
            break;
          }

          message.autoexposureRegion = AutoRegionRequest.decode(reader, reader.uint32());
          continue;
        case 20:
          if (tag !== 160) {
            break;
          }

          message.autofocusMode = reader.int32() as any;
          continue;
        case 24:
          if (tag !== 197) {
            break;
          }

          message.manualFocusPosition = reader.float();
          continue;
        case 15:
          if (tag !== 120) {
            break;
          }

          message.userBitrateOverride = reader.int32();
          continue;
        case 14:
          if (tag !== 112) {
            break;
          }

          message.antiBandingMode = reader.int32() as any;
          continue;
        case 12:
          if (tag !== 96) {
            break;
          }

          message.photo15mpEnabled = reader.bool();
          continue;
        case 16:
          if (tag !== 128) {
            break;
          }

          message.publishThumbnail = reader.bool();
          continue;
        case 17:
          if (tag !== 136) {
            break;
          }

          message.thumbnailWidth = reader.int32();
          continue;
        case 25:
          if (tag !== 200) {
            break;
          }

          message.videoRecordingMode = reader.int32() as any;
          continue;
        case 26:
          if (tag !== 208) {
            break;
          }

          message.photoRecordingMode = reader.int32() as any;
          continue;
        case 27:
          if (tag !== 218) {
            break;
          }

          message.customFolder = reader.string();
          continue;
        case 28:
          if (tag !== 224) {
            break;
          }

          message.useCustomFolder = reader.bool();
          continue;
        case 29:
          if (tag !== 232) {
            break;
          }

          message.recordingFileFormat = reader.int32() as any;
          continue;
        case 30:
          if (tag !== 240) {
            break;
          }

          message.eisHorizonLock = reader.bool();
          continue;
        case 31:
          if (tag !== 248) {
            break;
          }

          message.eisSmoothness = reader.int32();
          continue;
        case 33:
          if (tag !== 269) {
            break;
          }

          message.eisMargin = reader.float();
          continue;
        case 32:
          if (tag !== 258) {
            break;
          }

          message.skillsPhotoMetadata = SkillsPhotoMetadata.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): CaptureSettings {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      validationId: isSet(object.validationId) ? globalThis.Number(object.validationId) : 0,
      source: isSet(object.source) ? captureSettings_CaptureSettingsSourceFromJSON(object.source) : 0,
      recordingMode: isSet(object.recordingMode) ? recordingMode_EnumFromJSON(object.recordingMode) : 0,
      videoAutoRecord: isSet(object.videoAutoRecord) ? globalThis.Boolean(object.videoAutoRecord) : false,
      photoFormat: isSet(object.photoFormat) ? photoFormat_EnumFromJSON(object.photoFormat) : 0,
      photoInterval: isSet(object.photoInterval) ? photoIntervalMode_EnumFromJSON(object.photoInterval) : 0,
      iso: isSet(object.iso) ? isoMode_EnumFromJSON(object.iso) : 0,
      exposure: isSet(object.exposure) ? exposureMode_EnumFromJSON(object.exposure) : 0,
      compensation: isSet(object.compensation) ? compensationMode_EnumFromJSON(object.compensation) : 0,
      whiteBalance: isSet(object.whiteBalance) ? awbMode_EnumFromJSON(object.whiteBalance) : 0,
      encoderMode: isSet(object.encoderMode) ? encoderMode_EnumFromJSON(object.encoderMode) : 0,
      streamEncoderMode: isSet(object.streamEncoderMode) ? encoderMode_EnumFromJSON(object.streamEncoderMode) : 0,
      colorMode: isSet(object.colorMode) ? colorMode_EnumFromJSON(object.colorMode) : 0,
      userBitratePreset: isSet(object.userBitratePreset)
        ? recordingBitratePreset_EnumFromJSON(object.userBitratePreset)
        : 0,
      autofocusRegion: isSet(object.autofocusRegion) ? AutoRegionRequest.fromJSON(object.autofocusRegion) : undefined,
      autoexposureRegion: isSet(object.autoexposureRegion)
        ? AutoRegionRequest.fromJSON(object.autoexposureRegion)
        : undefined,
      autofocusMode: isSet(object.autofocusMode) ? autofocusMode_EnumFromJSON(object.autofocusMode) : 0,
      manualFocusPosition: isSet(object.manualFocusPosition) ? globalThis.Number(object.manualFocusPosition) : 0,
      userBitrateOverride: isSet(object.userBitrateOverride) ? globalThis.Number(object.userBitrateOverride) : 0,
      antiBandingMode: isSet(object.antiBandingMode) ? antiBandingMode_EnumFromJSON(object.antiBandingMode) : 0,
      photo15mpEnabled: isSet(object.photo15mpEnabled) ? globalThis.Boolean(object.photo15mpEnabled) : false,
      publishThumbnail: isSet(object.publishThumbnail) ? globalThis.Boolean(object.publishThumbnail) : false,
      thumbnailWidth: isSet(object.thumbnailWidth) ? globalThis.Number(object.thumbnailWidth) : 0,
      videoRecordingMode: isSet(object.videoRecordingMode) ? recordingMode_EnumFromJSON(object.videoRecordingMode) : 0,
      photoRecordingMode: isSet(object.photoRecordingMode) ? recordingMode_EnumFromJSON(object.photoRecordingMode) : 0,
      customFolder: isSet(object.customFolder) ? globalThis.String(object.customFolder) : "",
      useCustomFolder: isSet(object.useCustomFolder) ? globalThis.Boolean(object.useCustomFolder) : false,
      recordingFileFormat: isSet(object.recordingFileFormat)
        ? recordingFileFormat_EnumFromJSON(object.recordingFileFormat)
        : 0,
      eisHorizonLock: isSet(object.eisHorizonLock) ? globalThis.Boolean(object.eisHorizonLock) : false,
      eisSmoothness: isSet(object.eisSmoothness) ? globalThis.Number(object.eisSmoothness) : 0,
      eisMargin: isSet(object.eisMargin) ? globalThis.Number(object.eisMargin) : 0,
      skillsPhotoMetadata: isSet(object.skillsPhotoMetadata)
        ? SkillsPhotoMetadata.fromJSON(object.skillsPhotoMetadata)
        : undefined,
    };
  },

  toJSON(message: CaptureSettings): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.validationId !== 0) {
      obj.validationId = Math.round(message.validationId);
    }
    if (message.source !== 0) {
      obj.source = captureSettings_CaptureSettingsSourceToJSON(message.source);
    }
    if (message.recordingMode !== 0) {
      obj.recordingMode = recordingMode_EnumToJSON(message.recordingMode);
    }
    if (message.videoAutoRecord === true) {
      obj.videoAutoRecord = message.videoAutoRecord;
    }
    if (message.photoFormat !== 0) {
      obj.photoFormat = photoFormat_EnumToJSON(message.photoFormat);
    }
    if (message.photoInterval !== 0) {
      obj.photoInterval = photoIntervalMode_EnumToJSON(message.photoInterval);
    }
    if (message.iso !== 0) {
      obj.iso = isoMode_EnumToJSON(message.iso);
    }
    if (message.exposure !== 0) {
      obj.exposure = exposureMode_EnumToJSON(message.exposure);
    }
    if (message.compensation !== 0) {
      obj.compensation = compensationMode_EnumToJSON(message.compensation);
    }
    if (message.whiteBalance !== 0) {
      obj.whiteBalance = awbMode_EnumToJSON(message.whiteBalance);
    }
    if (message.encoderMode !== 0) {
      obj.encoderMode = encoderMode_EnumToJSON(message.encoderMode);
    }
    if (message.streamEncoderMode !== 0) {
      obj.streamEncoderMode = encoderMode_EnumToJSON(message.streamEncoderMode);
    }
    if (message.colorMode !== 0) {
      obj.colorMode = colorMode_EnumToJSON(message.colorMode);
    }
    if (message.userBitratePreset !== 0) {
      obj.userBitratePreset = recordingBitratePreset_EnumToJSON(message.userBitratePreset);
    }
    if (message.autofocusRegion !== undefined) {
      obj.autofocusRegion = AutoRegionRequest.toJSON(message.autofocusRegion);
    }
    if (message.autoexposureRegion !== undefined) {
      obj.autoexposureRegion = AutoRegionRequest.toJSON(message.autoexposureRegion);
    }
    if (message.autofocusMode !== 0) {
      obj.autofocusMode = autofocusMode_EnumToJSON(message.autofocusMode);
    }
    if (message.manualFocusPosition !== 0) {
      obj.manualFocusPosition = message.manualFocusPosition;
    }
    if (message.userBitrateOverride !== 0) {
      obj.userBitrateOverride = Math.round(message.userBitrateOverride);
    }
    if (message.antiBandingMode !== 0) {
      obj.antiBandingMode = antiBandingMode_EnumToJSON(message.antiBandingMode);
    }
    if (message.photo15mpEnabled === true) {
      obj.photo15mpEnabled = message.photo15mpEnabled;
    }
    if (message.publishThumbnail === true) {
      obj.publishThumbnail = message.publishThumbnail;
    }
    if (message.thumbnailWidth !== 0) {
      obj.thumbnailWidth = Math.round(message.thumbnailWidth);
    }
    if (message.videoRecordingMode !== 0) {
      obj.videoRecordingMode = recordingMode_EnumToJSON(message.videoRecordingMode);
    }
    if (message.photoRecordingMode !== 0) {
      obj.photoRecordingMode = recordingMode_EnumToJSON(message.photoRecordingMode);
    }
    if (message.customFolder !== "") {
      obj.customFolder = message.customFolder;
    }
    if (message.useCustomFolder === true) {
      obj.useCustomFolder = message.useCustomFolder;
    }
    if (message.recordingFileFormat !== 0) {
      obj.recordingFileFormat = recordingFileFormat_EnumToJSON(message.recordingFileFormat);
    }
    if (message.eisHorizonLock === true) {
      obj.eisHorizonLock = message.eisHorizonLock;
    }
    if (message.eisSmoothness !== 0) {
      obj.eisSmoothness = Math.round(message.eisSmoothness);
    }
    if (message.eisMargin !== 0) {
      obj.eisMargin = message.eisMargin;
    }
    if (message.skillsPhotoMetadata !== undefined) {
      obj.skillsPhotoMetadata = SkillsPhotoMetadata.toJSON(message.skillsPhotoMetadata);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<CaptureSettings>, I>>(base?: I): CaptureSettings {
    return CaptureSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<CaptureSettings>, I>>(object: I): CaptureSettings {
    const message = createBaseCaptureSettings();
    message.utime = object.utime ?? 0;
    message.validationId = object.validationId ?? 0;
    message.source = object.source ?? 0;
    message.recordingMode = object.recordingMode ?? 0;
    message.videoAutoRecord = object.videoAutoRecord ?? false;
    message.photoFormat = object.photoFormat ?? 0;
    message.photoInterval = object.photoInterval ?? 0;
    message.iso = object.iso ?? 0;
    message.exposure = object.exposure ?? 0;
    message.compensation = object.compensation ?? 0;
    message.whiteBalance = object.whiteBalance ?? 0;
    message.encoderMode = object.encoderMode ?? 0;
    message.streamEncoderMode = object.streamEncoderMode ?? 0;
    message.colorMode = object.colorMode ?? 0;
    message.userBitratePreset = object.userBitratePreset ?? 0;
    message.autofocusRegion = (object.autofocusRegion !== undefined && object.autofocusRegion !== null)
      ? AutoRegionRequest.fromPartial(object.autofocusRegion)
      : undefined;
    message.autoexposureRegion = (object.autoexposureRegion !== undefined && object.autoexposureRegion !== null)
      ? AutoRegionRequest.fromPartial(object.autoexposureRegion)
      : undefined;
    message.autofocusMode = object.autofocusMode ?? 0;
    message.manualFocusPosition = object.manualFocusPosition ?? 0;
    message.userBitrateOverride = object.userBitrateOverride ?? 0;
    message.antiBandingMode = object.antiBandingMode ?? 0;
    message.photo15mpEnabled = object.photo15mpEnabled ?? false;
    message.publishThumbnail = object.publishThumbnail ?? false;
    message.thumbnailWidth = object.thumbnailWidth ?? 0;
    message.videoRecordingMode = object.videoRecordingMode ?? 0;
    message.photoRecordingMode = object.photoRecordingMode ?? 0;
    message.customFolder = object.customFolder ?? "";
    message.useCustomFolder = object.useCustomFolder ?? false;
    message.recordingFileFormat = object.recordingFileFormat ?? 0;
    message.eisHorizonLock = object.eisHorizonLock ?? false;
    message.eisSmoothness = object.eisSmoothness ?? 0;
    message.eisMargin = object.eisMargin ?? 0;
    message.skillsPhotoMetadata = (object.skillsPhotoMetadata !== undefined && object.skillsPhotoMetadata !== null)
      ? SkillsPhotoMetadata.fromPartial(object.skillsPhotoMetadata)
      : undefined;
    return message;
  },
};

function createBaseIrCaptureSettings(): IrCaptureSettings {
  return {
    utime: 0,
    colorlut: 0,
    photoDisabled: false,
    photoFormat: 0,
    agc: undefined,
    exposureRoi: undefined,
    thermalParams: undefined,
    isotherm: undefined,
    boson: undefined,
  };
}

export const IrCaptureSettings = {
  encode(message: IrCaptureSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.colorlut !== 0) {
      writer.uint32(16).int32(message.colorlut);
    }
    if (message.photoDisabled === true) {
      writer.uint32(24).bool(message.photoDisabled);
    }
    if (message.photoFormat !== 0) {
      writer.uint32(32).int32(message.photoFormat);
    }
    if (message.agc !== undefined) {
      AgcSettings.encode(message.agc, writer.uint32(42).fork()).ldelim();
    }
    if (message.exposureRoi !== undefined) {
      RoiSettings.encode(message.exposureRoi, writer.uint32(50).fork()).ldelim();
    }
    if (message.thermalParams !== undefined) {
      ThermalParams.encode(message.thermalParams, writer.uint32(58).fork()).ldelim();
    }
    if (message.isotherm !== undefined) {
      IsothermSettings.encode(message.isotherm, writer.uint32(66).fork()).ldelim();
    }
    if (message.boson !== undefined) {
      BosonSettings.encode(message.boson, writer.uint32(74).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): IrCaptureSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseIrCaptureSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.colorlut = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.photoDisabled = reader.bool();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.photoFormat = reader.int32() as any;
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.agc = AgcSettings.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.exposureRoi = RoiSettings.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.thermalParams = ThermalParams.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.isotherm = IsothermSettings.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.boson = BosonSettings.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): IrCaptureSettings {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      colorlut: isSet(object.colorlut) ? colorlut_EnumFromJSON(object.colorlut) : 0,
      photoDisabled: isSet(object.photoDisabled) ? globalThis.Boolean(object.photoDisabled) : false,
      photoFormat: isSet(object.photoFormat) ? photoFormat_EnumFromJSON(object.photoFormat) : 0,
      agc: isSet(object.agc) ? AgcSettings.fromJSON(object.agc) : undefined,
      exposureRoi: isSet(object.exposureRoi) ? RoiSettings.fromJSON(object.exposureRoi) : undefined,
      thermalParams: isSet(object.thermalParams) ? ThermalParams.fromJSON(object.thermalParams) : undefined,
      isotherm: isSet(object.isotherm) ? IsothermSettings.fromJSON(object.isotherm) : undefined,
      boson: isSet(object.boson) ? BosonSettings.fromJSON(object.boson) : undefined,
    };
  },

  toJSON(message: IrCaptureSettings): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.colorlut !== 0) {
      obj.colorlut = colorlut_EnumToJSON(message.colorlut);
    }
    if (message.photoDisabled === true) {
      obj.photoDisabled = message.photoDisabled;
    }
    if (message.photoFormat !== 0) {
      obj.photoFormat = photoFormat_EnumToJSON(message.photoFormat);
    }
    if (message.agc !== undefined) {
      obj.agc = AgcSettings.toJSON(message.agc);
    }
    if (message.exposureRoi !== undefined) {
      obj.exposureRoi = RoiSettings.toJSON(message.exposureRoi);
    }
    if (message.thermalParams !== undefined) {
      obj.thermalParams = ThermalParams.toJSON(message.thermalParams);
    }
    if (message.isotherm !== undefined) {
      obj.isotherm = IsothermSettings.toJSON(message.isotherm);
    }
    if (message.boson !== undefined) {
      obj.boson = BosonSettings.toJSON(message.boson);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<IrCaptureSettings>, I>>(base?: I): IrCaptureSettings {
    return IrCaptureSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<IrCaptureSettings>, I>>(object: I): IrCaptureSettings {
    const message = createBaseIrCaptureSettings();
    message.utime = object.utime ?? 0;
    message.colorlut = object.colorlut ?? 0;
    message.photoDisabled = object.photoDisabled ?? false;
    message.photoFormat = object.photoFormat ?? 0;
    message.agc = (object.agc !== undefined && object.agc !== null) ? AgcSettings.fromPartial(object.agc) : undefined;
    message.exposureRoi = (object.exposureRoi !== undefined && object.exposureRoi !== null)
      ? RoiSettings.fromPartial(object.exposureRoi)
      : undefined;
    message.thermalParams = (object.thermalParams !== undefined && object.thermalParams !== null)
      ? ThermalParams.fromPartial(object.thermalParams)
      : undefined;
    message.isotherm = (object.isotherm !== undefined && object.isotherm !== null)
      ? IsothermSettings.fromPartial(object.isotherm)
      : undefined;
    message.boson = (object.boson !== undefined && object.boson !== null)
      ? BosonSettings.fromPartial(object.boson)
      : undefined;
    return message;
  },
};

function createBaseIrAuxillaryPersistedSettings(): IrAuxillaryPersistedSettings {
  return { spotMeterEnabled: false, userStatsRegionEnabled: false };
}

export const IrAuxillaryPersistedSettings = {
  encode(message: IrAuxillaryPersistedSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.spotMeterEnabled === true) {
      writer.uint32(8).bool(message.spotMeterEnabled);
    }
    if (message.userStatsRegionEnabled === true) {
      writer.uint32(16).bool(message.userStatsRegionEnabled);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): IrAuxillaryPersistedSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseIrAuxillaryPersistedSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.spotMeterEnabled = reader.bool();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.userStatsRegionEnabled = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): IrAuxillaryPersistedSettings {
    return {
      spotMeterEnabled: isSet(object.spotMeterEnabled) ? globalThis.Boolean(object.spotMeterEnabled) : false,
      userStatsRegionEnabled: isSet(object.userStatsRegionEnabled)
        ? globalThis.Boolean(object.userStatsRegionEnabled)
        : false,
    };
  },

  toJSON(message: IrAuxillaryPersistedSettings): unknown {
    const obj: any = {};
    if (message.spotMeterEnabled === true) {
      obj.spotMeterEnabled = message.spotMeterEnabled;
    }
    if (message.userStatsRegionEnabled === true) {
      obj.userStatsRegionEnabled = message.userStatsRegionEnabled;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<IrAuxillaryPersistedSettings>, I>>(base?: I): IrAuxillaryPersistedSettings {
    return IrAuxillaryPersistedSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<IrAuxillaryPersistedSettings>, I>>(object: I): IrAuxillaryPersistedSettings {
    const message = createBaseIrAuxillaryPersistedSettings();
    message.spotMeterEnabled = object.spotMeterEnabled ?? false;
    message.userStatsRegionEnabled = object.userStatsRegionEnabled ?? false;
    return message;
  },
};

function createBaseRemoteIDAuthSettings(): RemoteIDAuthSettings {
  return { registrationCode: "", datablock: "", writeStatus: false, modifiedDate: "", broadcastMethod: false };
}

export const RemoteIDAuthSettings = {
  encode(message: RemoteIDAuthSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.registrationCode !== "") {
      writer.uint32(10).string(message.registrationCode);
    }
    if (message.datablock !== "") {
      writer.uint32(18).string(message.datablock);
    }
    if (message.writeStatus === true) {
      writer.uint32(24).bool(message.writeStatus);
    }
    if (message.modifiedDate !== "") {
      writer.uint32(34).string(message.modifiedDate);
    }
    if (message.broadcastMethod === true) {
      writer.uint32(40).bool(message.broadcastMethod);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RemoteIDAuthSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRemoteIDAuthSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.registrationCode = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.datablock = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.writeStatus = reader.bool();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.modifiedDate = reader.string();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.broadcastMethod = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RemoteIDAuthSettings {
    return {
      registrationCode: isSet(object.registrationCode) ? globalThis.String(object.registrationCode) : "",
      datablock: isSet(object.datablock) ? globalThis.String(object.datablock) : "",
      writeStatus: isSet(object.writeStatus) ? globalThis.Boolean(object.writeStatus) : false,
      modifiedDate: isSet(object.modifiedDate) ? globalThis.String(object.modifiedDate) : "",
      broadcastMethod: isSet(object.broadcastMethod) ? globalThis.Boolean(object.broadcastMethod) : false,
    };
  },

  toJSON(message: RemoteIDAuthSettings): unknown {
    const obj: any = {};
    if (message.registrationCode !== "") {
      obj.registrationCode = message.registrationCode;
    }
    if (message.datablock !== "") {
      obj.datablock = message.datablock;
    }
    if (message.writeStatus === true) {
      obj.writeStatus = message.writeStatus;
    }
    if (message.modifiedDate !== "") {
      obj.modifiedDate = message.modifiedDate;
    }
    if (message.broadcastMethod === true) {
      obj.broadcastMethod = message.broadcastMethod;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RemoteIDAuthSettings>, I>>(base?: I): RemoteIDAuthSettings {
    return RemoteIDAuthSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RemoteIDAuthSettings>, I>>(object: I): RemoteIDAuthSettings {
    const message = createBaseRemoteIDAuthSettings();
    message.registrationCode = object.registrationCode ?? "";
    message.datablock = object.datablock ?? "";
    message.writeStatus = object.writeStatus ?? false;
    message.modifiedDate = object.modifiedDate ?? "";
    message.broadcastMethod = object.broadcastMethod ?? false;
    return message;
  },
};

function createBaseMavlinkClientSettings(): MavlinkClientSettings {
  return { geofenceRadius: 0, geofenceHeight: 0, geofenceAction: 0 };
}

export const MavlinkClientSettings = {
  encode(message: MavlinkClientSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.geofenceRadius !== 0) {
      writer.uint32(13).float(message.geofenceRadius);
    }
    if (message.geofenceHeight !== 0) {
      writer.uint32(21).float(message.geofenceHeight);
    }
    if (message.geofenceAction !== 0) {
      writer.uint32(24).int32(message.geofenceAction);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MavlinkClientSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMavlinkClientSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 13) {
            break;
          }

          message.geofenceRadius = reader.float();
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.geofenceHeight = reader.float();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.geofenceAction = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MavlinkClientSettings {
    return {
      geofenceRadius: isSet(object.geofenceRadius) ? globalThis.Number(object.geofenceRadius) : 0,
      geofenceHeight: isSet(object.geofenceHeight) ? globalThis.Number(object.geofenceHeight) : 0,
      geofenceAction: isSet(object.geofenceAction)
        ? mavlinkClientSettings_MavlinkGeofenceActionFromJSON(object.geofenceAction)
        : 0,
    };
  },

  toJSON(message: MavlinkClientSettings): unknown {
    const obj: any = {};
    if (message.geofenceRadius !== 0) {
      obj.geofenceRadius = message.geofenceRadius;
    }
    if (message.geofenceHeight !== 0) {
      obj.geofenceHeight = message.geofenceHeight;
    }
    if (message.geofenceAction !== 0) {
      obj.geofenceAction = mavlinkClientSettings_MavlinkGeofenceActionToJSON(message.geofenceAction);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MavlinkClientSettings>, I>>(base?: I): MavlinkClientSettings {
    return MavlinkClientSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MavlinkClientSettings>, I>>(object: I): MavlinkClientSettings {
    const message = createBaseMavlinkClientSettings();
    message.geofenceRadius = object.geofenceRadius ?? 0;
    message.geofenceHeight = object.geofenceHeight ?? 0;
    message.geofenceAction = object.geofenceAction ?? 0;
    return message;
  },
};

function createBaseSubjectSettings(): SubjectSettings {
  return {
    source: 0,
    autoFollow: false,
    fixedElevation: false,
    absoluteAzimuth: false,
    detectionOn: false,
    detectionMode: 0,
    visualSource: 0,
  };
}

export const SubjectSettings = {
  encode(message: SubjectSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.source !== 0) {
      writer.uint32(8).int32(message.source);
    }
    if (message.autoFollow === true) {
      writer.uint32(16).bool(message.autoFollow);
    }
    if (message.fixedElevation === true) {
      writer.uint32(24).bool(message.fixedElevation);
    }
    if (message.absoluteAzimuth === true) {
      writer.uint32(32).bool(message.absoluteAzimuth);
    }
    if (message.detectionOn === true) {
      writer.uint32(40).bool(message.detectionOn);
    }
    if (message.detectionMode !== 0) {
      writer.uint32(48).int32(message.detectionMode);
    }
    if (message.visualSource !== 0) {
      writer.uint32(56).int32(message.visualSource);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SubjectSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSubjectSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.source = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.autoFollow = reader.bool();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.fixedElevation = reader.bool();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.absoluteAzimuth = reader.bool();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.detectionOn = reader.bool();
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.detectionMode = reader.int32() as any;
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.visualSource = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SubjectSettings {
    return {
      source: isSet(object.source) ? subjectSettings_SubjectSettingsSourceFromJSON(object.source) : 0,
      autoFollow: isSet(object.autoFollow) ? globalThis.Boolean(object.autoFollow) : false,
      fixedElevation: isSet(object.fixedElevation) ? globalThis.Boolean(object.fixedElevation) : false,
      absoluteAzimuth: isSet(object.absoluteAzimuth) ? globalThis.Boolean(object.absoluteAzimuth) : false,
      detectionOn: isSet(object.detectionOn) ? globalThis.Boolean(object.detectionOn) : false,
      detectionMode: isSet(object.detectionMode) ? subjectDetectionMode_EnumFromJSON(object.detectionMode) : 0,
      visualSource: isSet(object.visualSource) ? visualSource_EnumFromJSON(object.visualSource) : 0,
    };
  },

  toJSON(message: SubjectSettings): unknown {
    const obj: any = {};
    if (message.source !== 0) {
      obj.source = subjectSettings_SubjectSettingsSourceToJSON(message.source);
    }
    if (message.autoFollow === true) {
      obj.autoFollow = message.autoFollow;
    }
    if (message.fixedElevation === true) {
      obj.fixedElevation = message.fixedElevation;
    }
    if (message.absoluteAzimuth === true) {
      obj.absoluteAzimuth = message.absoluteAzimuth;
    }
    if (message.detectionOn === true) {
      obj.detectionOn = message.detectionOn;
    }
    if (message.detectionMode !== 0) {
      obj.detectionMode = subjectDetectionMode_EnumToJSON(message.detectionMode);
    }
    if (message.visualSource !== 0) {
      obj.visualSource = visualSource_EnumToJSON(message.visualSource);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SubjectSettings>, I>>(base?: I): SubjectSettings {
    return SubjectSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SubjectSettings>, I>>(object: I): SubjectSettings {
    const message = createBaseSubjectSettings();
    message.source = object.source ?? 0;
    message.autoFollow = object.autoFollow ?? false;
    message.fixedElevation = object.fixedElevation ?? false;
    message.absoluteAzimuth = object.absoluteAzimuth ?? false;
    message.detectionOn = object.detectionOn ?? false;
    message.detectionMode = object.detectionMode ?? 0;
    message.visualSource = object.visualSource ?? 0;
    return message;
  },
};

function createBaseVehicleSettings(): VehicleSettings {
  return {
    utime: 0,
    validationId: 0,
    cloudConfigUclock: 0,
    heightCeilingOff: false,
    heightCeilingValue: 0,
    ignoreGpsHeightCeil: false,
    geofenceOff: false,
    geofence: undefined,
    illuminationMode: 0,
    obstacleMarginMode: 0,
    voxelMode: 0,
    caseLandingDisabled: false,
    returnWithVisionOnly: false,
    rgbLedsDisabled: false,
    superzoom: false,
    digitalZoom: false,
    returnSettings: undefined,
    precisionFlightMode: false,
    enterpriseMode: false,
    navigationMode: 0,
    wifiBand: 0,
    narrowBandEnabled: false,
    disableMediaEncryption: false,
    skydiolinkAes256: false,
    skysticksPairingInfo: undefined,
    ignoreLowBatteryBehavior: false,
    offline: false,
    rgbLedMode: 0,
    overwriteOldMedia: false,
    flightHistory: false,
    manualWifiChannel: 0,
    wifiChannelList: [],
    multiResPreviewEnable: false,
    centralizedFrbRateEnable: false,
    alignedTelemetryCsv: false,
    handWaveEnabled: false,
    displayHandWaveCalibration: false,
    variableObstacleAvoidance: false,
    rebootBeforeScheduledMissionsSeconds: 0,
    dockJigglingEnabled: false,
    allowTakeoffFromMismatchedDock: false,
    batteryButtonLaunch: false,
    rfHighPower: false,
    flightMode: 0,
    ridFeatureEnabled: false,
    allowOnVehiclePhotogrammetry: false,
    ridFeatureDisabled: false,
    allowSurfaceScanAlphaFeatures: false,
    rebootMaxUptimeSeconds: 0,
    battImbalanceThresholds: undefined,
    wifiCountryCode: "",
    stopAtStructure: false,
    speedNearObstacles: 0,
    surfaceScan: false,
    landingLedMode: 0,
    enableRapidDescent: false,
    disableObstacleAvoidanceDockLanding: false,
    longRangeDisabled: false,
    allowPoweredPayloads: false,
    disableCountryCodeScan: false,
    allow3dCaptureScanMode: false,
    allowSurfaceCaptureScanMode: false,
    allowMapCaptureScanMode: false,
    allowTowerCaptureScanMode: false,
    allowIndoorCaptureScanMode: false,
    allowIndoorCaptureScanModeV2: false,
    allowOrbitCaptureScanMode: false,
    allowCylinderCaptureScanMode: false,
    allowSemanticTransmissionTowerScanMode: false,
    fullSpinOnTakeoff: false,
    allowIrSubjectTracking: false,
    enableIspPixelContentFlip: false,
    enableVehicleRtspKlv: false,
    gimbalInspectionMode: false,
    nightsenseDisabled: false,
    boostDisablesNightsense: false,
    nightsenseOffAllowedWithPoorGps: false,
    nightsenseDisabledNightAutonomySwitch: false,
    flashDriveRequiredForFlight: false,
    manualBandSelection: 0,
    bandSelectionList: [],
    manualRadiowidthSelection: 0,
    radiowidthSelectionList: [],
    lteModemEnabled: false,
    allowCommandedEland: false,
    allowInternalMissionActions: false,
    autoLandInAttiMode: false,
    mhVehicleDcsCapable: false,
    increasedMaxSpeedMapCapture: false,
    manualWifiChannelOnRest: 0,
    allowMavlinkPerch: false,
    policeMode: false,
    lteDataSaver: false,
    blackoutModeEnabled: false,
    disableGps: false,
    manualFrameSelection: 0,
    frameSelectionList: [],
    enableHighAltitudeVio: false,
    enableAttitudeRtx: false,
    enableManualWff: false,
    oaOffGroundAvoidanceDisabled: false,
    enableJpgHighCompress3ds: false,
    enablePerch: false,
    manualModeSelection: 0,
    modeSelectionList: [],
    enableCommandedAttitude: false,
    publicSafetyPoliceMode: false,
    publicSafetyEmergencyMode: false,
    europeanUnionLighting: false,
    commandedAttitudeModeOn: false,
    enableGetMicrohardRfConfig: false,
    enableCommandedLostComms: false,
    mavlinkClientSettings: undefined,
    enableGpsSpoofingDetector: false,
    enableRscToggle3ds: false,
    enableLowTakeoff: false,
    enableRtkSupport: false,
    enableFlightFileManifestTask: false,
    isEuropeanUnion: false,
    enableEuSlowSpeed: false,
    enableForceVioDegraded: false,
    enableThirdPartyAttachmentPrototyping: false,
    mhManualChannelCapacityAllocation: 0,
    mhChannelCapacityAllocationList: [],
    enableForceLowTakeoffToggle: false,
    forceLowTakeoff: false,
    alwaysLogSubjectStreamForDockFlights: false,
    enableInternalAttachmentPrototyping: false,
    enableRtkNavigation: false,
    enableMpegtsFileWriting: false,
    mapCaptureTerrainFollowAlpha: false,
    maxBoost: false,
    enable20MpsMaxSpeed: false,
    enableThirdPartyRadioAttachment: false,
    ridOperatorId: "",
    persistDisableGps: false,
    enableSlowMode: false,
    enableParachuteExpectedButMissing: false,
    allowSubjectFollow: false,
    allowGpsFlightWithoutHandwave: false,
    pathfinderDisablesNightsense: false,
    enableMissionsCloudToController: false,
    enableVisibleNightsenseStrobing: false,
    enableIrNightsenseStrobing: false,
    ignoreBaseGeofences: false,
    ignoreAutonomousGeofences: false,
    ignoreWarningGeofences: false,
    disableFrameLock: false,
    enableEnhancedSlBitrate: false,
    forceFallbackToHandwave: false,
    enableThirdPartyAttachmentConfigs: false,
    enable20MpsMaxSpeedWaypointsOnly: false,
    enableDcsModeSelection: false,
    skipValidateCountrySettings: false,
    enableOnDemandMissionTier: false,
    enableIcingDetection: false,
    subjectSettings: undefined,
    enableFepPowerLimitNotification: false,
    enableBatteryLightDiscipline: false,
    enableSubjectFollowWithoutOa: false,
    heightCeilingReferenceFrame: 0,
    enableLsop: false,
    enable30MpsMaxGroundSpeed: false,
    enableMobileInspectionAssets: false,
    adsbRegulatoryManagerSettings: undefined,
    enableGeoBasedDtedIngest: false,
  };
}

export const VehicleSettings = {
  encode(message: VehicleSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.validationId !== 0) {
      writer.uint32(240).int32(message.validationId);
    }
    if (message.cloudConfigUclock !== 0) {
      writer.uint32(184).int64(message.cloudConfigUclock);
    }
    if (message.heightCeilingOff === true) {
      writer.uint32(24).bool(message.heightCeilingOff);
    }
    if (message.heightCeilingValue !== 0) {
      writer.uint32(125).float(message.heightCeilingValue);
    }
    if (message.ignoreGpsHeightCeil === true) {
      writer.uint32(216).bool(message.ignoreGpsHeightCeil);
    }
    if (message.geofenceOff === true) {
      writer.uint32(432).bool(message.geofenceOff);
    }
    if (message.geofence !== undefined) {
      Geofence.encode(message.geofence, writer.uint32(442).fork()).ldelim();
    }
    if (message.illuminationMode !== 0) {
      writer.uint32(32).int32(message.illuminationMode);
    }
    if (message.obstacleMarginMode !== 0) {
      writer.uint32(40).int32(message.obstacleMarginMode);
    }
    if (message.voxelMode !== 0) {
      writer.uint32(1232).int32(message.voxelMode);
    }
    if (message.caseLandingDisabled === true) {
      writer.uint32(48).bool(message.caseLandingDisabled);
    }
    if (message.returnWithVisionOnly === true) {
      writer.uint32(56).bool(message.returnWithVisionOnly);
    }
    if (message.rgbLedsDisabled === true) {
      writer.uint32(64).bool(message.rgbLedsDisabled);
    }
    if (message.superzoom === true) {
      writer.uint32(72).bool(message.superzoom);
    }
    if (message.digitalZoom === true) {
      writer.uint32(144).bool(message.digitalZoom);
    }
    if (message.returnSettings !== undefined) {
      ReturnSettings.encode(message.returnSettings, writer.uint32(82).fork()).ldelim();
    }
    if (message.precisionFlightMode === true) {
      writer.uint32(88).bool(message.precisionFlightMode);
    }
    if (message.enterpriseMode === true) {
      writer.uint32(96).bool(message.enterpriseMode);
    }
    if (message.navigationMode !== 0) {
      writer.uint32(104).int32(message.navigationMode);
    }
    if (message.wifiBand !== 0) {
      writer.uint32(112).int32(message.wifiBand);
    }
    if (message.narrowBandEnabled === true) {
      writer.uint32(176).bool(message.narrowBandEnabled);
    }
    if (message.disableMediaEncryption === true) {
      writer.uint32(128).bool(message.disableMediaEncryption);
    }
    if (message.skydiolinkAes256 === true) {
      writer.uint32(136).bool(message.skydiolinkAes256);
    }
    if (message.skysticksPairingInfo !== undefined) {
      SkysticksPairingInfo.encode(message.skysticksPairingInfo, writer.uint32(154).fork()).ldelim();
    }
    if (message.ignoreLowBatteryBehavior === true) {
      writer.uint32(160).bool(message.ignoreLowBatteryBehavior);
    }
    if (message.offline === true) {
      writer.uint32(168).bool(message.offline);
    }
    if (message.rgbLedMode !== 0) {
      writer.uint32(192).int32(message.rgbLedMode);
    }
    if (message.overwriteOldMedia === true) {
      writer.uint32(200).bool(message.overwriteOldMedia);
    }
    if (message.flightHistory === true) {
      writer.uint32(208).bool(message.flightHistory);
    }
    if (message.manualWifiChannel !== 0) {
      writer.uint32(224).int32(message.manualWifiChannel);
    }
    writer.uint32(234).fork();
    for (const v of message.wifiChannelList) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.multiResPreviewEnable === true) {
      writer.uint32(248).bool(message.multiResPreviewEnable);
    }
    if (message.centralizedFrbRateEnable === true) {
      writer.uint32(256).bool(message.centralizedFrbRateEnable);
    }
    if (message.alignedTelemetryCsv === true) {
      writer.uint32(272).bool(message.alignedTelemetryCsv);
    }
    if (message.handWaveEnabled === true) {
      writer.uint32(288).bool(message.handWaveEnabled);
    }
    if (message.displayHandWaveCalibration === true) {
      writer.uint32(296).bool(message.displayHandWaveCalibration);
    }
    if (message.variableObstacleAvoidance === true) {
      writer.uint32(304).bool(message.variableObstacleAvoidance);
    }
    if (message.rebootBeforeScheduledMissionsSeconds !== 0) {
      writer.uint32(312).int32(message.rebootBeforeScheduledMissionsSeconds);
    }
    if (message.dockJigglingEnabled === true) {
      writer.uint32(320).bool(message.dockJigglingEnabled);
    }
    if (message.allowTakeoffFromMismatchedDock === true) {
      writer.uint32(328).bool(message.allowTakeoffFromMismatchedDock);
    }
    if (message.batteryButtonLaunch === true) {
      writer.uint32(336).bool(message.batteryButtonLaunch);
    }
    if (message.rfHighPower === true) {
      writer.uint32(344).bool(message.rfHighPower);
    }
    if (message.flightMode !== 0) {
      writer.uint32(352).int32(message.flightMode);
    }
    if (message.ridFeatureEnabled === true) {
      writer.uint32(400).bool(message.ridFeatureEnabled);
    }
    if (message.allowOnVehiclePhotogrammetry === true) {
      writer.uint32(360).bool(message.allowOnVehiclePhotogrammetry);
    }
    if (message.ridFeatureDisabled === true) {
      writer.uint32(408).bool(message.ridFeatureDisabled);
    }
    if (message.allowSurfaceScanAlphaFeatures === true) {
      writer.uint32(416).bool(message.allowSurfaceScanAlphaFeatures);
    }
    if (message.rebootMaxUptimeSeconds !== 0) {
      writer.uint32(424).int32(message.rebootMaxUptimeSeconds);
    }
    if (message.battImbalanceThresholds !== undefined) {
      BatteryCellVoltageImbalanceThresholds.encode(message.battImbalanceThresholds, writer.uint32(450).fork()).ldelim();
    }
    if (message.wifiCountryCode !== "") {
      writer.uint32(458).string(message.wifiCountryCode);
    }
    if (message.stopAtStructure === true) {
      writer.uint32(464).bool(message.stopAtStructure);
    }
    if (message.speedNearObstacles !== 0) {
      writer.uint32(477).float(message.speedNearObstacles);
    }
    if (message.surfaceScan === true) {
      writer.uint32(480).bool(message.surfaceScan);
    }
    if (message.landingLedMode !== 0) {
      writer.uint32(488).int32(message.landingLedMode);
    }
    if (message.enableRapidDescent === true) {
      writer.uint32(504).bool(message.enableRapidDescent);
    }
    if (message.disableObstacleAvoidanceDockLanding === true) {
      writer.uint32(512).bool(message.disableObstacleAvoidanceDockLanding);
    }
    if (message.longRangeDisabled === true) {
      writer.uint32(520).bool(message.longRangeDisabled);
    }
    if (message.allowPoweredPayloads === true) {
      writer.uint32(528).bool(message.allowPoweredPayloads);
    }
    if (message.disableCountryCodeScan === true) {
      writer.uint32(544).bool(message.disableCountryCodeScan);
    }
    if (message.allow3dCaptureScanMode === true) {
      writer.uint32(552).bool(message.allow3dCaptureScanMode);
    }
    if (message.allowSurfaceCaptureScanMode === true) {
      writer.uint32(560).bool(message.allowSurfaceCaptureScanMode);
    }
    if (message.allowMapCaptureScanMode === true) {
      writer.uint32(568).bool(message.allowMapCaptureScanMode);
    }
    if (message.allowTowerCaptureScanMode === true) {
      writer.uint32(280).bool(message.allowTowerCaptureScanMode);
    }
    if (message.allowIndoorCaptureScanMode === true) {
      writer.uint32(264).bool(message.allowIndoorCaptureScanMode);
    }
    if (message.allowIndoorCaptureScanModeV2 === true) {
      writer.uint32(776).bool(message.allowIndoorCaptureScanModeV2);
    }
    if (message.allowOrbitCaptureScanMode === true) {
      writer.uint32(496).bool(message.allowOrbitCaptureScanMode);
    }
    if (message.allowCylinderCaptureScanMode === true) {
      writer.uint32(576).bool(message.allowCylinderCaptureScanMode);
    }
    if (message.allowSemanticTransmissionTowerScanMode === true) {
      writer.uint32(640).bool(message.allowSemanticTransmissionTowerScanMode);
    }
    if (message.fullSpinOnTakeoff === true) {
      writer.uint32(584).bool(message.fullSpinOnTakeoff);
    }
    if (message.allowIrSubjectTracking === true) {
      writer.uint32(592).bool(message.allowIrSubjectTracking);
    }
    if (message.enableIspPixelContentFlip === true) {
      writer.uint32(600).bool(message.enableIspPixelContentFlip);
    }
    if (message.enableVehicleRtspKlv === true) {
      writer.uint32(616).bool(message.enableVehicleRtspKlv);
    }
    if (message.gimbalInspectionMode === true) {
      writer.uint32(624).bool(message.gimbalInspectionMode);
    }
    if (message.nightsenseDisabled === true) {
      writer.uint32(632).bool(message.nightsenseDisabled);
    }
    if (message.boostDisablesNightsense === true) {
      writer.uint32(784).bool(message.boostDisablesNightsense);
    }
    if (message.nightsenseOffAllowedWithPoorGps === true) {
      writer.uint32(792).bool(message.nightsenseOffAllowedWithPoorGps);
    }
    if (message.nightsenseDisabledNightAutonomySwitch === true) {
      writer.uint32(800).bool(message.nightsenseDisabledNightAutonomySwitch);
    }
    if (message.flashDriveRequiredForFlight === true) {
      writer.uint32(648).bool(message.flashDriveRequiredForFlight);
    }
    if (message.manualBandSelection !== 0) {
      writer.uint32(656).int32(message.manualBandSelection);
    }
    writer.uint32(666).fork();
    for (const v of message.bandSelectionList) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.manualRadiowidthSelection !== 0) {
      writer.uint32(672).int32(message.manualRadiowidthSelection);
    }
    writer.uint32(682).fork();
    for (const v of message.radiowidthSelectionList) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.lteModemEnabled === true) {
      writer.uint32(688).bool(message.lteModemEnabled);
    }
    if (message.allowCommandedEland === true) {
      writer.uint32(696).bool(message.allowCommandedEland);
    }
    if (message.allowInternalMissionActions === true) {
      writer.uint32(704).bool(message.allowInternalMissionActions);
    }
    if (message.autoLandInAttiMode === true) {
      writer.uint32(712).bool(message.autoLandInAttiMode);
    }
    if (message.mhVehicleDcsCapable === true) {
      writer.uint32(720).bool(message.mhVehicleDcsCapable);
    }
    if (message.increasedMaxSpeedMapCapture === true) {
      writer.uint32(728).bool(message.increasedMaxSpeedMapCapture);
    }
    if (message.manualWifiChannelOnRest !== 0) {
      writer.uint32(736).int32(message.manualWifiChannelOnRest);
    }
    if (message.allowMavlinkPerch === true) {
      writer.uint32(744).bool(message.allowMavlinkPerch);
    }
    if (message.policeMode === true) {
      writer.uint32(752).bool(message.policeMode);
    }
    if (message.lteDataSaver === true) {
      writer.uint32(760).bool(message.lteDataSaver);
    }
    if (message.blackoutModeEnabled === true) {
      writer.uint32(768).bool(message.blackoutModeEnabled);
    }
    if (message.disableGps === true) {
      writer.uint32(808).bool(message.disableGps);
    }
    if (message.manualFrameSelection !== 0) {
      writer.uint32(824).int32(message.manualFrameSelection);
    }
    writer.uint32(834).fork();
    for (const v of message.frameSelectionList) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.enableHighAltitudeVio === true) {
      writer.uint32(840).bool(message.enableHighAltitudeVio);
    }
    if (message.enableAttitudeRtx === true) {
      writer.uint32(848).bool(message.enableAttitudeRtx);
    }
    if (message.enableManualWff === true) {
      writer.uint32(856).bool(message.enableManualWff);
    }
    if (message.oaOffGroundAvoidanceDisabled === true) {
      writer.uint32(864).bool(message.oaOffGroundAvoidanceDisabled);
    }
    if (message.enableJpgHighCompress3ds === true) {
      writer.uint32(872).bool(message.enableJpgHighCompress3ds);
    }
    if (message.enablePerch === true) {
      writer.uint32(880).bool(message.enablePerch);
    }
    if (message.manualModeSelection !== 0) {
      writer.uint32(888).int32(message.manualModeSelection);
    }
    writer.uint32(898).fork();
    for (const v of message.modeSelectionList) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.enableCommandedAttitude === true) {
      writer.uint32(904).bool(message.enableCommandedAttitude);
    }
    if (message.publicSafetyPoliceMode === true) {
      writer.uint32(912).bool(message.publicSafetyPoliceMode);
    }
    if (message.publicSafetyEmergencyMode === true) {
      writer.uint32(920).bool(message.publicSafetyEmergencyMode);
    }
    if (message.europeanUnionLighting === true) {
      writer.uint32(1016).bool(message.europeanUnionLighting);
    }
    if (message.commandedAttitudeModeOn === true) {
      writer.uint32(928).bool(message.commandedAttitudeModeOn);
    }
    if (message.enableGetMicrohardRfConfig === true) {
      writer.uint32(936).bool(message.enableGetMicrohardRfConfig);
    }
    if (message.enableCommandedLostComms === true) {
      writer.uint32(944).bool(message.enableCommandedLostComms);
    }
    if (message.mavlinkClientSettings !== undefined) {
      MavlinkClientSettings.encode(message.mavlinkClientSettings, writer.uint32(954).fork()).ldelim();
    }
    if (message.enableGpsSpoofingDetector === true) {
      writer.uint32(960).bool(message.enableGpsSpoofingDetector);
    }
    if (message.enableRscToggle3ds === true) {
      writer.uint32(968).bool(message.enableRscToggle3ds);
    }
    if (message.enableLowTakeoff === true) {
      writer.uint32(976).bool(message.enableLowTakeoff);
    }
    if (message.enableRtkSupport === true) {
      writer.uint32(984).bool(message.enableRtkSupport);
    }
    if (message.enableFlightFileManifestTask === true) {
      writer.uint32(992).bool(message.enableFlightFileManifestTask);
    }
    if (message.isEuropeanUnion === true) {
      writer.uint32(1000).bool(message.isEuropeanUnion);
    }
    if (message.enableEuSlowSpeed === true) {
      writer.uint32(1008).bool(message.enableEuSlowSpeed);
    }
    if (message.enableForceVioDegraded === true) {
      writer.uint32(1024).bool(message.enableForceVioDegraded);
    }
    if (message.enableThirdPartyAttachmentPrototyping === true) {
      writer.uint32(1032).bool(message.enableThirdPartyAttachmentPrototyping);
    }
    if (message.mhManualChannelCapacityAllocation !== 0) {
      writer.uint32(1104).int32(message.mhManualChannelCapacityAllocation);
    }
    writer.uint32(1114).fork();
    for (const v of message.mhChannelCapacityAllocationList) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.enableForceLowTakeoffToggle === true) {
      writer.uint32(1120).bool(message.enableForceLowTakeoffToggle);
    }
    if (message.forceLowTakeoff === true) {
      writer.uint32(1064).bool(message.forceLowTakeoff);
    }
    if (message.alwaysLogSubjectStreamForDockFlights === true) {
      writer.uint32(1072).bool(message.alwaysLogSubjectStreamForDockFlights);
    }
    if (message.enableInternalAttachmentPrototyping === true) {
      writer.uint32(1080).bool(message.enableInternalAttachmentPrototyping);
    }
    if (message.enableRtkNavigation === true) {
      writer.uint32(1088).bool(message.enableRtkNavigation);
    }
    if (message.enableMpegtsFileWriting === true) {
      writer.uint32(1096).bool(message.enableMpegtsFileWriting);
    }
    if (message.mapCaptureTerrainFollowAlpha === true) {
      writer.uint32(1128).bool(message.mapCaptureTerrainFollowAlpha);
    }
    if (message.maxBoost === true) {
      writer.uint32(1136).bool(message.maxBoost);
    }
    if (message.enable20MpsMaxSpeed === true) {
      writer.uint32(1152).bool(message.enable20MpsMaxSpeed);
    }
    if (message.enableThirdPartyRadioAttachment === true) {
      writer.uint32(1160).bool(message.enableThirdPartyRadioAttachment);
    }
    if (message.ridOperatorId !== "") {
      writer.uint32(1170).string(message.ridOperatorId);
    }
    if (message.persistDisableGps === true) {
      writer.uint32(1176).bool(message.persistDisableGps);
    }
    if (message.enableSlowMode === true) {
      writer.uint32(1184).bool(message.enableSlowMode);
    }
    if (message.enableParachuteExpectedButMissing === true) {
      writer.uint32(1192).bool(message.enableParachuteExpectedButMissing);
    }
    if (message.allowSubjectFollow === true) {
      writer.uint32(1200).bool(message.allowSubjectFollow);
    }
    if (message.allowGpsFlightWithoutHandwave === true) {
      writer.uint32(1208).bool(message.allowGpsFlightWithoutHandwave);
    }
    if (message.pathfinderDisablesNightsense === true) {
      writer.uint32(1216).bool(message.pathfinderDisablesNightsense);
    }
    if (message.enableMissionsCloudToController === true) {
      writer.uint32(1224).bool(message.enableMissionsCloudToController);
    }
    if (message.enableVisibleNightsenseStrobing === true) {
      writer.uint32(1240).bool(message.enableVisibleNightsenseStrobing);
    }
    if (message.enableIrNightsenseStrobing === true) {
      writer.uint32(1248).bool(message.enableIrNightsenseStrobing);
    }
    if (message.ignoreBaseGeofences === true) {
      writer.uint32(1256).bool(message.ignoreBaseGeofences);
    }
    if (message.ignoreAutonomousGeofences === true) {
      writer.uint32(1264).bool(message.ignoreAutonomousGeofences);
    }
    if (message.ignoreWarningGeofences === true) {
      writer.uint32(1272).bool(message.ignoreWarningGeofences);
    }
    if (message.disableFrameLock === true) {
      writer.uint32(1280).bool(message.disableFrameLock);
    }
    if (message.enableEnhancedSlBitrate === true) {
      writer.uint32(1288).bool(message.enableEnhancedSlBitrate);
    }
    if (message.forceFallbackToHandwave === true) {
      writer.uint32(1296).bool(message.forceFallbackToHandwave);
    }
    if (message.enableThirdPartyAttachmentConfigs === true) {
      writer.uint32(1304).bool(message.enableThirdPartyAttachmentConfigs);
    }
    if (message.enable20MpsMaxSpeedWaypointsOnly === true) {
      writer.uint32(1312).bool(message.enable20MpsMaxSpeedWaypointsOnly);
    }
    if (message.enableDcsModeSelection === true) {
      writer.uint32(1320).bool(message.enableDcsModeSelection);
    }
    if (message.skipValidateCountrySettings === true) {
      writer.uint32(1328).bool(message.skipValidateCountrySettings);
    }
    if (message.enableOnDemandMissionTier === true) {
      writer.uint32(1336).bool(message.enableOnDemandMissionTier);
    }
    if (message.enableIcingDetection === true) {
      writer.uint32(1344).bool(message.enableIcingDetection);
    }
    if (message.subjectSettings !== undefined) {
      SubjectSettings.encode(message.subjectSettings, writer.uint32(1354).fork()).ldelim();
    }
    if (message.enableFepPowerLimitNotification === true) {
      writer.uint32(1360).bool(message.enableFepPowerLimitNotification);
    }
    if (message.enableBatteryLightDiscipline === true) {
      writer.uint32(1376).bool(message.enableBatteryLightDiscipline);
    }
    if (message.enableSubjectFollowWithoutOa === true) {
      writer.uint32(1384).bool(message.enableSubjectFollowWithoutOa);
    }
    if (message.heightCeilingReferenceFrame !== 0) {
      writer.uint32(1392).int32(message.heightCeilingReferenceFrame);
    }
    if (message.enableLsop === true) {
      writer.uint32(1400).bool(message.enableLsop);
    }
    if (message.enable30MpsMaxGroundSpeed === true) {
      writer.uint32(1408).bool(message.enable30MpsMaxGroundSpeed);
    }
    if (message.enableMobileInspectionAssets === true) {
      writer.uint32(1416).bool(message.enableMobileInspectionAssets);
    }
    if (message.adsbRegulatoryManagerSettings !== undefined) {
      AdsbRegulatoryManagerSettings.encode(message.adsbRegulatoryManagerSettings, writer.uint32(1426).fork()).ldelim();
    }
    if (message.enableGeoBasedDtedIngest === true) {
      writer.uint32(1432).bool(message.enableGeoBasedDtedIngest);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): VehicleSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseVehicleSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 30:
          if (tag !== 240) {
            break;
          }

          message.validationId = reader.int32();
          continue;
        case 23:
          if (tag !== 184) {
            break;
          }

          message.cloudConfigUclock = longToNumber(reader.int64() as Long);
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.heightCeilingOff = reader.bool();
          continue;
        case 15:
          if (tag !== 125) {
            break;
          }

          message.heightCeilingValue = reader.float();
          continue;
        case 27:
          if (tag !== 216) {
            break;
          }

          message.ignoreGpsHeightCeil = reader.bool();
          continue;
        case 54:
          if (tag !== 432) {
            break;
          }

          message.geofenceOff = reader.bool();
          continue;
        case 55:
          if (tag !== 442) {
            break;
          }

          message.geofence = Geofence.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.illuminationMode = reader.int32() as any;
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.obstacleMarginMode = reader.int32() as any;
          continue;
        case 154:
          if (tag !== 1232) {
            break;
          }

          message.voxelMode = reader.int32() as any;
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.caseLandingDisabled = reader.bool();
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.returnWithVisionOnly = reader.bool();
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.rgbLedsDisabled = reader.bool();
          continue;
        case 9:
          if (tag !== 72) {
            break;
          }

          message.superzoom = reader.bool();
          continue;
        case 18:
          if (tag !== 144) {
            break;
          }

          message.digitalZoom = reader.bool();
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.returnSettings = ReturnSettings.decode(reader, reader.uint32());
          continue;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.precisionFlightMode = reader.bool();
          continue;
        case 12:
          if (tag !== 96) {
            break;
          }

          message.enterpriseMode = reader.bool();
          continue;
        case 13:
          if (tag !== 104) {
            break;
          }

          message.navigationMode = reader.int32() as any;
          continue;
        case 14:
          if (tag !== 112) {
            break;
          }

          message.wifiBand = reader.int32() as any;
          continue;
        case 22:
          if (tag !== 176) {
            break;
          }

          message.narrowBandEnabled = reader.bool();
          continue;
        case 16:
          if (tag !== 128) {
            break;
          }

          message.disableMediaEncryption = reader.bool();
          continue;
        case 17:
          if (tag !== 136) {
            break;
          }

          message.skydiolinkAes256 = reader.bool();
          continue;
        case 19:
          if (tag !== 154) {
            break;
          }

          message.skysticksPairingInfo = SkysticksPairingInfo.decode(reader, reader.uint32());
          continue;
        case 20:
          if (tag !== 160) {
            break;
          }

          message.ignoreLowBatteryBehavior = reader.bool();
          continue;
        case 21:
          if (tag !== 168) {
            break;
          }

          message.offline = reader.bool();
          continue;
        case 24:
          if (tag !== 192) {
            break;
          }

          message.rgbLedMode = reader.int32() as any;
          continue;
        case 25:
          if (tag !== 200) {
            break;
          }

          message.overwriteOldMedia = reader.bool();
          continue;
        case 26:
          if (tag !== 208) {
            break;
          }

          message.flightHistory = reader.bool();
          continue;
        case 28:
          if (tag !== 224) {
            break;
          }

          message.manualWifiChannel = reader.int32();
          continue;
        case 29:
          if (tag === 232) {
            message.wifiChannelList.push(reader.int32());

            continue;
          }

          if (tag === 234) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.wifiChannelList.push(reader.int32());
            }

            continue;
          }

          break;
        case 31:
          if (tag !== 248) {
            break;
          }

          message.multiResPreviewEnable = reader.bool();
          continue;
        case 32:
          if (tag !== 256) {
            break;
          }

          message.centralizedFrbRateEnable = reader.bool();
          continue;
        case 34:
          if (tag !== 272) {
            break;
          }

          message.alignedTelemetryCsv = reader.bool();
          continue;
        case 36:
          if (tag !== 288) {
            break;
          }

          message.handWaveEnabled = reader.bool();
          continue;
        case 37:
          if (tag !== 296) {
            break;
          }

          message.displayHandWaveCalibration = reader.bool();
          continue;
        case 38:
          if (tag !== 304) {
            break;
          }

          message.variableObstacleAvoidance = reader.bool();
          continue;
        case 39:
          if (tag !== 312) {
            break;
          }

          message.rebootBeforeScheduledMissionsSeconds = reader.int32();
          continue;
        case 40:
          if (tag !== 320) {
            break;
          }

          message.dockJigglingEnabled = reader.bool();
          continue;
        case 41:
          if (tag !== 328) {
            break;
          }

          message.allowTakeoffFromMismatchedDock = reader.bool();
          continue;
        case 42:
          if (tag !== 336) {
            break;
          }

          message.batteryButtonLaunch = reader.bool();
          continue;
        case 43:
          if (tag !== 344) {
            break;
          }

          message.rfHighPower = reader.bool();
          continue;
        case 44:
          if (tag !== 352) {
            break;
          }

          message.flightMode = reader.int32() as any;
          continue;
        case 50:
          if (tag !== 400) {
            break;
          }

          message.ridFeatureEnabled = reader.bool();
          continue;
        case 45:
          if (tag !== 360) {
            break;
          }

          message.allowOnVehiclePhotogrammetry = reader.bool();
          continue;
        case 51:
          if (tag !== 408) {
            break;
          }

          message.ridFeatureDisabled = reader.bool();
          continue;
        case 52:
          if (tag !== 416) {
            break;
          }

          message.allowSurfaceScanAlphaFeatures = reader.bool();
          continue;
        case 53:
          if (tag !== 424) {
            break;
          }

          message.rebootMaxUptimeSeconds = reader.int32();
          continue;
        case 56:
          if (tag !== 450) {
            break;
          }

          message.battImbalanceThresholds = BatteryCellVoltageImbalanceThresholds.decode(reader, reader.uint32());
          continue;
        case 57:
          if (tag !== 458) {
            break;
          }

          message.wifiCountryCode = reader.string();
          continue;
        case 58:
          if (tag !== 464) {
            break;
          }

          message.stopAtStructure = reader.bool();
          continue;
        case 59:
          if (tag !== 477) {
            break;
          }

          message.speedNearObstacles = reader.float();
          continue;
        case 60:
          if (tag !== 480) {
            break;
          }

          message.surfaceScan = reader.bool();
          continue;
        case 61:
          if (tag !== 488) {
            break;
          }

          message.landingLedMode = reader.int32() as any;
          continue;
        case 63:
          if (tag !== 504) {
            break;
          }

          message.enableRapidDescent = reader.bool();
          continue;
        case 64:
          if (tag !== 512) {
            break;
          }

          message.disableObstacleAvoidanceDockLanding = reader.bool();
          continue;
        case 65:
          if (tag !== 520) {
            break;
          }

          message.longRangeDisabled = reader.bool();
          continue;
        case 66:
          if (tag !== 528) {
            break;
          }

          message.allowPoweredPayloads = reader.bool();
          continue;
        case 68:
          if (tag !== 544) {
            break;
          }

          message.disableCountryCodeScan = reader.bool();
          continue;
        case 69:
          if (tag !== 552) {
            break;
          }

          message.allow3dCaptureScanMode = reader.bool();
          continue;
        case 70:
          if (tag !== 560) {
            break;
          }

          message.allowSurfaceCaptureScanMode = reader.bool();
          continue;
        case 71:
          if (tag !== 568) {
            break;
          }

          message.allowMapCaptureScanMode = reader.bool();
          continue;
        case 35:
          if (tag !== 280) {
            break;
          }

          message.allowTowerCaptureScanMode = reader.bool();
          continue;
        case 33:
          if (tag !== 264) {
            break;
          }

          message.allowIndoorCaptureScanMode = reader.bool();
          continue;
        case 97:
          if (tag !== 776) {
            break;
          }

          message.allowIndoorCaptureScanModeV2 = reader.bool();
          continue;
        case 62:
          if (tag !== 496) {
            break;
          }

          message.allowOrbitCaptureScanMode = reader.bool();
          continue;
        case 72:
          if (tag !== 576) {
            break;
          }

          message.allowCylinderCaptureScanMode = reader.bool();
          continue;
        case 80:
          if (tag !== 640) {
            break;
          }

          message.allowSemanticTransmissionTowerScanMode = reader.bool();
          continue;
        case 73:
          if (tag !== 584) {
            break;
          }

          message.fullSpinOnTakeoff = reader.bool();
          continue;
        case 74:
          if (tag !== 592) {
            break;
          }

          message.allowIrSubjectTracking = reader.bool();
          continue;
        case 75:
          if (tag !== 600) {
            break;
          }

          message.enableIspPixelContentFlip = reader.bool();
          continue;
        case 77:
          if (tag !== 616) {
            break;
          }

          message.enableVehicleRtspKlv = reader.bool();
          continue;
        case 78:
          if (tag !== 624) {
            break;
          }

          message.gimbalInspectionMode = reader.bool();
          continue;
        case 79:
          if (tag !== 632) {
            break;
          }

          message.nightsenseDisabled = reader.bool();
          continue;
        case 98:
          if (tag !== 784) {
            break;
          }

          message.boostDisablesNightsense = reader.bool();
          continue;
        case 99:
          if (tag !== 792) {
            break;
          }

          message.nightsenseOffAllowedWithPoorGps = reader.bool();
          continue;
        case 100:
          if (tag !== 800) {
            break;
          }

          message.nightsenseDisabledNightAutonomySwitch = reader.bool();
          continue;
        case 81:
          if (tag !== 648) {
            break;
          }

          message.flashDriveRequiredForFlight = reader.bool();
          continue;
        case 82:
          if (tag !== 656) {
            break;
          }

          message.manualBandSelection = reader.int32();
          continue;
        case 83:
          if (tag === 664) {
            message.bandSelectionList.push(reader.int32());

            continue;
          }

          if (tag === 666) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.bandSelectionList.push(reader.int32());
            }

            continue;
          }

          break;
        case 84:
          if (tag !== 672) {
            break;
          }

          message.manualRadiowidthSelection = reader.int32();
          continue;
        case 85:
          if (tag === 680) {
            message.radiowidthSelectionList.push(reader.int32());

            continue;
          }

          if (tag === 682) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.radiowidthSelectionList.push(reader.int32());
            }

            continue;
          }

          break;
        case 86:
          if (tag !== 688) {
            break;
          }

          message.lteModemEnabled = reader.bool();
          continue;
        case 87:
          if (tag !== 696) {
            break;
          }

          message.allowCommandedEland = reader.bool();
          continue;
        case 88:
          if (tag !== 704) {
            break;
          }

          message.allowInternalMissionActions = reader.bool();
          continue;
        case 89:
          if (tag !== 712) {
            break;
          }

          message.autoLandInAttiMode = reader.bool();
          continue;
        case 90:
          if (tag !== 720) {
            break;
          }

          message.mhVehicleDcsCapable = reader.bool();
          continue;
        case 91:
          if (tag !== 728) {
            break;
          }

          message.increasedMaxSpeedMapCapture = reader.bool();
          continue;
        case 92:
          if (tag !== 736) {
            break;
          }

          message.manualWifiChannelOnRest = reader.int32();
          continue;
        case 93:
          if (tag !== 744) {
            break;
          }

          message.allowMavlinkPerch = reader.bool();
          continue;
        case 94:
          if (tag !== 752) {
            break;
          }

          message.policeMode = reader.bool();
          continue;
        case 95:
          if (tag !== 760) {
            break;
          }

          message.lteDataSaver = reader.bool();
          continue;
        case 96:
          if (tag !== 768) {
            break;
          }

          message.blackoutModeEnabled = reader.bool();
          continue;
        case 101:
          if (tag !== 808) {
            break;
          }

          message.disableGps = reader.bool();
          continue;
        case 103:
          if (tag !== 824) {
            break;
          }

          message.manualFrameSelection = reader.int32();
          continue;
        case 104:
          if (tag === 832) {
            message.frameSelectionList.push(reader.int32());

            continue;
          }

          if (tag === 834) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.frameSelectionList.push(reader.int32());
            }

            continue;
          }

          break;
        case 105:
          if (tag !== 840) {
            break;
          }

          message.enableHighAltitudeVio = reader.bool();
          continue;
        case 106:
          if (tag !== 848) {
            break;
          }

          message.enableAttitudeRtx = reader.bool();
          continue;
        case 107:
          if (tag !== 856) {
            break;
          }

          message.enableManualWff = reader.bool();
          continue;
        case 108:
          if (tag !== 864) {
            break;
          }

          message.oaOffGroundAvoidanceDisabled = reader.bool();
          continue;
        case 109:
          if (tag !== 872) {
            break;
          }

          message.enableJpgHighCompress3ds = reader.bool();
          continue;
        case 110:
          if (tag !== 880) {
            break;
          }

          message.enablePerch = reader.bool();
          continue;
        case 111:
          if (tag !== 888) {
            break;
          }

          message.manualModeSelection = reader.int32();
          continue;
        case 112:
          if (tag === 896) {
            message.modeSelectionList.push(reader.int32());

            continue;
          }

          if (tag === 898) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.modeSelectionList.push(reader.int32());
            }

            continue;
          }

          break;
        case 113:
          if (tag !== 904) {
            break;
          }

          message.enableCommandedAttitude = reader.bool();
          continue;
        case 114:
          if (tag !== 912) {
            break;
          }

          message.publicSafetyPoliceMode = reader.bool();
          continue;
        case 115:
          if (tag !== 920) {
            break;
          }

          message.publicSafetyEmergencyMode = reader.bool();
          continue;
        case 127:
          if (tag !== 1016) {
            break;
          }

          message.europeanUnionLighting = reader.bool();
          continue;
        case 116:
          if (tag !== 928) {
            break;
          }

          message.commandedAttitudeModeOn = reader.bool();
          continue;
        case 117:
          if (tag !== 936) {
            break;
          }

          message.enableGetMicrohardRfConfig = reader.bool();
          continue;
        case 118:
          if (tag !== 944) {
            break;
          }

          message.enableCommandedLostComms = reader.bool();
          continue;
        case 119:
          if (tag !== 954) {
            break;
          }

          message.mavlinkClientSettings = MavlinkClientSettings.decode(reader, reader.uint32());
          continue;
        case 120:
          if (tag !== 960) {
            break;
          }

          message.enableGpsSpoofingDetector = reader.bool();
          continue;
        case 121:
          if (tag !== 968) {
            break;
          }

          message.enableRscToggle3ds = reader.bool();
          continue;
        case 122:
          if (tag !== 976) {
            break;
          }

          message.enableLowTakeoff = reader.bool();
          continue;
        case 123:
          if (tag !== 984) {
            break;
          }

          message.enableRtkSupport = reader.bool();
          continue;
        case 124:
          if (tag !== 992) {
            break;
          }

          message.enableFlightFileManifestTask = reader.bool();
          continue;
        case 125:
          if (tag !== 1000) {
            break;
          }

          message.isEuropeanUnion = reader.bool();
          continue;
        case 126:
          if (tag !== 1008) {
            break;
          }

          message.enableEuSlowSpeed = reader.bool();
          continue;
        case 128:
          if (tag !== 1024) {
            break;
          }

          message.enableForceVioDegraded = reader.bool();
          continue;
        case 129:
          if (tag !== 1032) {
            break;
          }

          message.enableThirdPartyAttachmentPrototyping = reader.bool();
          continue;
        case 138:
          if (tag !== 1104) {
            break;
          }

          message.mhManualChannelCapacityAllocation = reader.int32();
          continue;
        case 139:
          if (tag === 1112) {
            message.mhChannelCapacityAllocationList.push(reader.int32());

            continue;
          }

          if (tag === 1114) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.mhChannelCapacityAllocationList.push(reader.int32());
            }

            continue;
          }

          break;
        case 140:
          if (tag !== 1120) {
            break;
          }

          message.enableForceLowTakeoffToggle = reader.bool();
          continue;
        case 133:
          if (tag !== 1064) {
            break;
          }

          message.forceLowTakeoff = reader.bool();
          continue;
        case 134:
          if (tag !== 1072) {
            break;
          }

          message.alwaysLogSubjectStreamForDockFlights = reader.bool();
          continue;
        case 135:
          if (tag !== 1080) {
            break;
          }

          message.enableInternalAttachmentPrototyping = reader.bool();
          continue;
        case 136:
          if (tag !== 1088) {
            break;
          }

          message.enableRtkNavigation = reader.bool();
          continue;
        case 137:
          if (tag !== 1096) {
            break;
          }

          message.enableMpegtsFileWriting = reader.bool();
          continue;
        case 141:
          if (tag !== 1128) {
            break;
          }

          message.mapCaptureTerrainFollowAlpha = reader.bool();
          continue;
        case 142:
          if (tag !== 1136) {
            break;
          }

          message.maxBoost = reader.bool();
          continue;
        case 144:
          if (tag !== 1152) {
            break;
          }

          message.enable20MpsMaxSpeed = reader.bool();
          continue;
        case 145:
          if (tag !== 1160) {
            break;
          }

          message.enableThirdPartyRadioAttachment = reader.bool();
          continue;
        case 146:
          if (tag !== 1170) {
            break;
          }

          message.ridOperatorId = reader.string();
          continue;
        case 147:
          if (tag !== 1176) {
            break;
          }

          message.persistDisableGps = reader.bool();
          continue;
        case 148:
          if (tag !== 1184) {
            break;
          }

          message.enableSlowMode = reader.bool();
          continue;
        case 149:
          if (tag !== 1192) {
            break;
          }

          message.enableParachuteExpectedButMissing = reader.bool();
          continue;
        case 150:
          if (tag !== 1200) {
            break;
          }

          message.allowSubjectFollow = reader.bool();
          continue;
        case 151:
          if (tag !== 1208) {
            break;
          }

          message.allowGpsFlightWithoutHandwave = reader.bool();
          continue;
        case 152:
          if (tag !== 1216) {
            break;
          }

          message.pathfinderDisablesNightsense = reader.bool();
          continue;
        case 153:
          if (tag !== 1224) {
            break;
          }

          message.enableMissionsCloudToController = reader.bool();
          continue;
        case 155:
          if (tag !== 1240) {
            break;
          }

          message.enableVisibleNightsenseStrobing = reader.bool();
          continue;
        case 156:
          if (tag !== 1248) {
            break;
          }

          message.enableIrNightsenseStrobing = reader.bool();
          continue;
        case 157:
          if (tag !== 1256) {
            break;
          }

          message.ignoreBaseGeofences = reader.bool();
          continue;
        case 158:
          if (tag !== 1264) {
            break;
          }

          message.ignoreAutonomousGeofences = reader.bool();
          continue;
        case 159:
          if (tag !== 1272) {
            break;
          }

          message.ignoreWarningGeofences = reader.bool();
          continue;
        case 160:
          if (tag !== 1280) {
            break;
          }

          message.disableFrameLock = reader.bool();
          continue;
        case 161:
          if (tag !== 1288) {
            break;
          }

          message.enableEnhancedSlBitrate = reader.bool();
          continue;
        case 162:
          if (tag !== 1296) {
            break;
          }

          message.forceFallbackToHandwave = reader.bool();
          continue;
        case 163:
          if (tag !== 1304) {
            break;
          }

          message.enableThirdPartyAttachmentConfigs = reader.bool();
          continue;
        case 164:
          if (tag !== 1312) {
            break;
          }

          message.enable20MpsMaxSpeedWaypointsOnly = reader.bool();
          continue;
        case 165:
          if (tag !== 1320) {
            break;
          }

          message.enableDcsModeSelection = reader.bool();
          continue;
        case 166:
          if (tag !== 1328) {
            break;
          }

          message.skipValidateCountrySettings = reader.bool();
          continue;
        case 167:
          if (tag !== 1336) {
            break;
          }

          message.enableOnDemandMissionTier = reader.bool();
          continue;
        case 168:
          if (tag !== 1344) {
            break;
          }

          message.enableIcingDetection = reader.bool();
          continue;
        case 169:
          if (tag !== 1354) {
            break;
          }

          message.subjectSettings = SubjectSettings.decode(reader, reader.uint32());
          continue;
        case 170:
          if (tag !== 1360) {
            break;
          }

          message.enableFepPowerLimitNotification = reader.bool();
          continue;
        case 172:
          if (tag !== 1376) {
            break;
          }

          message.enableBatteryLightDiscipline = reader.bool();
          continue;
        case 173:
          if (tag !== 1384) {
            break;
          }

          message.enableSubjectFollowWithoutOa = reader.bool();
          continue;
        case 174:
          if (tag !== 1392) {
            break;
          }

          message.heightCeilingReferenceFrame = reader.int32() as any;
          continue;
        case 175:
          if (tag !== 1400) {
            break;
          }

          message.enableLsop = reader.bool();
          continue;
        case 176:
          if (tag !== 1408) {
            break;
          }

          message.enable30MpsMaxGroundSpeed = reader.bool();
          continue;
        case 177:
          if (tag !== 1416) {
            break;
          }

          message.enableMobileInspectionAssets = reader.bool();
          continue;
        case 178:
          if (tag !== 1426) {
            break;
          }

          message.adsbRegulatoryManagerSettings = AdsbRegulatoryManagerSettings.decode(reader, reader.uint32());
          continue;
        case 179:
          if (tag !== 1432) {
            break;
          }

          message.enableGeoBasedDtedIngest = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): VehicleSettings {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      validationId: isSet(object.validationId) ? globalThis.Number(object.validationId) : 0,
      cloudConfigUclock: isSet(object.cloudConfigUclock) ? globalThis.Number(object.cloudConfigUclock) : 0,
      heightCeilingOff: isSet(object.heightCeilingOff) ? globalThis.Boolean(object.heightCeilingOff) : false,
      heightCeilingValue: isSet(object.heightCeilingValue) ? globalThis.Number(object.heightCeilingValue) : 0,
      ignoreGpsHeightCeil: isSet(object.ignoreGpsHeightCeil) ? globalThis.Boolean(object.ignoreGpsHeightCeil) : false,
      geofenceOff: isSet(object.geofenceOff) ? globalThis.Boolean(object.geofenceOff) : false,
      geofence: isSet(object.geofence) ? Geofence.fromJSON(object.geofence) : undefined,
      illuminationMode: isSet(object.illuminationMode)
        ? vehicleSettings_IlluminationModeFromJSON(object.illuminationMode)
        : 0,
      obstacleMarginMode: isSet(object.obstacleMarginMode)
        ? vehicleSettings_ObstacleMarginModeFromJSON(object.obstacleMarginMode)
        : 0,
      voxelMode: isSet(object.voxelMode) ? vehicleSettings_VoxelModeFromJSON(object.voxelMode) : 0,
      caseLandingDisabled: isSet(object.caseLandingDisabled) ? globalThis.Boolean(object.caseLandingDisabled) : false,
      returnWithVisionOnly: isSet(object.returnWithVisionOnly)
        ? globalThis.Boolean(object.returnWithVisionOnly)
        : false,
      rgbLedsDisabled: isSet(object.rgbLedsDisabled) ? globalThis.Boolean(object.rgbLedsDisabled) : false,
      superzoom: isSet(object.superzoom) ? globalThis.Boolean(object.superzoom) : false,
      digitalZoom: isSet(object.digitalZoom) ? globalThis.Boolean(object.digitalZoom) : false,
      returnSettings: isSet(object.returnSettings) ? ReturnSettings.fromJSON(object.returnSettings) : undefined,
      precisionFlightMode: isSet(object.precisionFlightMode) ? globalThis.Boolean(object.precisionFlightMode) : false,
      enterpriseMode: isSet(object.enterpriseMode) ? globalThis.Boolean(object.enterpriseMode) : false,
      navigationMode: isSet(object.navigationMode) ? navigationMode_ModeFromJSON(object.navigationMode) : 0,
      wifiBand: isSet(object.wifiBand) ? vehicleSettings_WifiBandFromJSON(object.wifiBand) : 0,
      narrowBandEnabled: isSet(object.narrowBandEnabled) ? globalThis.Boolean(object.narrowBandEnabled) : false,
      disableMediaEncryption: isSet(object.disableMediaEncryption)
        ? globalThis.Boolean(object.disableMediaEncryption)
        : false,
      skydiolinkAes256: isSet(object.skydiolinkAes256) ? globalThis.Boolean(object.skydiolinkAes256) : false,
      skysticksPairingInfo: isSet(object.skysticksPairingInfo)
        ? SkysticksPairingInfo.fromJSON(object.skysticksPairingInfo)
        : undefined,
      ignoreLowBatteryBehavior: isSet(object.ignoreLowBatteryBehavior)
        ? globalThis.Boolean(object.ignoreLowBatteryBehavior)
        : false,
      offline: isSet(object.offline) ? globalThis.Boolean(object.offline) : false,
      rgbLedMode: isSet(object.rgbLedMode) ? rgbLedMode_EnumFromJSON(object.rgbLedMode) : 0,
      overwriteOldMedia: isSet(object.overwriteOldMedia) ? globalThis.Boolean(object.overwriteOldMedia) : false,
      flightHistory: isSet(object.flightHistory) ? globalThis.Boolean(object.flightHistory) : false,
      manualWifiChannel: isSet(object.manualWifiChannel) ? globalThis.Number(object.manualWifiChannel) : 0,
      wifiChannelList: globalThis.Array.isArray(object?.wifiChannelList)
        ? object.wifiChannelList.map((e: any) => globalThis.Number(e))
        : [],
      multiResPreviewEnable: isSet(object.multiResPreviewEnable)
        ? globalThis.Boolean(object.multiResPreviewEnable)
        : false,
      centralizedFrbRateEnable: isSet(object.centralizedFrbRateEnable)
        ? globalThis.Boolean(object.centralizedFrbRateEnable)
        : false,
      alignedTelemetryCsv: isSet(object.alignedTelemetryCsv) ? globalThis.Boolean(object.alignedTelemetryCsv) : false,
      handWaveEnabled: isSet(object.handWaveEnabled) ? globalThis.Boolean(object.handWaveEnabled) : false,
      displayHandWaveCalibration: isSet(object.displayHandWaveCalibration)
        ? globalThis.Boolean(object.displayHandWaveCalibration)
        : false,
      variableObstacleAvoidance: isSet(object.variableObstacleAvoidance)
        ? globalThis.Boolean(object.variableObstacleAvoidance)
        : false,
      rebootBeforeScheduledMissionsSeconds: isSet(object.rebootBeforeScheduledMissionsSeconds)
        ? globalThis.Number(object.rebootBeforeScheduledMissionsSeconds)
        : 0,
      dockJigglingEnabled: isSet(object.dockJigglingEnabled) ? globalThis.Boolean(object.dockJigglingEnabled) : false,
      allowTakeoffFromMismatchedDock: isSet(object.allowTakeoffFromMismatchedDock)
        ? globalThis.Boolean(object.allowTakeoffFromMismatchedDock)
        : false,
      batteryButtonLaunch: isSet(object.batteryButtonLaunch) ? globalThis.Boolean(object.batteryButtonLaunch) : false,
      rfHighPower: isSet(object.rfHighPower) ? globalThis.Boolean(object.rfHighPower) : false,
      flightMode: isSet(object.flightMode) ? vehicleSettings_FlightModeFromJSON(object.flightMode) : 0,
      ridFeatureEnabled: isSet(object.ridFeatureEnabled) ? globalThis.Boolean(object.ridFeatureEnabled) : false,
      allowOnVehiclePhotogrammetry: isSet(object.allowOnVehiclePhotogrammetry)
        ? globalThis.Boolean(object.allowOnVehiclePhotogrammetry)
        : false,
      ridFeatureDisabled: isSet(object.ridFeatureDisabled) ? globalThis.Boolean(object.ridFeatureDisabled) : false,
      allowSurfaceScanAlphaFeatures: isSet(object.allowSurfaceScanAlphaFeatures)
        ? globalThis.Boolean(object.allowSurfaceScanAlphaFeatures)
        : false,
      rebootMaxUptimeSeconds: isSet(object.rebootMaxUptimeSeconds)
        ? globalThis.Number(object.rebootMaxUptimeSeconds)
        : 0,
      battImbalanceThresholds: isSet(object.battImbalanceThresholds)
        ? BatteryCellVoltageImbalanceThresholds.fromJSON(object.battImbalanceThresholds)
        : undefined,
      wifiCountryCode: isSet(object.wifiCountryCode) ? globalThis.String(object.wifiCountryCode) : "",
      stopAtStructure: isSet(object.stopAtStructure) ? globalThis.Boolean(object.stopAtStructure) : false,
      speedNearObstacles: isSet(object.speedNearObstacles) ? globalThis.Number(object.speedNearObstacles) : 0,
      surfaceScan: isSet(object.surfaceScan) ? globalThis.Boolean(object.surfaceScan) : false,
      landingLedMode: isSet(object.landingLedMode) ? vehicleSettings_LandingLedModeFromJSON(object.landingLedMode) : 0,
      enableRapidDescent: isSet(object.enableRapidDescent) ? globalThis.Boolean(object.enableRapidDescent) : false,
      disableObstacleAvoidanceDockLanding: isSet(object.disableObstacleAvoidanceDockLanding)
        ? globalThis.Boolean(object.disableObstacleAvoidanceDockLanding)
        : false,
      longRangeDisabled: isSet(object.longRangeDisabled) ? globalThis.Boolean(object.longRangeDisabled) : false,
      allowPoweredPayloads: isSet(object.allowPoweredPayloads)
        ? globalThis.Boolean(object.allowPoweredPayloads)
        : false,
      disableCountryCodeScan: isSet(object.disableCountryCodeScan)
        ? globalThis.Boolean(object.disableCountryCodeScan)
        : false,
      allow3dCaptureScanMode: isSet(object.allow3dCaptureScanMode)
        ? globalThis.Boolean(object.allow3dCaptureScanMode)
        : false,
      allowSurfaceCaptureScanMode: isSet(object.allowSurfaceCaptureScanMode)
        ? globalThis.Boolean(object.allowSurfaceCaptureScanMode)
        : false,
      allowMapCaptureScanMode: isSet(object.allowMapCaptureScanMode)
        ? globalThis.Boolean(object.allowMapCaptureScanMode)
        : false,
      allowTowerCaptureScanMode: isSet(object.allowTowerCaptureScanMode)
        ? globalThis.Boolean(object.allowTowerCaptureScanMode)
        : false,
      allowIndoorCaptureScanMode: isSet(object.allowIndoorCaptureScanMode)
        ? globalThis.Boolean(object.allowIndoorCaptureScanMode)
        : false,
      allowIndoorCaptureScanModeV2: isSet(object.allowIndoorCaptureScanModeV2)
        ? globalThis.Boolean(object.allowIndoorCaptureScanModeV2)
        : false,
      allowOrbitCaptureScanMode: isSet(object.allowOrbitCaptureScanMode)
        ? globalThis.Boolean(object.allowOrbitCaptureScanMode)
        : false,
      allowCylinderCaptureScanMode: isSet(object.allowCylinderCaptureScanMode)
        ? globalThis.Boolean(object.allowCylinderCaptureScanMode)
        : false,
      allowSemanticTransmissionTowerScanMode: isSet(object.allowSemanticTransmissionTowerScanMode)
        ? globalThis.Boolean(object.allowSemanticTransmissionTowerScanMode)
        : false,
      fullSpinOnTakeoff: isSet(object.fullSpinOnTakeoff) ? globalThis.Boolean(object.fullSpinOnTakeoff) : false,
      allowIrSubjectTracking: isSet(object.allowIrSubjectTracking)
        ? globalThis.Boolean(object.allowIrSubjectTracking)
        : false,
      enableIspPixelContentFlip: isSet(object.enableIspPixelContentFlip)
        ? globalThis.Boolean(object.enableIspPixelContentFlip)
        : false,
      enableVehicleRtspKlv: isSet(object.enableVehicleRtspKlv)
        ? globalThis.Boolean(object.enableVehicleRtspKlv)
        : false,
      gimbalInspectionMode: isSet(object.gimbalInspectionMode)
        ? globalThis.Boolean(object.gimbalInspectionMode)
        : false,
      nightsenseDisabled: isSet(object.nightsenseDisabled) ? globalThis.Boolean(object.nightsenseDisabled) : false,
      boostDisablesNightsense: isSet(object.boostDisablesNightsense)
        ? globalThis.Boolean(object.boostDisablesNightsense)
        : false,
      nightsenseOffAllowedWithPoorGps: isSet(object.nightsenseOffAllowedWithPoorGps)
        ? globalThis.Boolean(object.nightsenseOffAllowedWithPoorGps)
        : false,
      nightsenseDisabledNightAutonomySwitch: isSet(object.nightsenseDisabledNightAutonomySwitch)
        ? globalThis.Boolean(object.nightsenseDisabledNightAutonomySwitch)
        : false,
      flashDriveRequiredForFlight: isSet(object.flashDriveRequiredForFlight)
        ? globalThis.Boolean(object.flashDriveRequiredForFlight)
        : false,
      manualBandSelection: isSet(object.manualBandSelection) ? globalThis.Number(object.manualBandSelection) : 0,
      bandSelectionList: globalThis.Array.isArray(object?.bandSelectionList)
        ? object.bandSelectionList.map((e: any) => globalThis.Number(e))
        : [],
      manualRadiowidthSelection: isSet(object.manualRadiowidthSelection)
        ? globalThis.Number(object.manualRadiowidthSelection)
        : 0,
      radiowidthSelectionList: globalThis.Array.isArray(object?.radiowidthSelectionList)
        ? object.radiowidthSelectionList.map((e: any) => globalThis.Number(e))
        : [],
      lteModemEnabled: isSet(object.lteModemEnabled) ? globalThis.Boolean(object.lteModemEnabled) : false,
      allowCommandedEland: isSet(object.allowCommandedEland) ? globalThis.Boolean(object.allowCommandedEland) : false,
      allowInternalMissionActions: isSet(object.allowInternalMissionActions)
        ? globalThis.Boolean(object.allowInternalMissionActions)
        : false,
      autoLandInAttiMode: isSet(object.autoLandInAttiMode) ? globalThis.Boolean(object.autoLandInAttiMode) : false,
      mhVehicleDcsCapable: isSet(object.mhVehicleDcsCapable) ? globalThis.Boolean(object.mhVehicleDcsCapable) : false,
      increasedMaxSpeedMapCapture: isSet(object.increasedMaxSpeedMapCapture)
        ? globalThis.Boolean(object.increasedMaxSpeedMapCapture)
        : false,
      manualWifiChannelOnRest: isSet(object.manualWifiChannelOnRest)
        ? globalThis.Number(object.manualWifiChannelOnRest)
        : 0,
      allowMavlinkPerch: isSet(object.allowMavlinkPerch) ? globalThis.Boolean(object.allowMavlinkPerch) : false,
      policeMode: isSet(object.policeMode) ? globalThis.Boolean(object.policeMode) : false,
      lteDataSaver: isSet(object.lteDataSaver) ? globalThis.Boolean(object.lteDataSaver) : false,
      blackoutModeEnabled: isSet(object.blackoutModeEnabled) ? globalThis.Boolean(object.blackoutModeEnabled) : false,
      disableGps: isSet(object.disableGps) ? globalThis.Boolean(object.disableGps) : false,
      manualFrameSelection: isSet(object.manualFrameSelection) ? globalThis.Number(object.manualFrameSelection) : 0,
      frameSelectionList: globalThis.Array.isArray(object?.frameSelectionList)
        ? object.frameSelectionList.map((e: any) => globalThis.Number(e))
        : [],
      enableHighAltitudeVio: isSet(object.enableHighAltitudeVio)
        ? globalThis.Boolean(object.enableHighAltitudeVio)
        : false,
      enableAttitudeRtx: isSet(object.enableAttitudeRtx) ? globalThis.Boolean(object.enableAttitudeRtx) : false,
      enableManualWff: isSet(object.enableManualWff) ? globalThis.Boolean(object.enableManualWff) : false,
      oaOffGroundAvoidanceDisabled: isSet(object.oaOffGroundAvoidanceDisabled)
        ? globalThis.Boolean(object.oaOffGroundAvoidanceDisabled)
        : false,
      enableJpgHighCompress3ds: isSet(object.enableJpgHighCompress3ds)
        ? globalThis.Boolean(object.enableJpgHighCompress3ds)
        : false,
      enablePerch: isSet(object.enablePerch) ? globalThis.Boolean(object.enablePerch) : false,
      manualModeSelection: isSet(object.manualModeSelection) ? globalThis.Number(object.manualModeSelection) : 0,
      modeSelectionList: globalThis.Array.isArray(object?.modeSelectionList)
        ? object.modeSelectionList.map((e: any) => globalThis.Number(e))
        : [],
      enableCommandedAttitude: isSet(object.enableCommandedAttitude)
        ? globalThis.Boolean(object.enableCommandedAttitude)
        : false,
      publicSafetyPoliceMode: isSet(object.publicSafetyPoliceMode)
        ? globalThis.Boolean(object.publicSafetyPoliceMode)
        : false,
      publicSafetyEmergencyMode: isSet(object.publicSafetyEmergencyMode)
        ? globalThis.Boolean(object.publicSafetyEmergencyMode)
        : false,
      europeanUnionLighting: isSet(object.europeanUnionLighting)
        ? globalThis.Boolean(object.europeanUnionLighting)
        : false,
      commandedAttitudeModeOn: isSet(object.commandedAttitudeModeOn)
        ? globalThis.Boolean(object.commandedAttitudeModeOn)
        : false,
      enableGetMicrohardRfConfig: isSet(object.enableGetMicrohardRfConfig)
        ? globalThis.Boolean(object.enableGetMicrohardRfConfig)
        : false,
      enableCommandedLostComms: isSet(object.enableCommandedLostComms)
        ? globalThis.Boolean(object.enableCommandedLostComms)
        : false,
      mavlinkClientSettings: isSet(object.mavlinkClientSettings)
        ? MavlinkClientSettings.fromJSON(object.mavlinkClientSettings)
        : undefined,
      enableGpsSpoofingDetector: isSet(object.enableGpsSpoofingDetector)
        ? globalThis.Boolean(object.enableGpsSpoofingDetector)
        : false,
      enableRscToggle3ds: isSet(object.enableRscToggle3ds) ? globalThis.Boolean(object.enableRscToggle3ds) : false,
      enableLowTakeoff: isSet(object.enableLowTakeoff) ? globalThis.Boolean(object.enableLowTakeoff) : false,
      enableRtkSupport: isSet(object.enableRtkSupport) ? globalThis.Boolean(object.enableRtkSupport) : false,
      enableFlightFileManifestTask: isSet(object.enableFlightFileManifestTask)
        ? globalThis.Boolean(object.enableFlightFileManifestTask)
        : false,
      isEuropeanUnion: isSet(object.isEuropeanUnion) ? globalThis.Boolean(object.isEuropeanUnion) : false,
      enableEuSlowSpeed: isSet(object.enableEuSlowSpeed) ? globalThis.Boolean(object.enableEuSlowSpeed) : false,
      enableForceVioDegraded: isSet(object.enableForceVioDegraded)
        ? globalThis.Boolean(object.enableForceVioDegraded)
        : false,
      enableThirdPartyAttachmentPrototyping: isSet(object.enableThirdPartyAttachmentPrototyping)
        ? globalThis.Boolean(object.enableThirdPartyAttachmentPrototyping)
        : false,
      mhManualChannelCapacityAllocation: isSet(object.mhManualChannelCapacityAllocation)
        ? globalThis.Number(object.mhManualChannelCapacityAllocation)
        : 0,
      mhChannelCapacityAllocationList: globalThis.Array.isArray(object?.mhChannelCapacityAllocationList)
        ? object.mhChannelCapacityAllocationList.map((e: any) => globalThis.Number(e))
        : [],
      enableForceLowTakeoffToggle: isSet(object.enableForceLowTakeoffToggle)
        ? globalThis.Boolean(object.enableForceLowTakeoffToggle)
        : false,
      forceLowTakeoff: isSet(object.forceLowTakeoff) ? globalThis.Boolean(object.forceLowTakeoff) : false,
      alwaysLogSubjectStreamForDockFlights: isSet(object.alwaysLogSubjectStreamForDockFlights)
        ? globalThis.Boolean(object.alwaysLogSubjectStreamForDockFlights)
        : false,
      enableInternalAttachmentPrototyping: isSet(object.enableInternalAttachmentPrototyping)
        ? globalThis.Boolean(object.enableInternalAttachmentPrototyping)
        : false,
      enableRtkNavigation: isSet(object.enableRtkNavigation) ? globalThis.Boolean(object.enableRtkNavigation) : false,
      enableMpegtsFileWriting: isSet(object.enableMpegtsFileWriting)
        ? globalThis.Boolean(object.enableMpegtsFileWriting)
        : false,
      mapCaptureTerrainFollowAlpha: isSet(object.mapCaptureTerrainFollowAlpha)
        ? globalThis.Boolean(object.mapCaptureTerrainFollowAlpha)
        : false,
      maxBoost: isSet(object.maxBoost) ? globalThis.Boolean(object.maxBoost) : false,
      enable20MpsMaxSpeed: isSet(object.enable20MpsMaxSpeed) ? globalThis.Boolean(object.enable20MpsMaxSpeed) : false,
      enableThirdPartyRadioAttachment: isSet(object.enableThirdPartyRadioAttachment)
        ? globalThis.Boolean(object.enableThirdPartyRadioAttachment)
        : false,
      ridOperatorId: isSet(object.ridOperatorId) ? globalThis.String(object.ridOperatorId) : "",
      persistDisableGps: isSet(object.persistDisableGps) ? globalThis.Boolean(object.persistDisableGps) : false,
      enableSlowMode: isSet(object.enableSlowMode) ? globalThis.Boolean(object.enableSlowMode) : false,
      enableParachuteExpectedButMissing: isSet(object.enableParachuteExpectedButMissing)
        ? globalThis.Boolean(object.enableParachuteExpectedButMissing)
        : false,
      allowSubjectFollow: isSet(object.allowSubjectFollow) ? globalThis.Boolean(object.allowSubjectFollow) : false,
      allowGpsFlightWithoutHandwave: isSet(object.allowGpsFlightWithoutHandwave)
        ? globalThis.Boolean(object.allowGpsFlightWithoutHandwave)
        : false,
      pathfinderDisablesNightsense: isSet(object.pathfinderDisablesNightsense)
        ? globalThis.Boolean(object.pathfinderDisablesNightsense)
        : false,
      enableMissionsCloudToController: isSet(object.enableMissionsCloudToController)
        ? globalThis.Boolean(object.enableMissionsCloudToController)
        : false,
      enableVisibleNightsenseStrobing: isSet(object.enableVisibleNightsenseStrobing)
        ? globalThis.Boolean(object.enableVisibleNightsenseStrobing)
        : false,
      enableIrNightsenseStrobing: isSet(object.enableIrNightsenseStrobing)
        ? globalThis.Boolean(object.enableIrNightsenseStrobing)
        : false,
      ignoreBaseGeofences: isSet(object.ignoreBaseGeofences) ? globalThis.Boolean(object.ignoreBaseGeofences) : false,
      ignoreAutonomousGeofences: isSet(object.ignoreAutonomousGeofences)
        ? globalThis.Boolean(object.ignoreAutonomousGeofences)
        : false,
      ignoreWarningGeofences: isSet(object.ignoreWarningGeofences)
        ? globalThis.Boolean(object.ignoreWarningGeofences)
        : false,
      disableFrameLock: isSet(object.disableFrameLock) ? globalThis.Boolean(object.disableFrameLock) : false,
      enableEnhancedSlBitrate: isSet(object.enableEnhancedSlBitrate)
        ? globalThis.Boolean(object.enableEnhancedSlBitrate)
        : false,
      forceFallbackToHandwave: isSet(object.forceFallbackToHandwave)
        ? globalThis.Boolean(object.forceFallbackToHandwave)
        : false,
      enableThirdPartyAttachmentConfigs: isSet(object.enableThirdPartyAttachmentConfigs)
        ? globalThis.Boolean(object.enableThirdPartyAttachmentConfigs)
        : false,
      enable20MpsMaxSpeedWaypointsOnly: isSet(object.enable20MpsMaxSpeedWaypointsOnly)
        ? globalThis.Boolean(object.enable20MpsMaxSpeedWaypointsOnly)
        : false,
      enableDcsModeSelection: isSet(object.enableDcsModeSelection)
        ? globalThis.Boolean(object.enableDcsModeSelection)
        : false,
      skipValidateCountrySettings: isSet(object.skipValidateCountrySettings)
        ? globalThis.Boolean(object.skipValidateCountrySettings)
        : false,
      enableOnDemandMissionTier: isSet(object.enableOnDemandMissionTier)
        ? globalThis.Boolean(object.enableOnDemandMissionTier)
        : false,
      enableIcingDetection: isSet(object.enableIcingDetection)
        ? globalThis.Boolean(object.enableIcingDetection)
        : false,
      subjectSettings: isSet(object.subjectSettings) ? SubjectSettings.fromJSON(object.subjectSettings) : undefined,
      enableFepPowerLimitNotification: isSet(object.enableFepPowerLimitNotification)
        ? globalThis.Boolean(object.enableFepPowerLimitNotification)
        : false,
      enableBatteryLightDiscipline: isSet(object.enableBatteryLightDiscipline)
        ? globalThis.Boolean(object.enableBatteryLightDiscipline)
        : false,
      enableSubjectFollowWithoutOa: isSet(object.enableSubjectFollowWithoutOa)
        ? globalThis.Boolean(object.enableSubjectFollowWithoutOa)
        : false,
      heightCeilingReferenceFrame: isSet(object.heightCeilingReferenceFrame)
        ? frameType_EnumFromJSON(object.heightCeilingReferenceFrame)
        : 0,
      enableLsop: isSet(object.enableLsop) ? globalThis.Boolean(object.enableLsop) : false,
      enable30MpsMaxGroundSpeed: isSet(object.enable30MpsMaxGroundSpeed)
        ? globalThis.Boolean(object.enable30MpsMaxGroundSpeed)
        : false,
      enableMobileInspectionAssets: isSet(object.enableMobileInspectionAssets)
        ? globalThis.Boolean(object.enableMobileInspectionAssets)
        : false,
      adsbRegulatoryManagerSettings: isSet(object.adsbRegulatoryManagerSettings)
        ? AdsbRegulatoryManagerSettings.fromJSON(object.adsbRegulatoryManagerSettings)
        : undefined,
      enableGeoBasedDtedIngest: isSet(object.enableGeoBasedDtedIngest)
        ? globalThis.Boolean(object.enableGeoBasedDtedIngest)
        : false,
    };
  },

  toJSON(message: VehicleSettings): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.validationId !== 0) {
      obj.validationId = Math.round(message.validationId);
    }
    if (message.cloudConfigUclock !== 0) {
      obj.cloudConfigUclock = Math.round(message.cloudConfigUclock);
    }
    if (message.heightCeilingOff === true) {
      obj.heightCeilingOff = message.heightCeilingOff;
    }
    if (message.heightCeilingValue !== 0) {
      obj.heightCeilingValue = message.heightCeilingValue;
    }
    if (message.ignoreGpsHeightCeil === true) {
      obj.ignoreGpsHeightCeil = message.ignoreGpsHeightCeil;
    }
    if (message.geofenceOff === true) {
      obj.geofenceOff = message.geofenceOff;
    }
    if (message.geofence !== undefined) {
      obj.geofence = Geofence.toJSON(message.geofence);
    }
    if (message.illuminationMode !== 0) {
      obj.illuminationMode = vehicleSettings_IlluminationModeToJSON(message.illuminationMode);
    }
    if (message.obstacleMarginMode !== 0) {
      obj.obstacleMarginMode = vehicleSettings_ObstacleMarginModeToJSON(message.obstacleMarginMode);
    }
    if (message.voxelMode !== 0) {
      obj.voxelMode = vehicleSettings_VoxelModeToJSON(message.voxelMode);
    }
    if (message.caseLandingDisabled === true) {
      obj.caseLandingDisabled = message.caseLandingDisabled;
    }
    if (message.returnWithVisionOnly === true) {
      obj.returnWithVisionOnly = message.returnWithVisionOnly;
    }
    if (message.rgbLedsDisabled === true) {
      obj.rgbLedsDisabled = message.rgbLedsDisabled;
    }
    if (message.superzoom === true) {
      obj.superzoom = message.superzoom;
    }
    if (message.digitalZoom === true) {
      obj.digitalZoom = message.digitalZoom;
    }
    if (message.returnSettings !== undefined) {
      obj.returnSettings = ReturnSettings.toJSON(message.returnSettings);
    }
    if (message.precisionFlightMode === true) {
      obj.precisionFlightMode = message.precisionFlightMode;
    }
    if (message.enterpriseMode === true) {
      obj.enterpriseMode = message.enterpriseMode;
    }
    if (message.navigationMode !== 0) {
      obj.navigationMode = navigationMode_ModeToJSON(message.navigationMode);
    }
    if (message.wifiBand !== 0) {
      obj.wifiBand = vehicleSettings_WifiBandToJSON(message.wifiBand);
    }
    if (message.narrowBandEnabled === true) {
      obj.narrowBandEnabled = message.narrowBandEnabled;
    }
    if (message.disableMediaEncryption === true) {
      obj.disableMediaEncryption = message.disableMediaEncryption;
    }
    if (message.skydiolinkAes256 === true) {
      obj.skydiolinkAes256 = message.skydiolinkAes256;
    }
    if (message.skysticksPairingInfo !== undefined) {
      obj.skysticksPairingInfo = SkysticksPairingInfo.toJSON(message.skysticksPairingInfo);
    }
    if (message.ignoreLowBatteryBehavior === true) {
      obj.ignoreLowBatteryBehavior = message.ignoreLowBatteryBehavior;
    }
    if (message.offline === true) {
      obj.offline = message.offline;
    }
    if (message.rgbLedMode !== 0) {
      obj.rgbLedMode = rgbLedMode_EnumToJSON(message.rgbLedMode);
    }
    if (message.overwriteOldMedia === true) {
      obj.overwriteOldMedia = message.overwriteOldMedia;
    }
    if (message.flightHistory === true) {
      obj.flightHistory = message.flightHistory;
    }
    if (message.manualWifiChannel !== 0) {
      obj.manualWifiChannel = Math.round(message.manualWifiChannel);
    }
    if (message.wifiChannelList?.length) {
      obj.wifiChannelList = message.wifiChannelList.map((e) => Math.round(e));
    }
    if (message.multiResPreviewEnable === true) {
      obj.multiResPreviewEnable = message.multiResPreviewEnable;
    }
    if (message.centralizedFrbRateEnable === true) {
      obj.centralizedFrbRateEnable = message.centralizedFrbRateEnable;
    }
    if (message.alignedTelemetryCsv === true) {
      obj.alignedTelemetryCsv = message.alignedTelemetryCsv;
    }
    if (message.handWaveEnabled === true) {
      obj.handWaveEnabled = message.handWaveEnabled;
    }
    if (message.displayHandWaveCalibration === true) {
      obj.displayHandWaveCalibration = message.displayHandWaveCalibration;
    }
    if (message.variableObstacleAvoidance === true) {
      obj.variableObstacleAvoidance = message.variableObstacleAvoidance;
    }
    if (message.rebootBeforeScheduledMissionsSeconds !== 0) {
      obj.rebootBeforeScheduledMissionsSeconds = Math.round(message.rebootBeforeScheduledMissionsSeconds);
    }
    if (message.dockJigglingEnabled === true) {
      obj.dockJigglingEnabled = message.dockJigglingEnabled;
    }
    if (message.allowTakeoffFromMismatchedDock === true) {
      obj.allowTakeoffFromMismatchedDock = message.allowTakeoffFromMismatchedDock;
    }
    if (message.batteryButtonLaunch === true) {
      obj.batteryButtonLaunch = message.batteryButtonLaunch;
    }
    if (message.rfHighPower === true) {
      obj.rfHighPower = message.rfHighPower;
    }
    if (message.flightMode !== 0) {
      obj.flightMode = vehicleSettings_FlightModeToJSON(message.flightMode);
    }
    if (message.ridFeatureEnabled === true) {
      obj.ridFeatureEnabled = message.ridFeatureEnabled;
    }
    if (message.allowOnVehiclePhotogrammetry === true) {
      obj.allowOnVehiclePhotogrammetry = message.allowOnVehiclePhotogrammetry;
    }
    if (message.ridFeatureDisabled === true) {
      obj.ridFeatureDisabled = message.ridFeatureDisabled;
    }
    if (message.allowSurfaceScanAlphaFeatures === true) {
      obj.allowSurfaceScanAlphaFeatures = message.allowSurfaceScanAlphaFeatures;
    }
    if (message.rebootMaxUptimeSeconds !== 0) {
      obj.rebootMaxUptimeSeconds = Math.round(message.rebootMaxUptimeSeconds);
    }
    if (message.battImbalanceThresholds !== undefined) {
      obj.battImbalanceThresholds = BatteryCellVoltageImbalanceThresholds.toJSON(message.battImbalanceThresholds);
    }
    if (message.wifiCountryCode !== "") {
      obj.wifiCountryCode = message.wifiCountryCode;
    }
    if (message.stopAtStructure === true) {
      obj.stopAtStructure = message.stopAtStructure;
    }
    if (message.speedNearObstacles !== 0) {
      obj.speedNearObstacles = message.speedNearObstacles;
    }
    if (message.surfaceScan === true) {
      obj.surfaceScan = message.surfaceScan;
    }
    if (message.landingLedMode !== 0) {
      obj.landingLedMode = vehicleSettings_LandingLedModeToJSON(message.landingLedMode);
    }
    if (message.enableRapidDescent === true) {
      obj.enableRapidDescent = message.enableRapidDescent;
    }
    if (message.disableObstacleAvoidanceDockLanding === true) {
      obj.disableObstacleAvoidanceDockLanding = message.disableObstacleAvoidanceDockLanding;
    }
    if (message.longRangeDisabled === true) {
      obj.longRangeDisabled = message.longRangeDisabled;
    }
    if (message.allowPoweredPayloads === true) {
      obj.allowPoweredPayloads = message.allowPoweredPayloads;
    }
    if (message.disableCountryCodeScan === true) {
      obj.disableCountryCodeScan = message.disableCountryCodeScan;
    }
    if (message.allow3dCaptureScanMode === true) {
      obj.allow3dCaptureScanMode = message.allow3dCaptureScanMode;
    }
    if (message.allowSurfaceCaptureScanMode === true) {
      obj.allowSurfaceCaptureScanMode = message.allowSurfaceCaptureScanMode;
    }
    if (message.allowMapCaptureScanMode === true) {
      obj.allowMapCaptureScanMode = message.allowMapCaptureScanMode;
    }
    if (message.allowTowerCaptureScanMode === true) {
      obj.allowTowerCaptureScanMode = message.allowTowerCaptureScanMode;
    }
    if (message.allowIndoorCaptureScanMode === true) {
      obj.allowIndoorCaptureScanMode = message.allowIndoorCaptureScanMode;
    }
    if (message.allowIndoorCaptureScanModeV2 === true) {
      obj.allowIndoorCaptureScanModeV2 = message.allowIndoorCaptureScanModeV2;
    }
    if (message.allowOrbitCaptureScanMode === true) {
      obj.allowOrbitCaptureScanMode = message.allowOrbitCaptureScanMode;
    }
    if (message.allowCylinderCaptureScanMode === true) {
      obj.allowCylinderCaptureScanMode = message.allowCylinderCaptureScanMode;
    }
    if (message.allowSemanticTransmissionTowerScanMode === true) {
      obj.allowSemanticTransmissionTowerScanMode = message.allowSemanticTransmissionTowerScanMode;
    }
    if (message.fullSpinOnTakeoff === true) {
      obj.fullSpinOnTakeoff = message.fullSpinOnTakeoff;
    }
    if (message.allowIrSubjectTracking === true) {
      obj.allowIrSubjectTracking = message.allowIrSubjectTracking;
    }
    if (message.enableIspPixelContentFlip === true) {
      obj.enableIspPixelContentFlip = message.enableIspPixelContentFlip;
    }
    if (message.enableVehicleRtspKlv === true) {
      obj.enableVehicleRtspKlv = message.enableVehicleRtspKlv;
    }
    if (message.gimbalInspectionMode === true) {
      obj.gimbalInspectionMode = message.gimbalInspectionMode;
    }
    if (message.nightsenseDisabled === true) {
      obj.nightsenseDisabled = message.nightsenseDisabled;
    }
    if (message.boostDisablesNightsense === true) {
      obj.boostDisablesNightsense = message.boostDisablesNightsense;
    }
    if (message.nightsenseOffAllowedWithPoorGps === true) {
      obj.nightsenseOffAllowedWithPoorGps = message.nightsenseOffAllowedWithPoorGps;
    }
    if (message.nightsenseDisabledNightAutonomySwitch === true) {
      obj.nightsenseDisabledNightAutonomySwitch = message.nightsenseDisabledNightAutonomySwitch;
    }
    if (message.flashDriveRequiredForFlight === true) {
      obj.flashDriveRequiredForFlight = message.flashDriveRequiredForFlight;
    }
    if (message.manualBandSelection !== 0) {
      obj.manualBandSelection = Math.round(message.manualBandSelection);
    }
    if (message.bandSelectionList?.length) {
      obj.bandSelectionList = message.bandSelectionList.map((e) => Math.round(e));
    }
    if (message.manualRadiowidthSelection !== 0) {
      obj.manualRadiowidthSelection = Math.round(message.manualRadiowidthSelection);
    }
    if (message.radiowidthSelectionList?.length) {
      obj.radiowidthSelectionList = message.radiowidthSelectionList.map((e) => Math.round(e));
    }
    if (message.lteModemEnabled === true) {
      obj.lteModemEnabled = message.lteModemEnabled;
    }
    if (message.allowCommandedEland === true) {
      obj.allowCommandedEland = message.allowCommandedEland;
    }
    if (message.allowInternalMissionActions === true) {
      obj.allowInternalMissionActions = message.allowInternalMissionActions;
    }
    if (message.autoLandInAttiMode === true) {
      obj.autoLandInAttiMode = message.autoLandInAttiMode;
    }
    if (message.mhVehicleDcsCapable === true) {
      obj.mhVehicleDcsCapable = message.mhVehicleDcsCapable;
    }
    if (message.increasedMaxSpeedMapCapture === true) {
      obj.increasedMaxSpeedMapCapture = message.increasedMaxSpeedMapCapture;
    }
    if (message.manualWifiChannelOnRest !== 0) {
      obj.manualWifiChannelOnRest = Math.round(message.manualWifiChannelOnRest);
    }
    if (message.allowMavlinkPerch === true) {
      obj.allowMavlinkPerch = message.allowMavlinkPerch;
    }
    if (message.policeMode === true) {
      obj.policeMode = message.policeMode;
    }
    if (message.lteDataSaver === true) {
      obj.lteDataSaver = message.lteDataSaver;
    }
    if (message.blackoutModeEnabled === true) {
      obj.blackoutModeEnabled = message.blackoutModeEnabled;
    }
    if (message.disableGps === true) {
      obj.disableGps = message.disableGps;
    }
    if (message.manualFrameSelection !== 0) {
      obj.manualFrameSelection = Math.round(message.manualFrameSelection);
    }
    if (message.frameSelectionList?.length) {
      obj.frameSelectionList = message.frameSelectionList.map((e) => Math.round(e));
    }
    if (message.enableHighAltitudeVio === true) {
      obj.enableHighAltitudeVio = message.enableHighAltitudeVio;
    }
    if (message.enableAttitudeRtx === true) {
      obj.enableAttitudeRtx = message.enableAttitudeRtx;
    }
    if (message.enableManualWff === true) {
      obj.enableManualWff = message.enableManualWff;
    }
    if (message.oaOffGroundAvoidanceDisabled === true) {
      obj.oaOffGroundAvoidanceDisabled = message.oaOffGroundAvoidanceDisabled;
    }
    if (message.enableJpgHighCompress3ds === true) {
      obj.enableJpgHighCompress3ds = message.enableJpgHighCompress3ds;
    }
    if (message.enablePerch === true) {
      obj.enablePerch = message.enablePerch;
    }
    if (message.manualModeSelection !== 0) {
      obj.manualModeSelection = Math.round(message.manualModeSelection);
    }
    if (message.modeSelectionList?.length) {
      obj.modeSelectionList = message.modeSelectionList.map((e) => Math.round(e));
    }
    if (message.enableCommandedAttitude === true) {
      obj.enableCommandedAttitude = message.enableCommandedAttitude;
    }
    if (message.publicSafetyPoliceMode === true) {
      obj.publicSafetyPoliceMode = message.publicSafetyPoliceMode;
    }
    if (message.publicSafetyEmergencyMode === true) {
      obj.publicSafetyEmergencyMode = message.publicSafetyEmergencyMode;
    }
    if (message.europeanUnionLighting === true) {
      obj.europeanUnionLighting = message.europeanUnionLighting;
    }
    if (message.commandedAttitudeModeOn === true) {
      obj.commandedAttitudeModeOn = message.commandedAttitudeModeOn;
    }
    if (message.enableGetMicrohardRfConfig === true) {
      obj.enableGetMicrohardRfConfig = message.enableGetMicrohardRfConfig;
    }
    if (message.enableCommandedLostComms === true) {
      obj.enableCommandedLostComms = message.enableCommandedLostComms;
    }
    if (message.mavlinkClientSettings !== undefined) {
      obj.mavlinkClientSettings = MavlinkClientSettings.toJSON(message.mavlinkClientSettings);
    }
    if (message.enableGpsSpoofingDetector === true) {
      obj.enableGpsSpoofingDetector = message.enableGpsSpoofingDetector;
    }
    if (message.enableRscToggle3ds === true) {
      obj.enableRscToggle3ds = message.enableRscToggle3ds;
    }
    if (message.enableLowTakeoff === true) {
      obj.enableLowTakeoff = message.enableLowTakeoff;
    }
    if (message.enableRtkSupport === true) {
      obj.enableRtkSupport = message.enableRtkSupport;
    }
    if (message.enableFlightFileManifestTask === true) {
      obj.enableFlightFileManifestTask = message.enableFlightFileManifestTask;
    }
    if (message.isEuropeanUnion === true) {
      obj.isEuropeanUnion = message.isEuropeanUnion;
    }
    if (message.enableEuSlowSpeed === true) {
      obj.enableEuSlowSpeed = message.enableEuSlowSpeed;
    }
    if (message.enableForceVioDegraded === true) {
      obj.enableForceVioDegraded = message.enableForceVioDegraded;
    }
    if (message.enableThirdPartyAttachmentPrototyping === true) {
      obj.enableThirdPartyAttachmentPrototyping = message.enableThirdPartyAttachmentPrototyping;
    }
    if (message.mhManualChannelCapacityAllocation !== 0) {
      obj.mhManualChannelCapacityAllocation = Math.round(message.mhManualChannelCapacityAllocation);
    }
    if (message.mhChannelCapacityAllocationList?.length) {
      obj.mhChannelCapacityAllocationList = message.mhChannelCapacityAllocationList.map((e) => Math.round(e));
    }
    if (message.enableForceLowTakeoffToggle === true) {
      obj.enableForceLowTakeoffToggle = message.enableForceLowTakeoffToggle;
    }
    if (message.forceLowTakeoff === true) {
      obj.forceLowTakeoff = message.forceLowTakeoff;
    }
    if (message.alwaysLogSubjectStreamForDockFlights === true) {
      obj.alwaysLogSubjectStreamForDockFlights = message.alwaysLogSubjectStreamForDockFlights;
    }
    if (message.enableInternalAttachmentPrototyping === true) {
      obj.enableInternalAttachmentPrototyping = message.enableInternalAttachmentPrototyping;
    }
    if (message.enableRtkNavigation === true) {
      obj.enableRtkNavigation = message.enableRtkNavigation;
    }
    if (message.enableMpegtsFileWriting === true) {
      obj.enableMpegtsFileWriting = message.enableMpegtsFileWriting;
    }
    if (message.mapCaptureTerrainFollowAlpha === true) {
      obj.mapCaptureTerrainFollowAlpha = message.mapCaptureTerrainFollowAlpha;
    }
    if (message.maxBoost === true) {
      obj.maxBoost = message.maxBoost;
    }
    if (message.enable20MpsMaxSpeed === true) {
      obj.enable20MpsMaxSpeed = message.enable20MpsMaxSpeed;
    }
    if (message.enableThirdPartyRadioAttachment === true) {
      obj.enableThirdPartyRadioAttachment = message.enableThirdPartyRadioAttachment;
    }
    if (message.ridOperatorId !== "") {
      obj.ridOperatorId = message.ridOperatorId;
    }
    if (message.persistDisableGps === true) {
      obj.persistDisableGps = message.persistDisableGps;
    }
    if (message.enableSlowMode === true) {
      obj.enableSlowMode = message.enableSlowMode;
    }
    if (message.enableParachuteExpectedButMissing === true) {
      obj.enableParachuteExpectedButMissing = message.enableParachuteExpectedButMissing;
    }
    if (message.allowSubjectFollow === true) {
      obj.allowSubjectFollow = message.allowSubjectFollow;
    }
    if (message.allowGpsFlightWithoutHandwave === true) {
      obj.allowGpsFlightWithoutHandwave = message.allowGpsFlightWithoutHandwave;
    }
    if (message.pathfinderDisablesNightsense === true) {
      obj.pathfinderDisablesNightsense = message.pathfinderDisablesNightsense;
    }
    if (message.enableMissionsCloudToController === true) {
      obj.enableMissionsCloudToController = message.enableMissionsCloudToController;
    }
    if (message.enableVisibleNightsenseStrobing === true) {
      obj.enableVisibleNightsenseStrobing = message.enableVisibleNightsenseStrobing;
    }
    if (message.enableIrNightsenseStrobing === true) {
      obj.enableIrNightsenseStrobing = message.enableIrNightsenseStrobing;
    }
    if (message.ignoreBaseGeofences === true) {
      obj.ignoreBaseGeofences = message.ignoreBaseGeofences;
    }
    if (message.ignoreAutonomousGeofences === true) {
      obj.ignoreAutonomousGeofences = message.ignoreAutonomousGeofences;
    }
    if (message.ignoreWarningGeofences === true) {
      obj.ignoreWarningGeofences = message.ignoreWarningGeofences;
    }
    if (message.disableFrameLock === true) {
      obj.disableFrameLock = message.disableFrameLock;
    }
    if (message.enableEnhancedSlBitrate === true) {
      obj.enableEnhancedSlBitrate = message.enableEnhancedSlBitrate;
    }
    if (message.forceFallbackToHandwave === true) {
      obj.forceFallbackToHandwave = message.forceFallbackToHandwave;
    }
    if (message.enableThirdPartyAttachmentConfigs === true) {
      obj.enableThirdPartyAttachmentConfigs = message.enableThirdPartyAttachmentConfigs;
    }
    if (message.enable20MpsMaxSpeedWaypointsOnly === true) {
      obj.enable20MpsMaxSpeedWaypointsOnly = message.enable20MpsMaxSpeedWaypointsOnly;
    }
    if (message.enableDcsModeSelection === true) {
      obj.enableDcsModeSelection = message.enableDcsModeSelection;
    }
    if (message.skipValidateCountrySettings === true) {
      obj.skipValidateCountrySettings = message.skipValidateCountrySettings;
    }
    if (message.enableOnDemandMissionTier === true) {
      obj.enableOnDemandMissionTier = message.enableOnDemandMissionTier;
    }
    if (message.enableIcingDetection === true) {
      obj.enableIcingDetection = message.enableIcingDetection;
    }
    if (message.subjectSettings !== undefined) {
      obj.subjectSettings = SubjectSettings.toJSON(message.subjectSettings);
    }
    if (message.enableFepPowerLimitNotification === true) {
      obj.enableFepPowerLimitNotification = message.enableFepPowerLimitNotification;
    }
    if (message.enableBatteryLightDiscipline === true) {
      obj.enableBatteryLightDiscipline = message.enableBatteryLightDiscipline;
    }
    if (message.enableSubjectFollowWithoutOa === true) {
      obj.enableSubjectFollowWithoutOa = message.enableSubjectFollowWithoutOa;
    }
    if (message.heightCeilingReferenceFrame !== 0) {
      obj.heightCeilingReferenceFrame = frameType_EnumToJSON(message.heightCeilingReferenceFrame);
    }
    if (message.enableLsop === true) {
      obj.enableLsop = message.enableLsop;
    }
    if (message.enable30MpsMaxGroundSpeed === true) {
      obj.enable30MpsMaxGroundSpeed = message.enable30MpsMaxGroundSpeed;
    }
    if (message.enableMobileInspectionAssets === true) {
      obj.enableMobileInspectionAssets = message.enableMobileInspectionAssets;
    }
    if (message.adsbRegulatoryManagerSettings !== undefined) {
      obj.adsbRegulatoryManagerSettings = AdsbRegulatoryManagerSettings.toJSON(message.adsbRegulatoryManagerSettings);
    }
    if (message.enableGeoBasedDtedIngest === true) {
      obj.enableGeoBasedDtedIngest = message.enableGeoBasedDtedIngest;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<VehicleSettings>, I>>(base?: I): VehicleSettings {
    return VehicleSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<VehicleSettings>, I>>(object: I): VehicleSettings {
    const message = createBaseVehicleSettings();
    message.utime = object.utime ?? 0;
    message.validationId = object.validationId ?? 0;
    message.cloudConfigUclock = object.cloudConfigUclock ?? 0;
    message.heightCeilingOff = object.heightCeilingOff ?? false;
    message.heightCeilingValue = object.heightCeilingValue ?? 0;
    message.ignoreGpsHeightCeil = object.ignoreGpsHeightCeil ?? false;
    message.geofenceOff = object.geofenceOff ?? false;
    message.geofence = (object.geofence !== undefined && object.geofence !== null)
      ? Geofence.fromPartial(object.geofence)
      : undefined;
    message.illuminationMode = object.illuminationMode ?? 0;
    message.obstacleMarginMode = object.obstacleMarginMode ?? 0;
    message.voxelMode = object.voxelMode ?? 0;
    message.caseLandingDisabled = object.caseLandingDisabled ?? false;
    message.returnWithVisionOnly = object.returnWithVisionOnly ?? false;
    message.rgbLedsDisabled = object.rgbLedsDisabled ?? false;
    message.superzoom = object.superzoom ?? false;
    message.digitalZoom = object.digitalZoom ?? false;
    message.returnSettings = (object.returnSettings !== undefined && object.returnSettings !== null)
      ? ReturnSettings.fromPartial(object.returnSettings)
      : undefined;
    message.precisionFlightMode = object.precisionFlightMode ?? false;
    message.enterpriseMode = object.enterpriseMode ?? false;
    message.navigationMode = object.navigationMode ?? 0;
    message.wifiBand = object.wifiBand ?? 0;
    message.narrowBandEnabled = object.narrowBandEnabled ?? false;
    message.disableMediaEncryption = object.disableMediaEncryption ?? false;
    message.skydiolinkAes256 = object.skydiolinkAes256 ?? false;
    message.skysticksPairingInfo = (object.skysticksPairingInfo !== undefined && object.skysticksPairingInfo !== null)
      ? SkysticksPairingInfo.fromPartial(object.skysticksPairingInfo)
      : undefined;
    message.ignoreLowBatteryBehavior = object.ignoreLowBatteryBehavior ?? false;
    message.offline = object.offline ?? false;
    message.rgbLedMode = object.rgbLedMode ?? 0;
    message.overwriteOldMedia = object.overwriteOldMedia ?? false;
    message.flightHistory = object.flightHistory ?? false;
    message.manualWifiChannel = object.manualWifiChannel ?? 0;
    message.wifiChannelList = object.wifiChannelList?.map((e) => e) || [];
    message.multiResPreviewEnable = object.multiResPreviewEnable ?? false;
    message.centralizedFrbRateEnable = object.centralizedFrbRateEnable ?? false;
    message.alignedTelemetryCsv = object.alignedTelemetryCsv ?? false;
    message.handWaveEnabled = object.handWaveEnabled ?? false;
    message.displayHandWaveCalibration = object.displayHandWaveCalibration ?? false;
    message.variableObstacleAvoidance = object.variableObstacleAvoidance ?? false;
    message.rebootBeforeScheduledMissionsSeconds = object.rebootBeforeScheduledMissionsSeconds ?? 0;
    message.dockJigglingEnabled = object.dockJigglingEnabled ?? false;
    message.allowTakeoffFromMismatchedDock = object.allowTakeoffFromMismatchedDock ?? false;
    message.batteryButtonLaunch = object.batteryButtonLaunch ?? false;
    message.rfHighPower = object.rfHighPower ?? false;
    message.flightMode = object.flightMode ?? 0;
    message.ridFeatureEnabled = object.ridFeatureEnabled ?? false;
    message.allowOnVehiclePhotogrammetry = object.allowOnVehiclePhotogrammetry ?? false;
    message.ridFeatureDisabled = object.ridFeatureDisabled ?? false;
    message.allowSurfaceScanAlphaFeatures = object.allowSurfaceScanAlphaFeatures ?? false;
    message.rebootMaxUptimeSeconds = object.rebootMaxUptimeSeconds ?? 0;
    message.battImbalanceThresholds =
      (object.battImbalanceThresholds !== undefined && object.battImbalanceThresholds !== null)
        ? BatteryCellVoltageImbalanceThresholds.fromPartial(object.battImbalanceThresholds)
        : undefined;
    message.wifiCountryCode = object.wifiCountryCode ?? "";
    message.stopAtStructure = object.stopAtStructure ?? false;
    message.speedNearObstacles = object.speedNearObstacles ?? 0;
    message.surfaceScan = object.surfaceScan ?? false;
    message.landingLedMode = object.landingLedMode ?? 0;
    message.enableRapidDescent = object.enableRapidDescent ?? false;
    message.disableObstacleAvoidanceDockLanding = object.disableObstacleAvoidanceDockLanding ?? false;
    message.longRangeDisabled = object.longRangeDisabled ?? false;
    message.allowPoweredPayloads = object.allowPoweredPayloads ?? false;
    message.disableCountryCodeScan = object.disableCountryCodeScan ?? false;
    message.allow3dCaptureScanMode = object.allow3dCaptureScanMode ?? false;
    message.allowSurfaceCaptureScanMode = object.allowSurfaceCaptureScanMode ?? false;
    message.allowMapCaptureScanMode = object.allowMapCaptureScanMode ?? false;
    message.allowTowerCaptureScanMode = object.allowTowerCaptureScanMode ?? false;
    message.allowIndoorCaptureScanMode = object.allowIndoorCaptureScanMode ?? false;
    message.allowIndoorCaptureScanModeV2 = object.allowIndoorCaptureScanModeV2 ?? false;
    message.allowOrbitCaptureScanMode = object.allowOrbitCaptureScanMode ?? false;
    message.allowCylinderCaptureScanMode = object.allowCylinderCaptureScanMode ?? false;
    message.allowSemanticTransmissionTowerScanMode = object.allowSemanticTransmissionTowerScanMode ?? false;
    message.fullSpinOnTakeoff = object.fullSpinOnTakeoff ?? false;
    message.allowIrSubjectTracking = object.allowIrSubjectTracking ?? false;
    message.enableIspPixelContentFlip = object.enableIspPixelContentFlip ?? false;
    message.enableVehicleRtspKlv = object.enableVehicleRtspKlv ?? false;
    message.gimbalInspectionMode = object.gimbalInspectionMode ?? false;
    message.nightsenseDisabled = object.nightsenseDisabled ?? false;
    message.boostDisablesNightsense = object.boostDisablesNightsense ?? false;
    message.nightsenseOffAllowedWithPoorGps = object.nightsenseOffAllowedWithPoorGps ?? false;
    message.nightsenseDisabledNightAutonomySwitch = object.nightsenseDisabledNightAutonomySwitch ?? false;
    message.flashDriveRequiredForFlight = object.flashDriveRequiredForFlight ?? false;
    message.manualBandSelection = object.manualBandSelection ?? 0;
    message.bandSelectionList = object.bandSelectionList?.map((e) => e) || [];
    message.manualRadiowidthSelection = object.manualRadiowidthSelection ?? 0;
    message.radiowidthSelectionList = object.radiowidthSelectionList?.map((e) => e) || [];
    message.lteModemEnabled = object.lteModemEnabled ?? false;
    message.allowCommandedEland = object.allowCommandedEland ?? false;
    message.allowInternalMissionActions = object.allowInternalMissionActions ?? false;
    message.autoLandInAttiMode = object.autoLandInAttiMode ?? false;
    message.mhVehicleDcsCapable = object.mhVehicleDcsCapable ?? false;
    message.increasedMaxSpeedMapCapture = object.increasedMaxSpeedMapCapture ?? false;
    message.manualWifiChannelOnRest = object.manualWifiChannelOnRest ?? 0;
    message.allowMavlinkPerch = object.allowMavlinkPerch ?? false;
    message.policeMode = object.policeMode ?? false;
    message.lteDataSaver = object.lteDataSaver ?? false;
    message.blackoutModeEnabled = object.blackoutModeEnabled ?? false;
    message.disableGps = object.disableGps ?? false;
    message.manualFrameSelection = object.manualFrameSelection ?? 0;
    message.frameSelectionList = object.frameSelectionList?.map((e) => e) || [];
    message.enableHighAltitudeVio = object.enableHighAltitudeVio ?? false;
    message.enableAttitudeRtx = object.enableAttitudeRtx ?? false;
    message.enableManualWff = object.enableManualWff ?? false;
    message.oaOffGroundAvoidanceDisabled = object.oaOffGroundAvoidanceDisabled ?? false;
    message.enableJpgHighCompress3ds = object.enableJpgHighCompress3ds ?? false;
    message.enablePerch = object.enablePerch ?? false;
    message.manualModeSelection = object.manualModeSelection ?? 0;
    message.modeSelectionList = object.modeSelectionList?.map((e) => e) || [];
    message.enableCommandedAttitude = object.enableCommandedAttitude ?? false;
    message.publicSafetyPoliceMode = object.publicSafetyPoliceMode ?? false;
    message.publicSafetyEmergencyMode = object.publicSafetyEmergencyMode ?? false;
    message.europeanUnionLighting = object.europeanUnionLighting ?? false;
    message.commandedAttitudeModeOn = object.commandedAttitudeModeOn ?? false;
    message.enableGetMicrohardRfConfig = object.enableGetMicrohardRfConfig ?? false;
    message.enableCommandedLostComms = object.enableCommandedLostComms ?? false;
    message.mavlinkClientSettings =
      (object.mavlinkClientSettings !== undefined && object.mavlinkClientSettings !== null)
        ? MavlinkClientSettings.fromPartial(object.mavlinkClientSettings)
        : undefined;
    message.enableGpsSpoofingDetector = object.enableGpsSpoofingDetector ?? false;
    message.enableRscToggle3ds = object.enableRscToggle3ds ?? false;
    message.enableLowTakeoff = object.enableLowTakeoff ?? false;
    message.enableRtkSupport = object.enableRtkSupport ?? false;
    message.enableFlightFileManifestTask = object.enableFlightFileManifestTask ?? false;
    message.isEuropeanUnion = object.isEuropeanUnion ?? false;
    message.enableEuSlowSpeed = object.enableEuSlowSpeed ?? false;
    message.enableForceVioDegraded = object.enableForceVioDegraded ?? false;
    message.enableThirdPartyAttachmentPrototyping = object.enableThirdPartyAttachmentPrototyping ?? false;
    message.mhManualChannelCapacityAllocation = object.mhManualChannelCapacityAllocation ?? 0;
    message.mhChannelCapacityAllocationList = object.mhChannelCapacityAllocationList?.map((e) => e) || [];
    message.enableForceLowTakeoffToggle = object.enableForceLowTakeoffToggle ?? false;
    message.forceLowTakeoff = object.forceLowTakeoff ?? false;
    message.alwaysLogSubjectStreamForDockFlights = object.alwaysLogSubjectStreamForDockFlights ?? false;
    message.enableInternalAttachmentPrototyping = object.enableInternalAttachmentPrototyping ?? false;
    message.enableRtkNavigation = object.enableRtkNavigation ?? false;
    message.enableMpegtsFileWriting = object.enableMpegtsFileWriting ?? false;
    message.mapCaptureTerrainFollowAlpha = object.mapCaptureTerrainFollowAlpha ?? false;
    message.maxBoost = object.maxBoost ?? false;
    message.enable20MpsMaxSpeed = object.enable20MpsMaxSpeed ?? false;
    message.enableThirdPartyRadioAttachment = object.enableThirdPartyRadioAttachment ?? false;
    message.ridOperatorId = object.ridOperatorId ?? "";
    message.persistDisableGps = object.persistDisableGps ?? false;
    message.enableSlowMode = object.enableSlowMode ?? false;
    message.enableParachuteExpectedButMissing = object.enableParachuteExpectedButMissing ?? false;
    message.allowSubjectFollow = object.allowSubjectFollow ?? false;
    message.allowGpsFlightWithoutHandwave = object.allowGpsFlightWithoutHandwave ?? false;
    message.pathfinderDisablesNightsense = object.pathfinderDisablesNightsense ?? false;
    message.enableMissionsCloudToController = object.enableMissionsCloudToController ?? false;
    message.enableVisibleNightsenseStrobing = object.enableVisibleNightsenseStrobing ?? false;
    message.enableIrNightsenseStrobing = object.enableIrNightsenseStrobing ?? false;
    message.ignoreBaseGeofences = object.ignoreBaseGeofences ?? false;
    message.ignoreAutonomousGeofences = object.ignoreAutonomousGeofences ?? false;
    message.ignoreWarningGeofences = object.ignoreWarningGeofences ?? false;
    message.disableFrameLock = object.disableFrameLock ?? false;
    message.enableEnhancedSlBitrate = object.enableEnhancedSlBitrate ?? false;
    message.forceFallbackToHandwave = object.forceFallbackToHandwave ?? false;
    message.enableThirdPartyAttachmentConfigs = object.enableThirdPartyAttachmentConfigs ?? false;
    message.enable20MpsMaxSpeedWaypointsOnly = object.enable20MpsMaxSpeedWaypointsOnly ?? false;
    message.enableDcsModeSelection = object.enableDcsModeSelection ?? false;
    message.skipValidateCountrySettings = object.skipValidateCountrySettings ?? false;
    message.enableOnDemandMissionTier = object.enableOnDemandMissionTier ?? false;
    message.enableIcingDetection = object.enableIcingDetection ?? false;
    message.subjectSettings = (object.subjectSettings !== undefined && object.subjectSettings !== null)
      ? SubjectSettings.fromPartial(object.subjectSettings)
      : undefined;
    message.enableFepPowerLimitNotification = object.enableFepPowerLimitNotification ?? false;
    message.enableBatteryLightDiscipline = object.enableBatteryLightDiscipline ?? false;
    message.enableSubjectFollowWithoutOa = object.enableSubjectFollowWithoutOa ?? false;
    message.heightCeilingReferenceFrame = object.heightCeilingReferenceFrame ?? 0;
    message.enableLsop = object.enableLsop ?? false;
    message.enable30MpsMaxGroundSpeed = object.enable30MpsMaxGroundSpeed ?? false;
    message.enableMobileInspectionAssets = object.enableMobileInspectionAssets ?? false;
    message.adsbRegulatoryManagerSettings =
      (object.adsbRegulatoryManagerSettings !== undefined && object.adsbRegulatoryManagerSettings !== null)
        ? AdsbRegulatoryManagerSettings.fromPartial(object.adsbRegulatoryManagerSettings)
        : undefined;
    message.enableGeoBasedDtedIngest = object.enableGeoBasedDtedIngest ?? false;
    return message;
  },
};

function createBaseVehicleSettingsOverrides(): VehicleSettingsOverrides {
  return {
    heightCeilingOverride: 0,
    allowedIlluminationModes: [],
    allowedObstacleMarginModes: [],
    returnWithVisionOnlyOverride: 0,
    superzoomOverride: 0,
    digitalZoomOverride: 0,
    precisionFlightModeOverride: 0,
    skydiolinkAes256Override: 0,
    allowedNavigationModes: [],
    ignoreLowBatteryBehaviorOverride: 0,
    disableMediaEncryptionOverride: 0,
    disableMediaEncryptionOverrideV2: 0,
    offlineOverride: 0,
    rgbLedDisableOverride: 0,
    sdcardOverwriteOverride: 0,
    flightHistoryOverride: 0,
    multiResPreviewEnableOverride: 0,
    centralizedFrbRateEnableOverride: 0,
    alignedTelemetryCsvOverride: 0,
    displayHandWaveCalibrationOverride: 0,
    variableObstacleAvoidanceOverride: 0,
    dockJigglingEnabledOverride: 0,
    allowTakeoffFromMismatchedDockOverride: 0,
    allowOnVehiclePhotogrammetryOverride: 0,
    useBacktrackingRtxOverride: 0,
    ridFeatureEnabledOverride: 0,
    ridFeatureDisabledOverride: 0,
    allowSurfaceScanAlphaFeaturesOverride: 0,
    rebootMaxUptimeSecondsOverride: 0,
    defaultGeofence: undefined,
    battImbalanceThresholdsOverride: undefined,
    surfaceScanOverride: 0,
    disableObstacleAvoidanceDockLandingOverride: 0,
    allowPoweredPayloadsOverride: 0,
    disableCountryCodeScanOverride: 0,
    allow3dCaptureScanModeOverride: 0,
    allowSurfaceCaptureScanModeOverride: 0,
    allowMapCaptureScanModeOverride: 0,
    allowTowerCaptureScanModeOverride: 0,
    allowIndoorCaptureScanModeOverride: 0,
    allowIndoorCaptureScanModeV2Override: 0,
    allowOrbitCaptureScanModeOverride: 0,
    allowCylinderCaptureScanModeOverride: 0,
    allowSemanticTransmissionTowerScanModeOverride: 0,
    fullSpinOnTakeoffOverride: 0,
    allowIrSubjectTrackingOverride: 0,
    enableIspPixelContentFlipOverride: 0,
    enableVehicleRtspKlvOverride: 0,
    gimbalInspectionModeOverride: 0,
    flashDriveRequiredForFlightOverride: 0,
    allowCommandedElandOverride: 0,
    allowInternalMissionActionsOverride: 0,
    increasedMaxSpeedMapCaptureOverride: 0,
    allowMavlinkPerchOverride: 0,
    forceRgbLedDisableOverride: 0,
    enableHighAltitudeVioOverride: 0,
    enableAttitudeRtxOverride: 0,
    enableManualWffOverride: 0,
    enableJpgHighCompress3dsOverride: 0,
    enableGetMicrohardRfConfigOverride: 0,
    enableCommandedLostCommsOverride: 0,
    enableGpsSpoofingDetectorOverride: 0,
    enableRscToggle3dsOverride: 0,
    enableLowTakeoffOverride: 0,
    enableRtkSupportOverride: 0,
    enableFlightFileManifestTaskOverride: 0,
    isEuropeanUnionOverride: 0,
    enablePerchOverride: 0,
    enableThirdPartyAttachmentPrototypingOverride: 0,
    alwaysLogSubjectStreamForDockFlightsOverride: 0,
    enableForceLowTakeoffToggleOverride: 0,
    enableInternalAttachmentPrototypingOverride: 0,
    enableRtkNavigation: 0,
    enableMpegtsFileWritingOverride: 0,
    mapCaptureTerrainFollowAlphaOverride: 0,
    enable20MpsMaxSpeedOverride: 0,
    enableThirdPartyRadioAttachmentOverride: 0,
    enableSlowModeOverride: 0,
    enableParachuteExpectedButMissingOverride: 0,
    allowSubjectFollowOverride: 0,
    allowGpsFlightWithoutHandwaveOverride: 0,
    geofenceOffOverride: 0,
    enableMissionsCloudToControllerOverride: 0,
    enableVisibleNightsenseStrobingOverride: 0,
    enableIrNightsenseStrobingOverride: 0,
    enableEnhancedSlBitrateOverride: 0,
    enableThirdPartyAttachmentConfigsOverride: 0,
    enable20MpsMaxSpeedWaypointsOnlyOverride: 0,
    enableDcsModeSelectionOverride: 0,
    skipValidateCountrySettingsOverride: 0,
    enableOnDemandMissionTierOverride: 0,
    enableIcingDetectionOverride: 0,
    enableFepPowerLimitNotificationOverride: 0,
    enableBatteryLightDisciplineOverride: 0,
    enableSubjectFollowWithoutOaOverride: 0,
    enableLsopOverride: 0,
    enable30MpsMaxGroundSpeedOverride: 0,
    enableMobileInspectionAssetsOverride: 0,
    enableGeoBasedDtedIngestOverride: 0,
  };
}

export const VehicleSettingsOverrides = {
  encode(message: VehicleSettingsOverrides, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.heightCeilingOverride !== 0) {
      writer.uint32(16).int32(message.heightCeilingOverride);
    }
    writer.uint32(26).fork();
    for (const v of message.allowedIlluminationModes) {
      writer.int32(v);
    }
    writer.ldelim();
    writer.uint32(34).fork();
    for (const v of message.allowedObstacleMarginModes) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.returnWithVisionOnlyOverride !== 0) {
      writer.uint32(40).int32(message.returnWithVisionOnlyOverride);
    }
    if (message.superzoomOverride !== 0) {
      writer.uint32(48).int32(message.superzoomOverride);
    }
    if (message.digitalZoomOverride !== 0) {
      writer.uint32(80).int32(message.digitalZoomOverride);
    }
    if (message.precisionFlightModeOverride !== 0) {
      writer.uint32(56).int32(message.precisionFlightModeOverride);
    }
    if (message.skydiolinkAes256Override !== 0) {
      writer.uint32(64).int32(message.skydiolinkAes256Override);
    }
    writer.uint32(74).fork();
    for (const v of message.allowedNavigationModes) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.ignoreLowBatteryBehaviorOverride !== 0) {
      writer.uint32(88).int32(message.ignoreLowBatteryBehaviorOverride);
    }
    if (message.disableMediaEncryptionOverride !== 0) {
      writer.uint32(96).int32(message.disableMediaEncryptionOverride);
    }
    if (message.disableMediaEncryptionOverrideV2 !== 0) {
      writer.uint32(712).int32(message.disableMediaEncryptionOverrideV2);
    }
    if (message.offlineOverride !== 0) {
      writer.uint32(104).int32(message.offlineOverride);
    }
    if (message.rgbLedDisableOverride !== 0) {
      writer.uint32(112).int32(message.rgbLedDisableOverride);
    }
    if (message.sdcardOverwriteOverride !== 0) {
      writer.uint32(120).int32(message.sdcardOverwriteOverride);
    }
    if (message.flightHistoryOverride !== 0) {
      writer.uint32(128).int32(message.flightHistoryOverride);
    }
    if (message.multiResPreviewEnableOverride !== 0) {
      writer.uint32(144).int32(message.multiResPreviewEnableOverride);
    }
    if (message.centralizedFrbRateEnableOverride !== 0) {
      writer.uint32(152).int32(message.centralizedFrbRateEnableOverride);
    }
    if (message.alignedTelemetryCsvOverride !== 0) {
      writer.uint32(168).int32(message.alignedTelemetryCsvOverride);
    }
    if (message.displayHandWaveCalibrationOverride !== 0) {
      writer.uint32(184).int32(message.displayHandWaveCalibrationOverride);
    }
    if (message.variableObstacleAvoidanceOverride !== 0) {
      writer.uint32(200).int32(message.variableObstacleAvoidanceOverride);
    }
    if (message.dockJigglingEnabledOverride !== 0) {
      writer.uint32(208).int32(message.dockJigglingEnabledOverride);
    }
    if (message.allowTakeoffFromMismatchedDockOverride !== 0) {
      writer.uint32(216).int32(message.allowTakeoffFromMismatchedDockOverride);
    }
    if (message.allowOnVehiclePhotogrammetryOverride !== 0) {
      writer.uint32(224).int32(message.allowOnVehiclePhotogrammetryOverride);
    }
    if (message.useBacktrackingRtxOverride !== 0) {
      writer.uint32(232).int32(message.useBacktrackingRtxOverride);
    }
    if (message.ridFeatureEnabledOverride !== 0) {
      writer.uint32(272).int32(message.ridFeatureEnabledOverride);
    }
    if (message.ridFeatureDisabledOverride !== 0) {
      writer.uint32(280).int32(message.ridFeatureDisabledOverride);
    }
    if (message.allowSurfaceScanAlphaFeaturesOverride !== 0) {
      writer.uint32(288).int32(message.allowSurfaceScanAlphaFeaturesOverride);
    }
    if (message.rebootMaxUptimeSecondsOverride !== 0) {
      writer.uint32(296).int32(message.rebootMaxUptimeSecondsOverride);
    }
    if (message.defaultGeofence !== undefined) {
      Geofence.encode(message.defaultGeofence, writer.uint32(306).fork()).ldelim();
    }
    if (message.battImbalanceThresholdsOverride !== undefined) {
      BatteryCellVoltageImbalanceThresholds.encode(message.battImbalanceThresholdsOverride, writer.uint32(314).fork())
        .ldelim();
    }
    if (message.surfaceScanOverride !== 0) {
      writer.uint32(320).int32(message.surfaceScanOverride);
    }
    if (message.disableObstacleAvoidanceDockLandingOverride !== 0) {
      writer.uint32(336).int32(message.disableObstacleAvoidanceDockLandingOverride);
    }
    if (message.allowPoweredPayloadsOverride !== 0) {
      writer.uint32(344).int32(message.allowPoweredPayloadsOverride);
    }
    if (message.disableCountryCodeScanOverride !== 0) {
      writer.uint32(360).int32(message.disableCountryCodeScanOverride);
    }
    if (message.allow3dCaptureScanModeOverride !== 0) {
      writer.uint32(368).int32(message.allow3dCaptureScanModeOverride);
    }
    if (message.allowSurfaceCaptureScanModeOverride !== 0) {
      writer.uint32(376).int32(message.allowSurfaceCaptureScanModeOverride);
    }
    if (message.allowMapCaptureScanModeOverride !== 0) {
      writer.uint32(384).int32(message.allowMapCaptureScanModeOverride);
    }
    if (message.allowTowerCaptureScanModeOverride !== 0) {
      writer.uint32(176).int32(message.allowTowerCaptureScanModeOverride);
    }
    if (message.allowIndoorCaptureScanModeOverride !== 0) {
      writer.uint32(160).int32(message.allowIndoorCaptureScanModeOverride);
    }
    if (message.allowIndoorCaptureScanModeV2Override !== 0) {
      writer.uint32(504).int32(message.allowIndoorCaptureScanModeV2Override);
    }
    if (message.allowOrbitCaptureScanModeOverride !== 0) {
      writer.uint32(328).int32(message.allowOrbitCaptureScanModeOverride);
    }
    if (message.allowCylinderCaptureScanModeOverride !== 0) {
      writer.uint32(392).int32(message.allowCylinderCaptureScanModeOverride);
    }
    if (message.allowSemanticTransmissionTowerScanModeOverride !== 0) {
      writer.uint32(448).int32(message.allowSemanticTransmissionTowerScanModeOverride);
    }
    if (message.fullSpinOnTakeoffOverride !== 0) {
      writer.uint32(400).int32(message.fullSpinOnTakeoffOverride);
    }
    if (message.allowIrSubjectTrackingOverride !== 0) {
      writer.uint32(408).int32(message.allowIrSubjectTrackingOverride);
    }
    if (message.enableIspPixelContentFlipOverride !== 0) {
      writer.uint32(416).int32(message.enableIspPixelContentFlipOverride);
    }
    if (message.enableVehicleRtspKlvOverride !== 0) {
      writer.uint32(432).int32(message.enableVehicleRtspKlvOverride);
    }
    if (message.gimbalInspectionModeOverride !== 0) {
      writer.uint32(440).int32(message.gimbalInspectionModeOverride);
    }
    if (message.flashDriveRequiredForFlightOverride !== 0) {
      writer.uint32(456).int32(message.flashDriveRequiredForFlightOverride);
    }
    if (message.allowCommandedElandOverride !== 0) {
      writer.uint32(464).int32(message.allowCommandedElandOverride);
    }
    if (message.allowInternalMissionActionsOverride !== 0) {
      writer.uint32(472).int32(message.allowInternalMissionActionsOverride);
    }
    if (message.increasedMaxSpeedMapCaptureOverride !== 0) {
      writer.uint32(480).int32(message.increasedMaxSpeedMapCaptureOverride);
    }
    if (message.allowMavlinkPerchOverride !== 0) {
      writer.uint32(488).int32(message.allowMavlinkPerchOverride);
    }
    if (message.forceRgbLedDisableOverride !== 0) {
      writer.uint32(496).int32(message.forceRgbLedDisableOverride);
    }
    if (message.enableHighAltitudeVioOverride !== 0) {
      writer.uint32(512).int32(message.enableHighAltitudeVioOverride);
    }
    if (message.enableAttitudeRtxOverride !== 0) {
      writer.uint32(520).int32(message.enableAttitudeRtxOverride);
    }
    if (message.enableManualWffOverride !== 0) {
      writer.uint32(528).int32(message.enableManualWffOverride);
    }
    if (message.enableJpgHighCompress3dsOverride !== 0) {
      writer.uint32(536).int32(message.enableJpgHighCompress3dsOverride);
    }
    if (message.enableGetMicrohardRfConfigOverride !== 0) {
      writer.uint32(544).int32(message.enableGetMicrohardRfConfigOverride);
    }
    if (message.enableCommandedLostCommsOverride !== 0) {
      writer.uint32(552).int32(message.enableCommandedLostCommsOverride);
    }
    if (message.enableGpsSpoofingDetectorOverride !== 0) {
      writer.uint32(560).int32(message.enableGpsSpoofingDetectorOverride);
    }
    if (message.enableRscToggle3dsOverride !== 0) {
      writer.uint32(568).int32(message.enableRscToggle3dsOverride);
    }
    if (message.enableLowTakeoffOverride !== 0) {
      writer.uint32(576).int32(message.enableLowTakeoffOverride);
    }
    if (message.enableRtkSupportOverride !== 0) {
      writer.uint32(584).int32(message.enableRtkSupportOverride);
    }
    if (message.enableFlightFileManifestTaskOverride !== 0) {
      writer.uint32(592).int32(message.enableFlightFileManifestTaskOverride);
    }
    if (message.isEuropeanUnionOverride !== 0) {
      writer.uint32(600).int32(message.isEuropeanUnionOverride);
    }
    if (message.enablePerchOverride !== 0) {
      writer.uint32(608).int32(message.enablePerchOverride);
    }
    if (message.enableThirdPartyAttachmentPrototypingOverride !== 0) {
      writer.uint32(616).int32(message.enableThirdPartyAttachmentPrototypingOverride);
    }
    if (message.alwaysLogSubjectStreamForDockFlightsOverride !== 0) {
      writer.uint32(624).int32(message.alwaysLogSubjectStreamForDockFlightsOverride);
    }
    if (message.enableForceLowTakeoffToggleOverride !== 0) {
      writer.uint32(632).int32(message.enableForceLowTakeoffToggleOverride);
    }
    if (message.enableInternalAttachmentPrototypingOverride !== 0) {
      writer.uint32(640).int32(message.enableInternalAttachmentPrototypingOverride);
    }
    if (message.enableRtkNavigation !== 0) {
      writer.uint32(648).int32(message.enableRtkNavigation);
    }
    if (message.enableMpegtsFileWritingOverride !== 0) {
      writer.uint32(656).int32(message.enableMpegtsFileWritingOverride);
    }
    if (message.mapCaptureTerrainFollowAlphaOverride !== 0) {
      writer.uint32(664).int32(message.mapCaptureTerrainFollowAlphaOverride);
    }
    if (message.enable20MpsMaxSpeedOverride !== 0) {
      writer.uint32(680).int32(message.enable20MpsMaxSpeedOverride);
    }
    if (message.enableThirdPartyRadioAttachmentOverride !== 0) {
      writer.uint32(688).int32(message.enableThirdPartyRadioAttachmentOverride);
    }
    if (message.enableSlowModeOverride !== 0) {
      writer.uint32(696).int32(message.enableSlowModeOverride);
    }
    if (message.enableParachuteExpectedButMissingOverride !== 0) {
      writer.uint32(704).int32(message.enableParachuteExpectedButMissingOverride);
    }
    if (message.allowSubjectFollowOverride !== 0) {
      writer.uint32(720).int32(message.allowSubjectFollowOverride);
    }
    if (message.allowGpsFlightWithoutHandwaveOverride !== 0) {
      writer.uint32(728).int32(message.allowGpsFlightWithoutHandwaveOverride);
    }
    if (message.geofenceOffOverride !== 0) {
      writer.uint32(736).int32(message.geofenceOffOverride);
    }
    if (message.enableMissionsCloudToControllerOverride !== 0) {
      writer.uint32(744).int32(message.enableMissionsCloudToControllerOverride);
    }
    if (message.enableVisibleNightsenseStrobingOverride !== 0) {
      writer.uint32(752).int32(message.enableVisibleNightsenseStrobingOverride);
    }
    if (message.enableIrNightsenseStrobingOverride !== 0) {
      writer.uint32(760).int32(message.enableIrNightsenseStrobingOverride);
    }
    if (message.enableEnhancedSlBitrateOverride !== 0) {
      writer.uint32(768).int32(message.enableEnhancedSlBitrateOverride);
    }
    if (message.enableThirdPartyAttachmentConfigsOverride !== 0) {
      writer.uint32(776).int32(message.enableThirdPartyAttachmentConfigsOverride);
    }
    if (message.enable20MpsMaxSpeedWaypointsOnlyOverride !== 0) {
      writer.uint32(784).int32(message.enable20MpsMaxSpeedWaypointsOnlyOverride);
    }
    if (message.enableDcsModeSelectionOverride !== 0) {
      writer.uint32(792).int32(message.enableDcsModeSelectionOverride);
    }
    if (message.skipValidateCountrySettingsOverride !== 0) {
      writer.uint32(800).int32(message.skipValidateCountrySettingsOverride);
    }
    if (message.enableOnDemandMissionTierOverride !== 0) {
      writer.uint32(808).int32(message.enableOnDemandMissionTierOverride);
    }
    if (message.enableIcingDetectionOverride !== 0) {
      writer.uint32(816).int32(message.enableIcingDetectionOverride);
    }
    if (message.enableFepPowerLimitNotificationOverride !== 0) {
      writer.uint32(824).int32(message.enableFepPowerLimitNotificationOverride);
    }
    if (message.enableBatteryLightDisciplineOverride !== 0) {
      writer.uint32(832).int32(message.enableBatteryLightDisciplineOverride);
    }
    if (message.enableSubjectFollowWithoutOaOverride !== 0) {
      writer.uint32(840).int32(message.enableSubjectFollowWithoutOaOverride);
    }
    if (message.enableLsopOverride !== 0) {
      writer.uint32(848).int32(message.enableLsopOverride);
    }
    if (message.enable30MpsMaxGroundSpeedOverride !== 0) {
      writer.uint32(856).int32(message.enable30MpsMaxGroundSpeedOverride);
    }
    if (message.enableMobileInspectionAssetsOverride !== 0) {
      writer.uint32(864).int32(message.enableMobileInspectionAssetsOverride);
    }
    if (message.enableGeoBasedDtedIngestOverride !== 0) {
      writer.uint32(872).int32(message.enableGeoBasedDtedIngestOverride);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): VehicleSettingsOverrides {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseVehicleSettingsOverrides();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          if (tag !== 16) {
            break;
          }

          message.heightCeilingOverride = reader.int32() as any;
          continue;
        case 3:
          if (tag === 24) {
            message.allowedIlluminationModes.push(reader.int32() as any);

            continue;
          }

          if (tag === 26) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.allowedIlluminationModes.push(reader.int32() as any);
            }

            continue;
          }

          break;
        case 4:
          if (tag === 32) {
            message.allowedObstacleMarginModes.push(reader.int32() as any);

            continue;
          }

          if (tag === 34) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.allowedObstacleMarginModes.push(reader.int32() as any);
            }

            continue;
          }

          break;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.returnWithVisionOnlyOverride = reader.int32() as any;
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.superzoomOverride = reader.int32() as any;
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.digitalZoomOverride = reader.int32() as any;
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.precisionFlightModeOverride = reader.int32() as any;
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.skydiolinkAes256Override = reader.int32() as any;
          continue;
        case 9:
          if (tag === 72) {
            message.allowedNavigationModes.push(reader.int32() as any);

            continue;
          }

          if (tag === 74) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.allowedNavigationModes.push(reader.int32() as any);
            }

            continue;
          }

          break;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.ignoreLowBatteryBehaviorOverride = reader.int32() as any;
          continue;
        case 12:
          if (tag !== 96) {
            break;
          }

          message.disableMediaEncryptionOverride = reader.int32() as any;
          continue;
        case 89:
          if (tag !== 712) {
            break;
          }

          message.disableMediaEncryptionOverrideV2 = reader.int32() as any;
          continue;
        case 13:
          if (tag !== 104) {
            break;
          }

          message.offlineOverride = reader.int32() as any;
          continue;
        case 14:
          if (tag !== 112) {
            break;
          }

          message.rgbLedDisableOverride = reader.int32() as any;
          continue;
        case 15:
          if (tag !== 120) {
            break;
          }

          message.sdcardOverwriteOverride = reader.int32() as any;
          continue;
        case 16:
          if (tag !== 128) {
            break;
          }

          message.flightHistoryOverride = reader.int32() as any;
          continue;
        case 18:
          if (tag !== 144) {
            break;
          }

          message.multiResPreviewEnableOverride = reader.int32() as any;
          continue;
        case 19:
          if (tag !== 152) {
            break;
          }

          message.centralizedFrbRateEnableOverride = reader.int32() as any;
          continue;
        case 21:
          if (tag !== 168) {
            break;
          }

          message.alignedTelemetryCsvOverride = reader.int32() as any;
          continue;
        case 23:
          if (tag !== 184) {
            break;
          }

          message.displayHandWaveCalibrationOverride = reader.int32() as any;
          continue;
        case 25:
          if (tag !== 200) {
            break;
          }

          message.variableObstacleAvoidanceOverride = reader.int32() as any;
          continue;
        case 26:
          if (tag !== 208) {
            break;
          }

          message.dockJigglingEnabledOverride = reader.int32() as any;
          continue;
        case 27:
          if (tag !== 216) {
            break;
          }

          message.allowTakeoffFromMismatchedDockOverride = reader.int32() as any;
          continue;
        case 28:
          if (tag !== 224) {
            break;
          }

          message.allowOnVehiclePhotogrammetryOverride = reader.int32() as any;
          continue;
        case 29:
          if (tag !== 232) {
            break;
          }

          message.useBacktrackingRtxOverride = reader.int32() as any;
          continue;
        case 34:
          if (tag !== 272) {
            break;
          }

          message.ridFeatureEnabledOverride = reader.int32() as any;
          continue;
        case 35:
          if (tag !== 280) {
            break;
          }

          message.ridFeatureDisabledOverride = reader.int32() as any;
          continue;
        case 36:
          if (tag !== 288) {
            break;
          }

          message.allowSurfaceScanAlphaFeaturesOverride = reader.int32() as any;
          continue;
        case 37:
          if (tag !== 296) {
            break;
          }

          message.rebootMaxUptimeSecondsOverride = reader.int32();
          continue;
        case 38:
          if (tag !== 306) {
            break;
          }

          message.defaultGeofence = Geofence.decode(reader, reader.uint32());
          continue;
        case 39:
          if (tag !== 314) {
            break;
          }

          message.battImbalanceThresholdsOverride = BatteryCellVoltageImbalanceThresholds.decode(
            reader,
            reader.uint32(),
          );
          continue;
        case 40:
          if (tag !== 320) {
            break;
          }

          message.surfaceScanOverride = reader.int32() as any;
          continue;
        case 42:
          if (tag !== 336) {
            break;
          }

          message.disableObstacleAvoidanceDockLandingOverride = reader.int32() as any;
          continue;
        case 43:
          if (tag !== 344) {
            break;
          }

          message.allowPoweredPayloadsOverride = reader.int32() as any;
          continue;
        case 45:
          if (tag !== 360) {
            break;
          }

          message.disableCountryCodeScanOverride = reader.int32() as any;
          continue;
        case 46:
          if (tag !== 368) {
            break;
          }

          message.allow3dCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 47:
          if (tag !== 376) {
            break;
          }

          message.allowSurfaceCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 48:
          if (tag !== 384) {
            break;
          }

          message.allowMapCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 22:
          if (tag !== 176) {
            break;
          }

          message.allowTowerCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 20:
          if (tag !== 160) {
            break;
          }

          message.allowIndoorCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 63:
          if (tag !== 504) {
            break;
          }

          message.allowIndoorCaptureScanModeV2Override = reader.int32() as any;
          continue;
        case 41:
          if (tag !== 328) {
            break;
          }

          message.allowOrbitCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 49:
          if (tag !== 392) {
            break;
          }

          message.allowCylinderCaptureScanModeOverride = reader.int32() as any;
          continue;
        case 56:
          if (tag !== 448) {
            break;
          }

          message.allowSemanticTransmissionTowerScanModeOverride = reader.int32() as any;
          continue;
        case 50:
          if (tag !== 400) {
            break;
          }

          message.fullSpinOnTakeoffOverride = reader.int32() as any;
          continue;
        case 51:
          if (tag !== 408) {
            break;
          }

          message.allowIrSubjectTrackingOverride = reader.int32() as any;
          continue;
        case 52:
          if (tag !== 416) {
            break;
          }

          message.enableIspPixelContentFlipOverride = reader.int32() as any;
          continue;
        case 54:
          if (tag !== 432) {
            break;
          }

          message.enableVehicleRtspKlvOverride = reader.int32() as any;
          continue;
        case 55:
          if (tag !== 440) {
            break;
          }

          message.gimbalInspectionModeOverride = reader.int32() as any;
          continue;
        case 57:
          if (tag !== 456) {
            break;
          }

          message.flashDriveRequiredForFlightOverride = reader.int32() as any;
          continue;
        case 58:
          if (tag !== 464) {
            break;
          }

          message.allowCommandedElandOverride = reader.int32() as any;
          continue;
        case 59:
          if (tag !== 472) {
            break;
          }

          message.allowInternalMissionActionsOverride = reader.int32() as any;
          continue;
        case 60:
          if (tag !== 480) {
            break;
          }

          message.increasedMaxSpeedMapCaptureOverride = reader.int32() as any;
          continue;
        case 61:
          if (tag !== 488) {
            break;
          }

          message.allowMavlinkPerchOverride = reader.int32() as any;
          continue;
        case 62:
          if (tag !== 496) {
            break;
          }

          message.forceRgbLedDisableOverride = reader.int32() as any;
          continue;
        case 64:
          if (tag !== 512) {
            break;
          }

          message.enableHighAltitudeVioOverride = reader.int32() as any;
          continue;
        case 65:
          if (tag !== 520) {
            break;
          }

          message.enableAttitudeRtxOverride = reader.int32() as any;
          continue;
        case 66:
          if (tag !== 528) {
            break;
          }

          message.enableManualWffOverride = reader.int32() as any;
          continue;
        case 67:
          if (tag !== 536) {
            break;
          }

          message.enableJpgHighCompress3dsOverride = reader.int32() as any;
          continue;
        case 68:
          if (tag !== 544) {
            break;
          }

          message.enableGetMicrohardRfConfigOverride = reader.int32() as any;
          continue;
        case 69:
          if (tag !== 552) {
            break;
          }

          message.enableCommandedLostCommsOverride = reader.int32() as any;
          continue;
        case 70:
          if (tag !== 560) {
            break;
          }

          message.enableGpsSpoofingDetectorOverride = reader.int32() as any;
          continue;
        case 71:
          if (tag !== 568) {
            break;
          }

          message.enableRscToggle3dsOverride = reader.int32() as any;
          continue;
        case 72:
          if (tag !== 576) {
            break;
          }

          message.enableLowTakeoffOverride = reader.int32() as any;
          continue;
        case 73:
          if (tag !== 584) {
            break;
          }

          message.enableRtkSupportOverride = reader.int32() as any;
          continue;
        case 74:
          if (tag !== 592) {
            break;
          }

          message.enableFlightFileManifestTaskOverride = reader.int32() as any;
          continue;
        case 75:
          if (tag !== 600) {
            break;
          }

          message.isEuropeanUnionOverride = reader.int32() as any;
          continue;
        case 76:
          if (tag !== 608) {
            break;
          }

          message.enablePerchOverride = reader.int32() as any;
          continue;
        case 77:
          if (tag !== 616) {
            break;
          }

          message.enableThirdPartyAttachmentPrototypingOverride = reader.int32() as any;
          continue;
        case 78:
          if (tag !== 624) {
            break;
          }

          message.alwaysLogSubjectStreamForDockFlightsOverride = reader.int32() as any;
          continue;
        case 79:
          if (tag !== 632) {
            break;
          }

          message.enableForceLowTakeoffToggleOverride = reader.int32() as any;
          continue;
        case 80:
          if (tag !== 640) {
            break;
          }

          message.enableInternalAttachmentPrototypingOverride = reader.int32() as any;
          continue;
        case 81:
          if (tag !== 648) {
            break;
          }

          message.enableRtkNavigation = reader.int32() as any;
          continue;
        case 82:
          if (tag !== 656) {
            break;
          }

          message.enableMpegtsFileWritingOverride = reader.int32() as any;
          continue;
        case 83:
          if (tag !== 664) {
            break;
          }

          message.mapCaptureTerrainFollowAlphaOverride = reader.int32() as any;
          continue;
        case 85:
          if (tag !== 680) {
            break;
          }

          message.enable20MpsMaxSpeedOverride = reader.int32() as any;
          continue;
        case 86:
          if (tag !== 688) {
            break;
          }

          message.enableThirdPartyRadioAttachmentOverride = reader.int32() as any;
          continue;
        case 87:
          if (tag !== 696) {
            break;
          }

          message.enableSlowModeOverride = reader.int32() as any;
          continue;
        case 88:
          if (tag !== 704) {
            break;
          }

          message.enableParachuteExpectedButMissingOverride = reader.int32() as any;
          continue;
        case 90:
          if (tag !== 720) {
            break;
          }

          message.allowSubjectFollowOverride = reader.int32() as any;
          continue;
        case 91:
          if (tag !== 728) {
            break;
          }

          message.allowGpsFlightWithoutHandwaveOverride = reader.int32() as any;
          continue;
        case 92:
          if (tag !== 736) {
            break;
          }

          message.geofenceOffOverride = reader.int32() as any;
          continue;
        case 93:
          if (tag !== 744) {
            break;
          }

          message.enableMissionsCloudToControllerOverride = reader.int32() as any;
          continue;
        case 94:
          if (tag !== 752) {
            break;
          }

          message.enableVisibleNightsenseStrobingOverride = reader.int32() as any;
          continue;
        case 95:
          if (tag !== 760) {
            break;
          }

          message.enableIrNightsenseStrobingOverride = reader.int32() as any;
          continue;
        case 96:
          if (tag !== 768) {
            break;
          }

          message.enableEnhancedSlBitrateOverride = reader.int32() as any;
          continue;
        case 97:
          if (tag !== 776) {
            break;
          }

          message.enableThirdPartyAttachmentConfigsOverride = reader.int32() as any;
          continue;
        case 98:
          if (tag !== 784) {
            break;
          }

          message.enable20MpsMaxSpeedWaypointsOnlyOverride = reader.int32() as any;
          continue;
        case 99:
          if (tag !== 792) {
            break;
          }

          message.enableDcsModeSelectionOverride = reader.int32() as any;
          continue;
        case 100:
          if (tag !== 800) {
            break;
          }

          message.skipValidateCountrySettingsOverride = reader.int32() as any;
          continue;
        case 101:
          if (tag !== 808) {
            break;
          }

          message.enableOnDemandMissionTierOverride = reader.int32() as any;
          continue;
        case 102:
          if (tag !== 816) {
            break;
          }

          message.enableIcingDetectionOverride = reader.int32() as any;
          continue;
        case 103:
          if (tag !== 824) {
            break;
          }

          message.enableFepPowerLimitNotificationOverride = reader.int32() as any;
          continue;
        case 104:
          if (tag !== 832) {
            break;
          }

          message.enableBatteryLightDisciplineOverride = reader.int32() as any;
          continue;
        case 105:
          if (tag !== 840) {
            break;
          }

          message.enableSubjectFollowWithoutOaOverride = reader.int32() as any;
          continue;
        case 106:
          if (tag !== 848) {
            break;
          }

          message.enableLsopOverride = reader.int32() as any;
          continue;
        case 107:
          if (tag !== 856) {
            break;
          }

          message.enable30MpsMaxGroundSpeedOverride = reader.int32() as any;
          continue;
        case 108:
          if (tag !== 864) {
            break;
          }

          message.enableMobileInspectionAssetsOverride = reader.int32() as any;
          continue;
        case 109:
          if (tag !== 872) {
            break;
          }

          message.enableGeoBasedDtedIngestOverride = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): VehicleSettingsOverrides {
    return {
      heightCeilingOverride: isSet(object.heightCeilingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.heightCeilingOverride)
        : 0,
      allowedIlluminationModes: globalThis.Array.isArray(object?.allowedIlluminationModes)
        ? object.allowedIlluminationModes.map((e: any) => vehicleSettings_IlluminationModeFromJSON(e))
        : [],
      allowedObstacleMarginModes: globalThis.Array.isArray(object?.allowedObstacleMarginModes)
        ? object.allowedObstacleMarginModes.map((e: any) => vehicleSettings_ObstacleMarginModeFromJSON(e))
        : [],
      returnWithVisionOnlyOverride: isSet(object.returnWithVisionOnlyOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.returnWithVisionOnlyOverride)
        : 0,
      superzoomOverride: isSet(object.superzoomOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.superzoomOverride)
        : 0,
      digitalZoomOverride: isSet(object.digitalZoomOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.digitalZoomOverride)
        : 0,
      precisionFlightModeOverride: isSet(object.precisionFlightModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.precisionFlightModeOverride)
        : 0,
      skydiolinkAes256Override: isSet(object.skydiolinkAes256Override)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.skydiolinkAes256Override)
        : 0,
      allowedNavigationModes: globalThis.Array.isArray(object?.allowedNavigationModes)
        ? object.allowedNavigationModes.map((e: any) => navigationMode_ModeFromJSON(e))
        : [],
      ignoreLowBatteryBehaviorOverride: isSet(object.ignoreLowBatteryBehaviorOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.ignoreLowBatteryBehaviorOverride)
        : 0,
      disableMediaEncryptionOverride: isSet(object.disableMediaEncryptionOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.disableMediaEncryptionOverride)
        : 0,
      disableMediaEncryptionOverrideV2: isSet(object.disableMediaEncryptionOverrideV2)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.disableMediaEncryptionOverrideV2)
        : 0,
      offlineOverride: isSet(object.offlineOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.offlineOverride)
        : 0,
      rgbLedDisableOverride: isSet(object.rgbLedDisableOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.rgbLedDisableOverride)
        : 0,
      sdcardOverwriteOverride: isSet(object.sdcardOverwriteOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.sdcardOverwriteOverride)
        : 0,
      flightHistoryOverride: isSet(object.flightHistoryOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.flightHistoryOverride)
        : 0,
      multiResPreviewEnableOverride: isSet(object.multiResPreviewEnableOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.multiResPreviewEnableOverride)
        : 0,
      centralizedFrbRateEnableOverride: isSet(object.centralizedFrbRateEnableOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.centralizedFrbRateEnableOverride)
        : 0,
      alignedTelemetryCsvOverride: isSet(object.alignedTelemetryCsvOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.alignedTelemetryCsvOverride)
        : 0,
      displayHandWaveCalibrationOverride: isSet(object.displayHandWaveCalibrationOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.displayHandWaveCalibrationOverride)
        : 0,
      variableObstacleAvoidanceOverride: isSet(object.variableObstacleAvoidanceOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.variableObstacleAvoidanceOverride)
        : 0,
      dockJigglingEnabledOverride: isSet(object.dockJigglingEnabledOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.dockJigglingEnabledOverride)
        : 0,
      allowTakeoffFromMismatchedDockOverride: isSet(object.allowTakeoffFromMismatchedDockOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowTakeoffFromMismatchedDockOverride)
        : 0,
      allowOnVehiclePhotogrammetryOverride: isSet(object.allowOnVehiclePhotogrammetryOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowOnVehiclePhotogrammetryOverride)
        : 0,
      useBacktrackingRtxOverride: isSet(object.useBacktrackingRtxOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.useBacktrackingRtxOverride)
        : 0,
      ridFeatureEnabledOverride: isSet(object.ridFeatureEnabledOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.ridFeatureEnabledOverride)
        : 0,
      ridFeatureDisabledOverride: isSet(object.ridFeatureDisabledOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.ridFeatureDisabledOverride)
        : 0,
      allowSurfaceScanAlphaFeaturesOverride: isSet(object.allowSurfaceScanAlphaFeaturesOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowSurfaceScanAlphaFeaturesOverride)
        : 0,
      rebootMaxUptimeSecondsOverride: isSet(object.rebootMaxUptimeSecondsOverride)
        ? globalThis.Number(object.rebootMaxUptimeSecondsOverride)
        : 0,
      defaultGeofence: isSet(object.defaultGeofence) ? Geofence.fromJSON(object.defaultGeofence) : undefined,
      battImbalanceThresholdsOverride: isSet(object.battImbalanceThresholdsOverride)
        ? BatteryCellVoltageImbalanceThresholds.fromJSON(object.battImbalanceThresholdsOverride)
        : undefined,
      surfaceScanOverride: isSet(object.surfaceScanOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.surfaceScanOverride)
        : 0,
      disableObstacleAvoidanceDockLandingOverride: isSet(object.disableObstacleAvoidanceDockLandingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.disableObstacleAvoidanceDockLandingOverride)
        : 0,
      allowPoweredPayloadsOverride: isSet(object.allowPoweredPayloadsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowPoweredPayloadsOverride)
        : 0,
      disableCountryCodeScanOverride: isSet(object.disableCountryCodeScanOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.disableCountryCodeScanOverride)
        : 0,
      allow3dCaptureScanModeOverride: isSet(object.allow3dCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allow3dCaptureScanModeOverride)
        : 0,
      allowSurfaceCaptureScanModeOverride: isSet(object.allowSurfaceCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowSurfaceCaptureScanModeOverride)
        : 0,
      allowMapCaptureScanModeOverride: isSet(object.allowMapCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowMapCaptureScanModeOverride)
        : 0,
      allowTowerCaptureScanModeOverride: isSet(object.allowTowerCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowTowerCaptureScanModeOverride)
        : 0,
      allowIndoorCaptureScanModeOverride: isSet(object.allowIndoorCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowIndoorCaptureScanModeOverride)
        : 0,
      allowIndoorCaptureScanModeV2Override: isSet(object.allowIndoorCaptureScanModeV2Override)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowIndoorCaptureScanModeV2Override)
        : 0,
      allowOrbitCaptureScanModeOverride: isSet(object.allowOrbitCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowOrbitCaptureScanModeOverride)
        : 0,
      allowCylinderCaptureScanModeOverride: isSet(object.allowCylinderCaptureScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowCylinderCaptureScanModeOverride)
        : 0,
      allowSemanticTransmissionTowerScanModeOverride: isSet(object.allowSemanticTransmissionTowerScanModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowSemanticTransmissionTowerScanModeOverride)
        : 0,
      fullSpinOnTakeoffOverride: isSet(object.fullSpinOnTakeoffOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.fullSpinOnTakeoffOverride)
        : 0,
      allowIrSubjectTrackingOverride: isSet(object.allowIrSubjectTrackingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowIrSubjectTrackingOverride)
        : 0,
      enableIspPixelContentFlipOverride: isSet(object.enableIspPixelContentFlipOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableIspPixelContentFlipOverride)
        : 0,
      enableVehicleRtspKlvOverride: isSet(object.enableVehicleRtspKlvOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableVehicleRtspKlvOverride)
        : 0,
      gimbalInspectionModeOverride: isSet(object.gimbalInspectionModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.gimbalInspectionModeOverride)
        : 0,
      flashDriveRequiredForFlightOverride: isSet(object.flashDriveRequiredForFlightOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.flashDriveRequiredForFlightOverride)
        : 0,
      allowCommandedElandOverride: isSet(object.allowCommandedElandOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowCommandedElandOverride)
        : 0,
      allowInternalMissionActionsOverride: isSet(object.allowInternalMissionActionsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowInternalMissionActionsOverride)
        : 0,
      increasedMaxSpeedMapCaptureOverride: isSet(object.increasedMaxSpeedMapCaptureOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.increasedMaxSpeedMapCaptureOverride)
        : 0,
      allowMavlinkPerchOverride: isSet(object.allowMavlinkPerchOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowMavlinkPerchOverride)
        : 0,
      forceRgbLedDisableOverride: isSet(object.forceRgbLedDisableOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.forceRgbLedDisableOverride)
        : 0,
      enableHighAltitudeVioOverride: isSet(object.enableHighAltitudeVioOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableHighAltitudeVioOverride)
        : 0,
      enableAttitudeRtxOverride: isSet(object.enableAttitudeRtxOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableAttitudeRtxOverride)
        : 0,
      enableManualWffOverride: isSet(object.enableManualWffOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableManualWffOverride)
        : 0,
      enableJpgHighCompress3dsOverride: isSet(object.enableJpgHighCompress3dsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableJpgHighCompress3dsOverride)
        : 0,
      enableGetMicrohardRfConfigOverride: isSet(object.enableGetMicrohardRfConfigOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableGetMicrohardRfConfigOverride)
        : 0,
      enableCommandedLostCommsOverride: isSet(object.enableCommandedLostCommsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableCommandedLostCommsOverride)
        : 0,
      enableGpsSpoofingDetectorOverride: isSet(object.enableGpsSpoofingDetectorOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableGpsSpoofingDetectorOverride)
        : 0,
      enableRscToggle3dsOverride: isSet(object.enableRscToggle3dsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableRscToggle3dsOverride)
        : 0,
      enableLowTakeoffOverride: isSet(object.enableLowTakeoffOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableLowTakeoffOverride)
        : 0,
      enableRtkSupportOverride: isSet(object.enableRtkSupportOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableRtkSupportOverride)
        : 0,
      enableFlightFileManifestTaskOverride: isSet(object.enableFlightFileManifestTaskOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableFlightFileManifestTaskOverride)
        : 0,
      isEuropeanUnionOverride: isSet(object.isEuropeanUnionOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.isEuropeanUnionOverride)
        : 0,
      enablePerchOverride: isSet(object.enablePerchOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enablePerchOverride)
        : 0,
      enableThirdPartyAttachmentPrototypingOverride: isSet(object.enableThirdPartyAttachmentPrototypingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableThirdPartyAttachmentPrototypingOverride)
        : 0,
      alwaysLogSubjectStreamForDockFlightsOverride: isSet(object.alwaysLogSubjectStreamForDockFlightsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.alwaysLogSubjectStreamForDockFlightsOverride)
        : 0,
      enableForceLowTakeoffToggleOverride: isSet(object.enableForceLowTakeoffToggleOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableForceLowTakeoffToggleOverride)
        : 0,
      enableInternalAttachmentPrototypingOverride: isSet(object.enableInternalAttachmentPrototypingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableInternalAttachmentPrototypingOverride)
        : 0,
      enableRtkNavigation: isSet(object.enableRtkNavigation)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableRtkNavigation)
        : 0,
      enableMpegtsFileWritingOverride: isSet(object.enableMpegtsFileWritingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableMpegtsFileWritingOverride)
        : 0,
      mapCaptureTerrainFollowAlphaOverride: isSet(object.mapCaptureTerrainFollowAlphaOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.mapCaptureTerrainFollowAlphaOverride)
        : 0,
      enable20MpsMaxSpeedOverride: isSet(object.enable20MpsMaxSpeedOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enable20MpsMaxSpeedOverride)
        : 0,
      enableThirdPartyRadioAttachmentOverride: isSet(object.enableThirdPartyRadioAttachmentOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableThirdPartyRadioAttachmentOverride)
        : 0,
      enableSlowModeOverride: isSet(object.enableSlowModeOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableSlowModeOverride)
        : 0,
      enableParachuteExpectedButMissingOverride: isSet(object.enableParachuteExpectedButMissingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableParachuteExpectedButMissingOverride)
        : 0,
      allowSubjectFollowOverride: isSet(object.allowSubjectFollowOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowSubjectFollowOverride)
        : 0,
      allowGpsFlightWithoutHandwaveOverride: isSet(object.allowGpsFlightWithoutHandwaveOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.allowGpsFlightWithoutHandwaveOverride)
        : 0,
      geofenceOffOverride: isSet(object.geofenceOffOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.geofenceOffOverride)
        : 0,
      enableMissionsCloudToControllerOverride: isSet(object.enableMissionsCloudToControllerOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableMissionsCloudToControllerOverride)
        : 0,
      enableVisibleNightsenseStrobingOverride: isSet(object.enableVisibleNightsenseStrobingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableVisibleNightsenseStrobingOverride)
        : 0,
      enableIrNightsenseStrobingOverride: isSet(object.enableIrNightsenseStrobingOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableIrNightsenseStrobingOverride)
        : 0,
      enableEnhancedSlBitrateOverride: isSet(object.enableEnhancedSlBitrateOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableEnhancedSlBitrateOverride)
        : 0,
      enableThirdPartyAttachmentConfigsOverride: isSet(object.enableThirdPartyAttachmentConfigsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableThirdPartyAttachmentConfigsOverride)
        : 0,
      enable20MpsMaxSpeedWaypointsOnlyOverride: isSet(object.enable20MpsMaxSpeedWaypointsOnlyOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enable20MpsMaxSpeedWaypointsOnlyOverride)
        : 0,
      enableDcsModeSelectionOverride: isSet(object.enableDcsModeSelectionOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableDcsModeSelectionOverride)
        : 0,
      skipValidateCountrySettingsOverride: isSet(object.skipValidateCountrySettingsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.skipValidateCountrySettingsOverride)
        : 0,
      enableOnDemandMissionTierOverride: isSet(object.enableOnDemandMissionTierOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableOnDemandMissionTierOverride)
        : 0,
      enableIcingDetectionOverride: isSet(object.enableIcingDetectionOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableIcingDetectionOverride)
        : 0,
      enableFepPowerLimitNotificationOverride: isSet(object.enableFepPowerLimitNotificationOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableFepPowerLimitNotificationOverride)
        : 0,
      enableBatteryLightDisciplineOverride: isSet(object.enableBatteryLightDisciplineOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableBatteryLightDisciplineOverride)
        : 0,
      enableSubjectFollowWithoutOaOverride: isSet(object.enableSubjectFollowWithoutOaOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableSubjectFollowWithoutOaOverride)
        : 0,
      enableLsopOverride: isSet(object.enableLsopOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableLsopOverride)
        : 0,
      enable30MpsMaxGroundSpeedOverride: isSet(object.enable30MpsMaxGroundSpeedOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enable30MpsMaxGroundSpeedOverride)
        : 0,
      enableMobileInspectionAssetsOverride: isSet(object.enableMobileInspectionAssetsOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableMobileInspectionAssetsOverride)
        : 0,
      enableGeoBasedDtedIngestOverride: isSet(object.enableGeoBasedDtedIngestOverride)
        ? vehicleSettingsOverrides_BooleanOverrideFromJSON(object.enableGeoBasedDtedIngestOverride)
        : 0,
    };
  },

  toJSON(message: VehicleSettingsOverrides): unknown {
    const obj: any = {};
    if (message.heightCeilingOverride !== 0) {
      obj.heightCeilingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.heightCeilingOverride);
    }
    if (message.allowedIlluminationModes?.length) {
      obj.allowedIlluminationModes = message.allowedIlluminationModes.map((e) =>
        vehicleSettings_IlluminationModeToJSON(e)
      );
    }
    if (message.allowedObstacleMarginModes?.length) {
      obj.allowedObstacleMarginModes = message.allowedObstacleMarginModes.map((e) =>
        vehicleSettings_ObstacleMarginModeToJSON(e)
      );
    }
    if (message.returnWithVisionOnlyOverride !== 0) {
      obj.returnWithVisionOnlyOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.returnWithVisionOnlyOverride,
      );
    }
    if (message.superzoomOverride !== 0) {
      obj.superzoomOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.superzoomOverride);
    }
    if (message.digitalZoomOverride !== 0) {
      obj.digitalZoomOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.digitalZoomOverride);
    }
    if (message.precisionFlightModeOverride !== 0) {
      obj.precisionFlightModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.precisionFlightModeOverride,
      );
    }
    if (message.skydiolinkAes256Override !== 0) {
      obj.skydiolinkAes256Override = vehicleSettingsOverrides_BooleanOverrideToJSON(message.skydiolinkAes256Override);
    }
    if (message.allowedNavigationModes?.length) {
      obj.allowedNavigationModes = message.allowedNavigationModes.map((e) => navigationMode_ModeToJSON(e));
    }
    if (message.ignoreLowBatteryBehaviorOverride !== 0) {
      obj.ignoreLowBatteryBehaviorOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.ignoreLowBatteryBehaviorOverride,
      );
    }
    if (message.disableMediaEncryptionOverride !== 0) {
      obj.disableMediaEncryptionOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.disableMediaEncryptionOverride,
      );
    }
    if (message.disableMediaEncryptionOverrideV2 !== 0) {
      obj.disableMediaEncryptionOverrideV2 = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.disableMediaEncryptionOverrideV2,
      );
    }
    if (message.offlineOverride !== 0) {
      obj.offlineOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.offlineOverride);
    }
    if (message.rgbLedDisableOverride !== 0) {
      obj.rgbLedDisableOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.rgbLedDisableOverride);
    }
    if (message.sdcardOverwriteOverride !== 0) {
      obj.sdcardOverwriteOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.sdcardOverwriteOverride);
    }
    if (message.flightHistoryOverride !== 0) {
      obj.flightHistoryOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.flightHistoryOverride);
    }
    if (message.multiResPreviewEnableOverride !== 0) {
      obj.multiResPreviewEnableOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.multiResPreviewEnableOverride,
      );
    }
    if (message.centralizedFrbRateEnableOverride !== 0) {
      obj.centralizedFrbRateEnableOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.centralizedFrbRateEnableOverride,
      );
    }
    if (message.alignedTelemetryCsvOverride !== 0) {
      obj.alignedTelemetryCsvOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.alignedTelemetryCsvOverride,
      );
    }
    if (message.displayHandWaveCalibrationOverride !== 0) {
      obj.displayHandWaveCalibrationOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.displayHandWaveCalibrationOverride,
      );
    }
    if (message.variableObstacleAvoidanceOverride !== 0) {
      obj.variableObstacleAvoidanceOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.variableObstacleAvoidanceOverride,
      );
    }
    if (message.dockJigglingEnabledOverride !== 0) {
      obj.dockJigglingEnabledOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.dockJigglingEnabledOverride,
      );
    }
    if (message.allowTakeoffFromMismatchedDockOverride !== 0) {
      obj.allowTakeoffFromMismatchedDockOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowTakeoffFromMismatchedDockOverride,
      );
    }
    if (message.allowOnVehiclePhotogrammetryOverride !== 0) {
      obj.allowOnVehiclePhotogrammetryOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowOnVehiclePhotogrammetryOverride,
      );
    }
    if (message.useBacktrackingRtxOverride !== 0) {
      obj.useBacktrackingRtxOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.useBacktrackingRtxOverride,
      );
    }
    if (message.ridFeatureEnabledOverride !== 0) {
      obj.ridFeatureEnabledOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.ridFeatureEnabledOverride);
    }
    if (message.ridFeatureDisabledOverride !== 0) {
      obj.ridFeatureDisabledOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.ridFeatureDisabledOverride,
      );
    }
    if (message.allowSurfaceScanAlphaFeaturesOverride !== 0) {
      obj.allowSurfaceScanAlphaFeaturesOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowSurfaceScanAlphaFeaturesOverride,
      );
    }
    if (message.rebootMaxUptimeSecondsOverride !== 0) {
      obj.rebootMaxUptimeSecondsOverride = Math.round(message.rebootMaxUptimeSecondsOverride);
    }
    if (message.defaultGeofence !== undefined) {
      obj.defaultGeofence = Geofence.toJSON(message.defaultGeofence);
    }
    if (message.battImbalanceThresholdsOverride !== undefined) {
      obj.battImbalanceThresholdsOverride = BatteryCellVoltageImbalanceThresholds.toJSON(
        message.battImbalanceThresholdsOverride,
      );
    }
    if (message.surfaceScanOverride !== 0) {
      obj.surfaceScanOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.surfaceScanOverride);
    }
    if (message.disableObstacleAvoidanceDockLandingOverride !== 0) {
      obj.disableObstacleAvoidanceDockLandingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.disableObstacleAvoidanceDockLandingOverride,
      );
    }
    if (message.allowPoweredPayloadsOverride !== 0) {
      obj.allowPoweredPayloadsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowPoweredPayloadsOverride,
      );
    }
    if (message.disableCountryCodeScanOverride !== 0) {
      obj.disableCountryCodeScanOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.disableCountryCodeScanOverride,
      );
    }
    if (message.allow3dCaptureScanModeOverride !== 0) {
      obj.allow3dCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allow3dCaptureScanModeOverride,
      );
    }
    if (message.allowSurfaceCaptureScanModeOverride !== 0) {
      obj.allowSurfaceCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowSurfaceCaptureScanModeOverride,
      );
    }
    if (message.allowMapCaptureScanModeOverride !== 0) {
      obj.allowMapCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowMapCaptureScanModeOverride,
      );
    }
    if (message.allowTowerCaptureScanModeOverride !== 0) {
      obj.allowTowerCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowTowerCaptureScanModeOverride,
      );
    }
    if (message.allowIndoorCaptureScanModeOverride !== 0) {
      obj.allowIndoorCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowIndoorCaptureScanModeOverride,
      );
    }
    if (message.allowIndoorCaptureScanModeV2Override !== 0) {
      obj.allowIndoorCaptureScanModeV2Override = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowIndoorCaptureScanModeV2Override,
      );
    }
    if (message.allowOrbitCaptureScanModeOverride !== 0) {
      obj.allowOrbitCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowOrbitCaptureScanModeOverride,
      );
    }
    if (message.allowCylinderCaptureScanModeOverride !== 0) {
      obj.allowCylinderCaptureScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowCylinderCaptureScanModeOverride,
      );
    }
    if (message.allowSemanticTransmissionTowerScanModeOverride !== 0) {
      obj.allowSemanticTransmissionTowerScanModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowSemanticTransmissionTowerScanModeOverride,
      );
    }
    if (message.fullSpinOnTakeoffOverride !== 0) {
      obj.fullSpinOnTakeoffOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.fullSpinOnTakeoffOverride);
    }
    if (message.allowIrSubjectTrackingOverride !== 0) {
      obj.allowIrSubjectTrackingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowIrSubjectTrackingOverride,
      );
    }
    if (message.enableIspPixelContentFlipOverride !== 0) {
      obj.enableIspPixelContentFlipOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableIspPixelContentFlipOverride,
      );
    }
    if (message.enableVehicleRtspKlvOverride !== 0) {
      obj.enableVehicleRtspKlvOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableVehicleRtspKlvOverride,
      );
    }
    if (message.gimbalInspectionModeOverride !== 0) {
      obj.gimbalInspectionModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.gimbalInspectionModeOverride,
      );
    }
    if (message.flashDriveRequiredForFlightOverride !== 0) {
      obj.flashDriveRequiredForFlightOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.flashDriveRequiredForFlightOverride,
      );
    }
    if (message.allowCommandedElandOverride !== 0) {
      obj.allowCommandedElandOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowCommandedElandOverride,
      );
    }
    if (message.allowInternalMissionActionsOverride !== 0) {
      obj.allowInternalMissionActionsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowInternalMissionActionsOverride,
      );
    }
    if (message.increasedMaxSpeedMapCaptureOverride !== 0) {
      obj.increasedMaxSpeedMapCaptureOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.increasedMaxSpeedMapCaptureOverride,
      );
    }
    if (message.allowMavlinkPerchOverride !== 0) {
      obj.allowMavlinkPerchOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.allowMavlinkPerchOverride);
    }
    if (message.forceRgbLedDisableOverride !== 0) {
      obj.forceRgbLedDisableOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.forceRgbLedDisableOverride,
      );
    }
    if (message.enableHighAltitudeVioOverride !== 0) {
      obj.enableHighAltitudeVioOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableHighAltitudeVioOverride,
      );
    }
    if (message.enableAttitudeRtxOverride !== 0) {
      obj.enableAttitudeRtxOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableAttitudeRtxOverride);
    }
    if (message.enableManualWffOverride !== 0) {
      obj.enableManualWffOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableManualWffOverride);
    }
    if (message.enableJpgHighCompress3dsOverride !== 0) {
      obj.enableJpgHighCompress3dsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableJpgHighCompress3dsOverride,
      );
    }
    if (message.enableGetMicrohardRfConfigOverride !== 0) {
      obj.enableGetMicrohardRfConfigOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableGetMicrohardRfConfigOverride,
      );
    }
    if (message.enableCommandedLostCommsOverride !== 0) {
      obj.enableCommandedLostCommsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableCommandedLostCommsOverride,
      );
    }
    if (message.enableGpsSpoofingDetectorOverride !== 0) {
      obj.enableGpsSpoofingDetectorOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableGpsSpoofingDetectorOverride,
      );
    }
    if (message.enableRscToggle3dsOverride !== 0) {
      obj.enableRscToggle3dsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableRscToggle3dsOverride,
      );
    }
    if (message.enableLowTakeoffOverride !== 0) {
      obj.enableLowTakeoffOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableLowTakeoffOverride);
    }
    if (message.enableRtkSupportOverride !== 0) {
      obj.enableRtkSupportOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableRtkSupportOverride);
    }
    if (message.enableFlightFileManifestTaskOverride !== 0) {
      obj.enableFlightFileManifestTaskOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableFlightFileManifestTaskOverride,
      );
    }
    if (message.isEuropeanUnionOverride !== 0) {
      obj.isEuropeanUnionOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.isEuropeanUnionOverride);
    }
    if (message.enablePerchOverride !== 0) {
      obj.enablePerchOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enablePerchOverride);
    }
    if (message.enableThirdPartyAttachmentPrototypingOverride !== 0) {
      obj.enableThirdPartyAttachmentPrototypingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableThirdPartyAttachmentPrototypingOverride,
      );
    }
    if (message.alwaysLogSubjectStreamForDockFlightsOverride !== 0) {
      obj.alwaysLogSubjectStreamForDockFlightsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.alwaysLogSubjectStreamForDockFlightsOverride,
      );
    }
    if (message.enableForceLowTakeoffToggleOverride !== 0) {
      obj.enableForceLowTakeoffToggleOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableForceLowTakeoffToggleOverride,
      );
    }
    if (message.enableInternalAttachmentPrototypingOverride !== 0) {
      obj.enableInternalAttachmentPrototypingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableInternalAttachmentPrototypingOverride,
      );
    }
    if (message.enableRtkNavigation !== 0) {
      obj.enableRtkNavigation = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableRtkNavigation);
    }
    if (message.enableMpegtsFileWritingOverride !== 0) {
      obj.enableMpegtsFileWritingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableMpegtsFileWritingOverride,
      );
    }
    if (message.mapCaptureTerrainFollowAlphaOverride !== 0) {
      obj.mapCaptureTerrainFollowAlphaOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.mapCaptureTerrainFollowAlphaOverride,
      );
    }
    if (message.enable20MpsMaxSpeedOverride !== 0) {
      obj.enable20MpsMaxSpeedOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enable20MpsMaxSpeedOverride,
      );
    }
    if (message.enableThirdPartyRadioAttachmentOverride !== 0) {
      obj.enableThirdPartyRadioAttachmentOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableThirdPartyRadioAttachmentOverride,
      );
    }
    if (message.enableSlowModeOverride !== 0) {
      obj.enableSlowModeOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableSlowModeOverride);
    }
    if (message.enableParachuteExpectedButMissingOverride !== 0) {
      obj.enableParachuteExpectedButMissingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableParachuteExpectedButMissingOverride,
      );
    }
    if (message.allowSubjectFollowOverride !== 0) {
      obj.allowSubjectFollowOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowSubjectFollowOverride,
      );
    }
    if (message.allowGpsFlightWithoutHandwaveOverride !== 0) {
      obj.allowGpsFlightWithoutHandwaveOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.allowGpsFlightWithoutHandwaveOverride,
      );
    }
    if (message.geofenceOffOverride !== 0) {
      obj.geofenceOffOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.geofenceOffOverride);
    }
    if (message.enableMissionsCloudToControllerOverride !== 0) {
      obj.enableMissionsCloudToControllerOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableMissionsCloudToControllerOverride,
      );
    }
    if (message.enableVisibleNightsenseStrobingOverride !== 0) {
      obj.enableVisibleNightsenseStrobingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableVisibleNightsenseStrobingOverride,
      );
    }
    if (message.enableIrNightsenseStrobingOverride !== 0) {
      obj.enableIrNightsenseStrobingOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableIrNightsenseStrobingOverride,
      );
    }
    if (message.enableEnhancedSlBitrateOverride !== 0) {
      obj.enableEnhancedSlBitrateOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableEnhancedSlBitrateOverride,
      );
    }
    if (message.enableThirdPartyAttachmentConfigsOverride !== 0) {
      obj.enableThirdPartyAttachmentConfigsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableThirdPartyAttachmentConfigsOverride,
      );
    }
    if (message.enable20MpsMaxSpeedWaypointsOnlyOverride !== 0) {
      obj.enable20MpsMaxSpeedWaypointsOnlyOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enable20MpsMaxSpeedWaypointsOnlyOverride,
      );
    }
    if (message.enableDcsModeSelectionOverride !== 0) {
      obj.enableDcsModeSelectionOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableDcsModeSelectionOverride,
      );
    }
    if (message.skipValidateCountrySettingsOverride !== 0) {
      obj.skipValidateCountrySettingsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.skipValidateCountrySettingsOverride,
      );
    }
    if (message.enableOnDemandMissionTierOverride !== 0) {
      obj.enableOnDemandMissionTierOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableOnDemandMissionTierOverride,
      );
    }
    if (message.enableIcingDetectionOverride !== 0) {
      obj.enableIcingDetectionOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableIcingDetectionOverride,
      );
    }
    if (message.enableFepPowerLimitNotificationOverride !== 0) {
      obj.enableFepPowerLimitNotificationOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableFepPowerLimitNotificationOverride,
      );
    }
    if (message.enableBatteryLightDisciplineOverride !== 0) {
      obj.enableBatteryLightDisciplineOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableBatteryLightDisciplineOverride,
      );
    }
    if (message.enableSubjectFollowWithoutOaOverride !== 0) {
      obj.enableSubjectFollowWithoutOaOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableSubjectFollowWithoutOaOverride,
      );
    }
    if (message.enableLsopOverride !== 0) {
      obj.enableLsopOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(message.enableLsopOverride);
    }
    if (message.enable30MpsMaxGroundSpeedOverride !== 0) {
      obj.enable30MpsMaxGroundSpeedOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enable30MpsMaxGroundSpeedOverride,
      );
    }
    if (message.enableMobileInspectionAssetsOverride !== 0) {
      obj.enableMobileInspectionAssetsOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableMobileInspectionAssetsOverride,
      );
    }
    if (message.enableGeoBasedDtedIngestOverride !== 0) {
      obj.enableGeoBasedDtedIngestOverride = vehicleSettingsOverrides_BooleanOverrideToJSON(
        message.enableGeoBasedDtedIngestOverride,
      );
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<VehicleSettingsOverrides>, I>>(base?: I): VehicleSettingsOverrides {
    return VehicleSettingsOverrides.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<VehicleSettingsOverrides>, I>>(object: I): VehicleSettingsOverrides {
    const message = createBaseVehicleSettingsOverrides();
    message.heightCeilingOverride = object.heightCeilingOverride ?? 0;
    message.allowedIlluminationModes = object.allowedIlluminationModes?.map((e) => e) || [];
    message.allowedObstacleMarginModes = object.allowedObstacleMarginModes?.map((e) => e) || [];
    message.returnWithVisionOnlyOverride = object.returnWithVisionOnlyOverride ?? 0;
    message.superzoomOverride = object.superzoomOverride ?? 0;
    message.digitalZoomOverride = object.digitalZoomOverride ?? 0;
    message.precisionFlightModeOverride = object.precisionFlightModeOverride ?? 0;
    message.skydiolinkAes256Override = object.skydiolinkAes256Override ?? 0;
    message.allowedNavigationModes = object.allowedNavigationModes?.map((e) => e) || [];
    message.ignoreLowBatteryBehaviorOverride = object.ignoreLowBatteryBehaviorOverride ?? 0;
    message.disableMediaEncryptionOverride = object.disableMediaEncryptionOverride ?? 0;
    message.disableMediaEncryptionOverrideV2 = object.disableMediaEncryptionOverrideV2 ?? 0;
    message.offlineOverride = object.offlineOverride ?? 0;
    message.rgbLedDisableOverride = object.rgbLedDisableOverride ?? 0;
    message.sdcardOverwriteOverride = object.sdcardOverwriteOverride ?? 0;
    message.flightHistoryOverride = object.flightHistoryOverride ?? 0;
    message.multiResPreviewEnableOverride = object.multiResPreviewEnableOverride ?? 0;
    message.centralizedFrbRateEnableOverride = object.centralizedFrbRateEnableOverride ?? 0;
    message.alignedTelemetryCsvOverride = object.alignedTelemetryCsvOverride ?? 0;
    message.displayHandWaveCalibrationOverride = object.displayHandWaveCalibrationOverride ?? 0;
    message.variableObstacleAvoidanceOverride = object.variableObstacleAvoidanceOverride ?? 0;
    message.dockJigglingEnabledOverride = object.dockJigglingEnabledOverride ?? 0;
    message.allowTakeoffFromMismatchedDockOverride = object.allowTakeoffFromMismatchedDockOverride ?? 0;
    message.allowOnVehiclePhotogrammetryOverride = object.allowOnVehiclePhotogrammetryOverride ?? 0;
    message.useBacktrackingRtxOverride = object.useBacktrackingRtxOverride ?? 0;
    message.ridFeatureEnabledOverride = object.ridFeatureEnabledOverride ?? 0;
    message.ridFeatureDisabledOverride = object.ridFeatureDisabledOverride ?? 0;
    message.allowSurfaceScanAlphaFeaturesOverride = object.allowSurfaceScanAlphaFeaturesOverride ?? 0;
    message.rebootMaxUptimeSecondsOverride = object.rebootMaxUptimeSecondsOverride ?? 0;
    message.defaultGeofence = (object.defaultGeofence !== undefined && object.defaultGeofence !== null)
      ? Geofence.fromPartial(object.defaultGeofence)
      : undefined;
    message.battImbalanceThresholdsOverride =
      (object.battImbalanceThresholdsOverride !== undefined && object.battImbalanceThresholdsOverride !== null)
        ? BatteryCellVoltageImbalanceThresholds.fromPartial(object.battImbalanceThresholdsOverride)
        : undefined;
    message.surfaceScanOverride = object.surfaceScanOverride ?? 0;
    message.disableObstacleAvoidanceDockLandingOverride = object.disableObstacleAvoidanceDockLandingOverride ?? 0;
    message.allowPoweredPayloadsOverride = object.allowPoweredPayloadsOverride ?? 0;
    message.disableCountryCodeScanOverride = object.disableCountryCodeScanOverride ?? 0;
    message.allow3dCaptureScanModeOverride = object.allow3dCaptureScanModeOverride ?? 0;
    message.allowSurfaceCaptureScanModeOverride = object.allowSurfaceCaptureScanModeOverride ?? 0;
    message.allowMapCaptureScanModeOverride = object.allowMapCaptureScanModeOverride ?? 0;
    message.allowTowerCaptureScanModeOverride = object.allowTowerCaptureScanModeOverride ?? 0;
    message.allowIndoorCaptureScanModeOverride = object.allowIndoorCaptureScanModeOverride ?? 0;
    message.allowIndoorCaptureScanModeV2Override = object.allowIndoorCaptureScanModeV2Override ?? 0;
    message.allowOrbitCaptureScanModeOverride = object.allowOrbitCaptureScanModeOverride ?? 0;
    message.allowCylinderCaptureScanModeOverride = object.allowCylinderCaptureScanModeOverride ?? 0;
    message.allowSemanticTransmissionTowerScanModeOverride = object.allowSemanticTransmissionTowerScanModeOverride ?? 0;
    message.fullSpinOnTakeoffOverride = object.fullSpinOnTakeoffOverride ?? 0;
    message.allowIrSubjectTrackingOverride = object.allowIrSubjectTrackingOverride ?? 0;
    message.enableIspPixelContentFlipOverride = object.enableIspPixelContentFlipOverride ?? 0;
    message.enableVehicleRtspKlvOverride = object.enableVehicleRtspKlvOverride ?? 0;
    message.gimbalInspectionModeOverride = object.gimbalInspectionModeOverride ?? 0;
    message.flashDriveRequiredForFlightOverride = object.flashDriveRequiredForFlightOverride ?? 0;
    message.allowCommandedElandOverride = object.allowCommandedElandOverride ?? 0;
    message.allowInternalMissionActionsOverride = object.allowInternalMissionActionsOverride ?? 0;
    message.increasedMaxSpeedMapCaptureOverride = object.increasedMaxSpeedMapCaptureOverride ?? 0;
    message.allowMavlinkPerchOverride = object.allowMavlinkPerchOverride ?? 0;
    message.forceRgbLedDisableOverride = object.forceRgbLedDisableOverride ?? 0;
    message.enableHighAltitudeVioOverride = object.enableHighAltitudeVioOverride ?? 0;
    message.enableAttitudeRtxOverride = object.enableAttitudeRtxOverride ?? 0;
    message.enableManualWffOverride = object.enableManualWffOverride ?? 0;
    message.enableJpgHighCompress3dsOverride = object.enableJpgHighCompress3dsOverride ?? 0;
    message.enableGetMicrohardRfConfigOverride = object.enableGetMicrohardRfConfigOverride ?? 0;
    message.enableCommandedLostCommsOverride = object.enableCommandedLostCommsOverride ?? 0;
    message.enableGpsSpoofingDetectorOverride = object.enableGpsSpoofingDetectorOverride ?? 0;
    message.enableRscToggle3dsOverride = object.enableRscToggle3dsOverride ?? 0;
    message.enableLowTakeoffOverride = object.enableLowTakeoffOverride ?? 0;
    message.enableRtkSupportOverride = object.enableRtkSupportOverride ?? 0;
    message.enableFlightFileManifestTaskOverride = object.enableFlightFileManifestTaskOverride ?? 0;
    message.isEuropeanUnionOverride = object.isEuropeanUnionOverride ?? 0;
    message.enablePerchOverride = object.enablePerchOverride ?? 0;
    message.enableThirdPartyAttachmentPrototypingOverride = object.enableThirdPartyAttachmentPrototypingOverride ?? 0;
    message.alwaysLogSubjectStreamForDockFlightsOverride = object.alwaysLogSubjectStreamForDockFlightsOverride ?? 0;
    message.enableForceLowTakeoffToggleOverride = object.enableForceLowTakeoffToggleOverride ?? 0;
    message.enableInternalAttachmentPrototypingOverride = object.enableInternalAttachmentPrototypingOverride ?? 0;
    message.enableRtkNavigation = object.enableRtkNavigation ?? 0;
    message.enableMpegtsFileWritingOverride = object.enableMpegtsFileWritingOverride ?? 0;
    message.mapCaptureTerrainFollowAlphaOverride = object.mapCaptureTerrainFollowAlphaOverride ?? 0;
    message.enable20MpsMaxSpeedOverride = object.enable20MpsMaxSpeedOverride ?? 0;
    message.enableThirdPartyRadioAttachmentOverride = object.enableThirdPartyRadioAttachmentOverride ?? 0;
    message.enableSlowModeOverride = object.enableSlowModeOverride ?? 0;
    message.enableParachuteExpectedButMissingOverride = object.enableParachuteExpectedButMissingOverride ?? 0;
    message.allowSubjectFollowOverride = object.allowSubjectFollowOverride ?? 0;
    message.allowGpsFlightWithoutHandwaveOverride = object.allowGpsFlightWithoutHandwaveOverride ?? 0;
    message.geofenceOffOverride = object.geofenceOffOverride ?? 0;
    message.enableMissionsCloudToControllerOverride = object.enableMissionsCloudToControllerOverride ?? 0;
    message.enableVisibleNightsenseStrobingOverride = object.enableVisibleNightsenseStrobingOverride ?? 0;
    message.enableIrNightsenseStrobingOverride = object.enableIrNightsenseStrobingOverride ?? 0;
    message.enableEnhancedSlBitrateOverride = object.enableEnhancedSlBitrateOverride ?? 0;
    message.enableThirdPartyAttachmentConfigsOverride = object.enableThirdPartyAttachmentConfigsOverride ?? 0;
    message.enable20MpsMaxSpeedWaypointsOnlyOverride = object.enable20MpsMaxSpeedWaypointsOnlyOverride ?? 0;
    message.enableDcsModeSelectionOverride = object.enableDcsModeSelectionOverride ?? 0;
    message.skipValidateCountrySettingsOverride = object.skipValidateCountrySettingsOverride ?? 0;
    message.enableOnDemandMissionTierOverride = object.enableOnDemandMissionTierOverride ?? 0;
    message.enableIcingDetectionOverride = object.enableIcingDetectionOverride ?? 0;
    message.enableFepPowerLimitNotificationOverride = object.enableFepPowerLimitNotificationOverride ?? 0;
    message.enableBatteryLightDisciplineOverride = object.enableBatteryLightDisciplineOverride ?? 0;
    message.enableSubjectFollowWithoutOaOverride = object.enableSubjectFollowWithoutOaOverride ?? 0;
    message.enableLsopOverride = object.enableLsopOverride ?? 0;
    message.enable30MpsMaxGroundSpeedOverride = object.enable30MpsMaxGroundSpeedOverride ?? 0;
    message.enableMobileInspectionAssetsOverride = object.enableMobileInspectionAssetsOverride ?? 0;
    message.enableGeoBasedDtedIngestOverride = object.enableGeoBasedDtedIngestOverride ?? 0;
    return message;
  },
};

function createBaseSettingsUpdateResponse(): SettingsUpdateResponse {
  return { success: false };
}

export const SettingsUpdateResponse = {
  encode(message: SettingsUpdateResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SettingsUpdateResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSettingsUpdateResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SettingsUpdateResponse {
    return { success: isSet(object.success) ? globalThis.Boolean(object.success) : false };
  },

  toJSON(message: SettingsUpdateResponse): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SettingsUpdateResponse>, I>>(base?: I): SettingsUpdateResponse {
    return SettingsUpdateResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SettingsUpdateResponse>, I>>(object: I): SettingsUpdateResponse {
    const message = createBaseSettingsUpdateResponse();
    message.success = object.success ?? false;
    return message;
  },
};

function createBaseSettingsPreviewSource(): SettingsPreviewSource {
  return { utime: 0, previewSource: false };
}

export const SettingsPreviewSource = {
  encode(message: SettingsPreviewSource, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.previewSource === true) {
      writer.uint32(16).bool(message.previewSource);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SettingsPreviewSource {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSettingsPreviewSource();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.previewSource = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SettingsPreviewSource {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      previewSource: isSet(object.previewSource) ? globalThis.Boolean(object.previewSource) : false,
    };
  },

  toJSON(message: SettingsPreviewSource): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.previewSource === true) {
      obj.previewSource = message.previewSource;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SettingsPreviewSource>, I>>(base?: I): SettingsPreviewSource {
    return SettingsPreviewSource.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SettingsPreviewSource>, I>>(object: I): SettingsPreviewSource {
    const message = createBaseSettingsPreviewSource();
    message.utime = object.utime ?? 0;
    message.previewSource = object.previewSource ?? false;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
